// src/prototype/data/execute/vendorEvaluationData.ts
// ============================================================
// VENDOR EVALUATIONS (Oct 21, 2026), produced by the Vendor Evaluation Agent method
// (spec: "DVA Vendor Evaluation Agent: Spec v0.1") against the reconciled landscape and the
// ACME demo client's context (Epic stack from D3, capacity from D1). Rubric vendor-eval-v1.
// Pre-generated for the three demo use cases and reviewed by hand. Every score carries a
// rationale and evidence; verify=true marks criteria scored 2 for lack of evidence.
// Evidence only: never changes a VRI score. Regenerate from the evaluation JSON; do not
// hand-edit scores without evidence.
// ============================================================
import type { VendorEvaluation } from './vendorEvaluation';

export const VENDOR_EVALUATIONS: Record<string, VendorEvaluation> = {
 "opp-1": {
  "useCaseId": "opp-1",
  "asOf": "2026-10-21",
  "rubricVersion": "vendor-eval-v1",
  "recommendation": {
   "deliveryPath": "combine",
   "summary": "Run Phases 1 and 2 on Epic modules ACME already licenses (readmission model, Healthy Planet, Compass Rose, Case Management) and build cohort baselines and outcome measurement in Caboodle, since Corewell Health shows that core is sufficient and no vendor can be contracted before the December first-unit launch. Bring CarePort, Aidin and Olio to demos for the Phase 3 post-acute referral and SNF partner lot, and Andor Health, CipherHealth and Artera for post-discharge outreach, because both fill gaps in network reach and staff capacity that ACME cannot build with 2.1 unallocated FTE. Hold the ADT notification lot (Bamboo Health, CarePort Interop, PointClickCare) until ACME confirms how much of its rural and SNF event gap the state HIE can close."
  },
  "epicBaseline": {
   "covers": [
    "Phase 1 risk stratification: Epic's cognitive computing 30-day readmission risk model and Healthy Planet risk stratification, outreach campaigns and contract performance views.",
    "Phase 2 transition workflow: Compass Rose program enrollment, care plans, milestones and task worklists for care managers, with Reporting Workbench for operational tracking.",
    "Proof that the native stack can carry the program: Corewell Health combined the Epic readmission model, Compass Rose and Reporting Workbench and reported its high-risk value-based cohort readmission rate falling from 23% to 7%.",
    "Healthy Planet ranked #2 in 2025 Best in KLAS Population Health Management (89.2).",
    "Discharge planning in Epic Case Management, which is the host workflow for third-party post-acute referral networks through the Community Resource Network Toolbox category.",
    "Outcome measurement and baselines can be built in Caboodle (quality 4/5) without a vendor analytics layer."
   ],
   "gaps": [
    "Post-acute referral network: electronic referral to non-Epic SNF, home health and community partners requires a third-party network in Epic's Community Resource Network Toolbox category, and ACME lacks partner-level readmission and length-of-stay data for Phase 3 (Lot A).",
    "Out-of-network event visibility: ACME's external ADT source is the state HIE, real time but thin in rural areas, so outside ED visits, admissions and SNF stays can be missed (Lot B).",
    "Outreach capacity: Epic provides worklists and campaigns, but 48-hour contact with symptom and medication screening for every targeted discharge exceeds ACME's 2.1 unallocated FTE (Lot C).",
    "Claims-based attribution: payer claims feeds are monthly with a 60-90 day lag and manual member matching (quality 2/5), which limits Healthy Planet contract views and readmission baselines; this is an internal data fix already in progress, not a purchase."
   ],
   "sources": [
    {
     "title": "Epic population health software",
     "url": "https://www.epic.com/software/population-health/",
     "retrieved": "2026-10-21"
    },
    {
     "title": "Corewell Health: Reducing Readmission Risk (HIMSS Davies case study)",
     "url": "https://arkansas.himss.org/sites/hde/files/media/file/2024/10/31/corewell-health-reducing-readmission-risk-_1.pdf",
     "retrieved": "2026-10-21"
    },
    {
     "title": "2025 Best in KLAS: Population Health Management",
     "url": "https://klasresearch.com/best-in-klas-ranking/population-health-management/2025/256",
     "retrieved": "2026-10-21"
    },
    {
     "title": "WellSky Joins Epic Toolbox ... Community Resource Network (Business Wire)",
     "url": "https://www.businesswire.com/news/home/20251210760242/en/WellSky-Joins-Epic-Toolbox-to-Improve-Patient-Care-Transitions-and-Coordination-Through-the-Community-Resource-Network",
     "retrieved": "2026-10-21"
    },
    {
     "title": "CarePort for Epic clients",
     "url": "https://careporthealth.com/epic-clients/",
     "retrieved": "2026-10-21"
    }
   ]
  },
  "lots": [
   {
    "id": "lot-a",
    "name": "Post-acute referral network and partner performance",
    "gap": "Epic Case Management hosts discharge planning, but electronic referral to non-Epic SNF, home health and community partners runs through a third-party network (Epic's Community Resource Network Toolbox category), and ACME has no partner-level readmission or length-of-stay view for Phase 3 SNF onboarding.",
    "why": "Phase 3 (Month 5-6) depends on onboarding SNF partners and steering volume to better-performing ones; a referral network with existing partner connections is hard to build in-house with 2.1 unallocated FTE.",
    "evaluated": [
     {
      "vendor": "WellSky CarePort",
      "status": "shortlist",
      "rank": 1,
      "weightedScore": 77.0,
      "scores": {
       "functional": {
        "score": 4,
        "rationale": "Covers electronic referral from Epic Case Management to SNF, home health, IRF, LTACH and community providers plus Guide quality measures for patient choice, but partner-level readmission reporting is not shown in sources.",
        "evidence": [
         "https://www.businesswire.com/news/home/20251210760242/en/WellSky-Joins-Epic-Toolbox-to-Improve-Patient-Care-Transitions-and-Coordination-Through-the-Community-Resource-Network",
         "https://careporthealth.com/epic-clients/",
         "https://www.businesswire.com/news/home/20250924208380/en/WellSky%C2%AE-Unveils-Comprehensive-Platform-to-Transform-Referral-Workflows-Analyze-Performance-and-Improve-Patient-Care"
        ],
        "verify": false
       },
       "integration": {
        "score": 5,
        "rationale": "Community Referral Network is in the Epic Toolbox Community Resource Network category and runs inside Epic Case Management, with Interop on FHIR and Guide in the Epic marketplace.",
        "evidence": [
         "https://www.businesswire.com/news/home/20251210760242/en/WellSky-Joins-Epic-Toolbox-to-Improve-Patient-Care-Transitions-and-Coordination-Through-the-Community-Resource-Network",
         "https://www.homecaremag.com/news/wellsky-integrates-careport-network-epic-toolbox",
         "https://careporthealth.com/epic-clients/"
        ],
        "verify": false
       },
       "proof": {
        "score": 3,
        "rationale": "Ochsner Health selected CarePort for 18 hospitals and the vendor cites 1,000 hospitals, but no named Epic IDN outcome or independent ranking was found.",
        "evidence": [
         "https://www.businesswire.com/news/home/20210316005187/en/Ochsner-Health-Selects-CarePort-to-Streamline-Patient-Care-Transitions",
         "https://careporthealth.com/products/interop/"
        ],
        "verify": true
       },
       "timeToValue": {
        "score": 4,
        "rationale": "An existing 130,000+ provider network embedded in Epic reduces partner onboarding and interface build, though no implementation timeline is published.",
        "evidence": [
         "https://www.businesswire.com/news/home/20251210760242/en/WellSky-Joins-Epic-Toolbox-to-Improve-Patient-Care-Transitions-and-Coordination-Through-the-Community-Resource-Network"
        ],
        "verify": true
       },
       "viability": {
        "score": 4,
        "rationale": "Owned by WellSky, which reports 20,000+ client sites, and the product line has been extended in 2025 with Toolbox entry, dashboards and Referral Intake.",
        "evidence": [
         "https://www.businesswire.com/news/home/20250402229201/en/WellSky%C2%AE-Introduces-New-Care-Transition-Dashboards-to-Transform-Case-Management-and-Drive-Smarter-Decision-Making",
         "https://www.businesswire.com/news/home/20250924208380/en/WellSky%C2%AE-Unveils-Comprehensive-Platform-to-Transform-Referral-Workflows-Analyze-Performance-and-Improve-Patient-Care"
        ],
        "verify": false
       },
       "measurement": {
        "score": 3,
        "rationale": "Care Transitions dashboards report referral response time and length of stay combining EHR and claims data, but readmission by partner and data export terms are not described.",
        "evidence": [
         "https://www.businesswire.com/news/home/20250402229201/en/WellSky%C2%AE-Introduces-New-Care-Transition-Dashboards-to-Transform-Case-Management-and-Drive-Smarter-Decision-Making"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No public pricing model or risk-sharing option was found in sources opened.",
        "evidence": [
         "https://www.businesswire.com/news/home/20251210760242/en/WellSky-Joins-Epic-Toolbox-to-Improve-Patient-Care-Transitions-and-Coordination-Through-the-Community-Resource-Network"
        ],
        "verify": true
       }
      },
      "whyShortlisted": "It is the only candidate in this lot with an Epic Toolbox listing for the Community Resource Network inside Epic Case Management, so referrals stay in the case manager's existing workflow. Its large existing receiving-provider network lowers Phase 3 SNF and home health onboarding effort for a team with 2.1 unallocated FTE.",
      "keyRisk": "Partner-level readmission and length-of-stay reporting needed for preferred-network decisions is not evidenced, so ACME may still need to build that view in Caboodle.",
      "verifyInDemo": [
       "Sending a multi-facility SNF referral from an Epic Case Management discharge plan through the Toolbox integration, with acceptance status returning to the Epic chart.",
       "Partner scorecards showing 30-day readmission rate, length of stay and acceptance time by SNF and home health agency, with the data source for each metric.",
       "Patient choice list with quality measures generated in Epic and documentation that meets CMS discharge planning choice rules.",
       "Bulk export of referral, acceptance and outcome data to an enterprise warehouse such as Caboodle."
      ],
      "rfpQuestions": [
       "Which Epic IDNs of 10+ hospitals are live on the Community Resource Network Toolbox integration today, and may we contact two of them?",
       "What share of SNFs and home health agencies in our primary and secondary service areas actively accept referrals through CarePort, measured over the last 90 days?",
       "Is partner readmission and length-of-stay reporting included, which data feeds it uses, and is there a separate fee for the Care Transitions dashboards?",
       "If we also license CarePort Interop for ADT notifications, what is the combined pricing basis and does it share one implementation?"
      ]
     },
     {
      "vendor": "Aidin",
      "status": "shortlist",
      "rank": 2,
      "weightedScore": 71.0,
      "scores": {
       "functional": {
        "score": 4,
        "rationale": "Covers post-acute referral broadcasting, payer prior authorization, patient-choice compliance and provider response and acceptance reporting used to manage partners.",
        "evidence": [
         "https://www.myaidin.com/",
         "https://www.healthcareitnews.com/news/case-and-referral-management-technology-gets-big-results-st-lukes"
        ],
        "verify": false
       },
       "integration": {
        "score": 4,
        "rationale": "Vendor documents bi-directional Epic integration with write-back of notes, AVS, media and flowsheets and cites an Epic Showroom listing, and St. Luke's runs it with Epic, but no Toolbox listing was found.",
        "evidence": [
         "https://www.myaidin.com/epic-referral-management-integration-epic-crn",
         "https://www.healthcareitnews.com/news/case-and-referral-management-technology-gets-big-results-st-lukes"
        ],
        "verify": false
       },
       "proof": {
        "score": 4,
        "rationale": "Named IDN references include Emory Healthcare (10 hospitals), AdventHealth and St. Luke's, where press coverage reported 0.3 fewer post-acute length-of-stay days and 10 to 14 beds per day freed.",
        "evidence": [
         "https://www.myaidin.com/news_press/emory-healthcare-care-management/",
         "https://www.healthcareitnews.com/news/case-and-referral-management-technology-gets-big-results-st-lukes",
         "https://www.myaidin.com/"
        ],
        "verify": false
       },
       "timeToValue": {
        "score": 3,
        "rationale": "Epic interface work is required on the client side and the receiving network (21,000+ providers) is smaller than the largest alternative; no go-live timeline is published.",
        "evidence": [
         "https://www.myaidin.com/",
         "https://www.myaidin.com/epic-referral-management-integration-epic-crn"
        ],
        "verify": true
       },
       "viability": {
        "score": 3,
        "rationale": "Acquired by LeanTaaS in September 2026 and being folded into iQueue for Inpatient Flow, which adds product-direction and integration risk alongside Bain Capital backing.",
        "evidence": [
         "https://hitconsultant.net/2026/09/01/leantaas-acquires-aidin/",
         "https://www.mobihealthnews.com/news/leantaas-acquires-aidin-ease-hospital-discharges"
        ],
        "verify": false
       },
       "measurement": {
        "score": 3,
        "rationale": "St. Luke's uses provider response time and acceptance rate reporting and shares provider-level data with partners, but readmission tracking and export terms are not described.",
        "evidence": [
         "https://www.healthcareitnews.com/news/case-and-referral-management-technology-gets-big-results-st-lukes"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No public pricing model or risk-sharing option was found in sources opened.",
        "evidence": [
         "https://www.myaidin.com/"
        ],
        "verify": true
       }
      },
      "whyShortlisted": "It has named Epic IDN references for this workflow, including Emory and St. Luke's, with documented bi-directional Epic write-back and reported post-acute length-of-stay reduction. It adds payer authorization and IMM/MOON form automation that support discharge timeliness.",
      "keyRisk": "The September 2026 LeanTaaS acquisition may shift roadmap and support toward the iQueue inpatient flow suite during ACME's Phase 3 window.",
      "verifyInDemo": [
       "Referral sent from Epic to multiple SNFs at once with acceptances and the selected facility written back to Epic notes and flowsheets.",
       "Payer prior authorization submission and status tracking for a Medicare Advantage patient within the referral.",
       "Provider scorecard showing response time, acceptance rate and post-discharge readmission by facility.",
       "Product roadmap for Aidin as a standalone product versus inside LeanTaaS iQueue, including contract terms if packaging changes."
      ],
      "rfpQuestions": [
       "Is Aidin listed in Epic Showroom today and under which category, and do you plan a Toolbox Community Resource Network listing?",
       "How many post-acute providers in our service areas are active on Aidin, and what is their median response time over the last 90 days?",
       "Following the LeanTaaS acquisition, will Aidin remain separately licensable, and what price protection do you offer for a multi-year term?",
       "What client IT effort (interface build, analyst hours) did Emory's 10-hospital rollout require and how long did it take?"
      ]
     },
     {
      "vendor": "Olio",
      "status": "shortlist",
      "rank": 3,
      "weightedScore": 60.0,
      "scores": {
       "functional": {
        "score": 4,
        "rationale": "Supports bi-directional status updates with post-acute partners, following patients to the next site and readmission and length-of-stay views by facility to steer volume, though it is not a referral placement tool.",
        "evidence": [
         "https://www.olio.health/",
         "https://www.olio.health/for-providers"
        ],
        "verify": false
       },
       "integration": {
        "score": 2,
        "rationale": "Integrates with PointClickCare Marketplace and TEFCA access via Kno2, but no Epic integration was described in sources opened.",
        "evidence": [
         "https://www.olio.health/",
         "https://www.olio.health/for-providers"
        ],
        "verify": true
       },
       "proof": {
        "score": 3,
        "rationale": "Deaconess Health System reports all 44 network SNFs covered and an 8.9-day length-of-stay reduction, and Banner Health reports 55 hours a week saved, both on vendor pages.",
        "evidence": [
         "https://www.olio.health/for-providers",
         "https://landing.olio.health/"
        ],
        "verify": false
       },
       "timeToValue": {
        "score": 3,
        "rationale": "Deaconess reported results in the first quarter, but SNF partner adoption is required and no implementation load is described.",
        "evidence": [
         "https://www.olio.health/for-providers"
        ],
        "verify": true
       },
       "viability": {
        "score": 2,
        "rationale": "Ownership, funding and customer count were not found in sources opened.",
        "evidence": [
         "https://www.olio.health/"
        ],
        "verify": true
       },
       "measurement": {
        "score": 4,
        "rationale": "Dashboards report discharges, readmission rate, length of stay and escalation response time by post-acute facility.",
        "evidence": [
         "https://www.olio.health/",
         "https://www.olio.health/for-providers"
        ],
        "verify": false
       },
       "commercial": {
        "score": 2,
        "rationale": "No public pricing model or risk-sharing option was found in sources opened.",
        "evidence": [
         "https://www.olio.health/"
        ],
        "verify": true
       }
      },
      "whyShortlisted": "It targets the Phase 3 need to manage SNF partner performance after placement, with readmission and length-of-stay reporting by facility. Deaconess and Banner Health provide named provider references for that workflow.",
      "keyRisk": "No Epic integration was found, so care managers may work in a separate login unless an Epic connection is confirmed.",
      "verifyInDemo": [
       "SNF partner dashboard showing 30-day readmission rate and length of stay by facility with the underlying data source and refresh frequency.",
       "How patient status updates from a SNF reach an ACME care manager, and whether anything appears in Epic.",
       "Onboarding steps for a SNF that does not use PointClickCare.",
       "Escalation workflow when a SNF patient shows deterioration risk."
      ],
      "rfpQuestions": [
       "Do you have any production Epic integration (FHIR, HL7, In Basket) today, and which Epic health systems use it?",
       "What did Deaconess and Banner Health each need to supply in staff time to onboard their SNF networks?",
       "Who owns and funds Olio today, and how many health system customers are live?",
       "Can all partner performance data be exported to our warehouse without additional fees?"
      ]
     },
     {
      "vendor": "Real Time Medical Systems",
      "status": "nearMiss",
      "rank": 4,
      "weightedScore": 52.0,
      "scores": {
       "functional": {
        "score": 3,
        "rationale": "Provides SNF interventional analytics on readmissions and length of stay with a LACE-based risk score, but not referral placement.",
        "evidence": [
         "https://realtimemed.com/"
        ],
        "verify": false
       },
       "integration": {
        "score": 2,
        "rationale": "Integrates with post-acute EHRs such as MatrixCare; no Epic integration evidence was found.",
        "evidence": [
         "https://realtimemed.com/"
        ],
        "verify": true
       },
       "proof": {
        "score": 2,
        "rationale": "Cites 2,300+ facilities, a Maryland Total Cost of Care initiative and a 52% readmission reduction that is vendor-reported, with no named health system reference.",
        "evidence": [
         "https://realtimemed.com/"
        ],
        "verify": true
       },
       "timeToValue": {
        "score": 3,
        "rationale": "Extracts data from SNF EHRs, which limits hospital IT effort, but depends on SNF participation and has no published timeline.",
        "evidence": [
         "https://realtimemed.com/"
        ],
        "verify": true
       },
       "viability": {
        "score": 2,
        "rationale": "Ownership and funding were not found in sources opened.",
        "evidence": [
         "https://realtimemed.com/"
        ],
        "verify": true
       },
       "measurement": {
        "score": 4,
        "rationale": "Reports rehospitalization and length-of-stay metrics by SNF from post-acute EHR data.",
        "evidence": [
         "https://realtimemed.com/"
        ],
        "verify": false
       },
       "commercial": {
        "score": 2,
        "rationale": "No public pricing model or risk-sharing option was found in sources opened.",
        "evidence": [
         "https://realtimemed.com/"
        ],
        "verify": true
       }
      },
      "whyNot": "Strong SNF-side measurement but no Epic integration evidence and no named health system reference; consider as a data add-on if the shortlisted vendor cannot report partner readmissions."
     },
     {
      "vendor": "Dina",
      "status": "nearMiss",
      "rank": 5,
      "weightedScore": 50.0,
      "scores": {
       "functional": {
        "score": 3,
        "rationale": "Builds a preferred post-acute and in-home provider network with quality data, responsiveness dashboards, bed availability and patient choice lists.",
        "evidence": [
         "https://www.dinacare.com/providers"
        ],
        "verify": false
       },
       "integration": {
        "score": 2,
        "rationale": "Vendor states HL7 v2, FHIR and API connectivity to EHRs, but Epic is not named in sources opened.",
        "evidence": [
         "https://www.dinacare.com/providers"
        ],
        "verify": true
       },
       "proof": {
        "score": 3,
        "rationale": "Jefferson Health case study (three hospitals, $570,000 savings) and Redeemer Health testimonial on the vendor site.",
        "evidence": [
         "https://www.dinacare.com/providers"
        ],
        "verify": false
       },
       "timeToValue": {
        "score": 2,
        "rationale": "No implementation timeline or client effort described.",
        "evidence": [
         "https://www.dinacare.com/providers"
        ],
        "verify": true
       },
       "viability": {
        "score": 2,
        "rationale": "Acquired in May 2026 by Integrated Home Care Services, whose combined offer targets plan sponsors, which raises provider-direction risk.",
        "evidence": [
         "https://www.dinacare.com/integrated-home-care-services-acquires-dina-creating-the-category-defining-platform-for-in-home-benefit-management"
        ],
        "verify": false
       },
       "measurement": {
        "score": 3,
        "rationale": "Partner responsiveness dashboards and quality data are described, but readmission tracking and export are not.",
        "evidence": [
         "https://www.dinacare.com/providers"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No public pricing model or risk-sharing option was found in sources opened.",
        "evidence": [
         "https://www.dinacare.com/providers"
        ],
        "verify": true
       }
      },
      "whyNot": "Relevant preferred-network features, but the May 2026 acquisition points the product toward plan sponsors and no Epic integration was confirmed."
     },
     {
      "vendor": "ABOUT Healthcare (incl. Ensocare)",
      "status": "evaluated",
      "rank": 6,
      "weightedScore": 45.0,
      "scores": {
       "functional": {
        "score": 3,
        "rationale": "Covers discharge to post-acute care with a Respond portal for receiving providers and a Choice tool for patients, but partner performance analytics are not described.",
        "evidence": [
         "https://abouthealthcare.com/ensocare/",
         "https://abouthealthcare.com/technology/transition-for-post-acute-care-ensocare/"
        ],
        "verify": false
       },
       "integration": {
        "score": 2,
        "rationale": "No EHR or Epic integration described on pages opened.",
        "evidence": [
         "https://abouthealthcare.com/ensocare/",
         "https://abouthealthcare.com/technology/transition-for-post-acute-care-ensocare/"
        ],
        "verify": true
       },
       "proof": {
        "score": 2,
        "rationale": "Claims hundreds of health system users without naming any.",
        "evidence": [
         "https://abouthealthcare.com/ensocare/"
        ],
        "verify": true
       },
       "timeToValue": {
        "score": 2,
        "rationale": "No implementation timeline or client effort described.",
        "evidence": [
         "https://abouthealthcare.com/technology/transition-for-post-acute-care-ensocare/"
        ],
        "verify": true
       },
       "viability": {
        "score": 2,
        "rationale": "Operating as ABOUT since the 2021 Central Logic rebrand, but ownership is not stated in sources opened.",
        "evidence": [
         "https://business.dptribune.com/dptribune/news/read/41986644/central_logic_integrates_services_in_rebrand_to_about_healthcare"
        ],
        "verify": true
       },
       "measurement": {
        "score": 2,
        "rationale": "No outcome reporting or data export terms described.",
        "evidence": [
         "https://abouthealthcare.com/technology/transition-for-post-acute-care-ensocare/"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No public pricing model or risk-sharing option was found in sources opened.",
        "evidence": [
         "https://abouthealthcare.com/technology/transition-for-post-acute-care-ensocare/"
        ],
        "verify": true
       }
      }
     }
    ]
   },
   {
    "id": "lot-b",
    "name": "Out-of-network ADT event notifications",
    "gap": "ACME's only external ADT source is the state HIE, which is real time but thin in rural areas, so admissions, ED visits and SNF stays outside ACME can be missed for attributed patients.",
    "why": "Closed-loop transitions and ED revisit reduction require knowing when attributed patients present elsewhere; this is network coverage ACME cannot build, and it should be weighed against expanding the state HIE feed first.",
    "evaluated": [
     {
      "vendor": "Bamboo Health (Pings, formerly PatientPing)",
      "status": "shortlist",
      "rank": 1,
      "weightedScore": 63.0,
      "scores": {
       "functional": {
        "score": 4,
        "rationale": "Pings delivers real-time ADT notifications from in- and out-of-network encounters plus rising-risk flags, automated discharge summaries and transition tracking.",
        "evidence": [
         "https://bamboohealth.com/",
         "https://hitconsultant.net/2024/06/19/wakemed-deploys-real-time-care-intelligence-to-enhance-care-coordination/"
        ],
        "verify": false
       },
       "integration": {
        "score": 2,
        "rationale": "Described as EHR-agnostic with workflow integration for EHRs and HIEs, but no Epic-specific integration was found in sources opened.",
        "evidence": [
         "https://bamboohealth.com/",
         "https://hitconsultant.net/2024/06/19/wakemed-deploys-real-time-care-intelligence-to-enhance-care-coordination/"
        ],
        "verify": true
       },
       "proof": {
        "score": 4,
        "rationale": "Named IDN deployments include WakeMed (3 hospitals, 8 EDs) and Prisma Health with its clinically integrated network, though no outcome results were published.",
        "evidence": [
         "https://www.silicon.co.uk/press-release/wakemed-selects-bamboo-health-to-drive-improved-care-coordination-for-patients",
         "https://hitconsultant.net/2024/06/19/wakemed-deploys-real-time-care-intelligence-to-enhance-care-coordination/",
         "https://www.silicon.co.uk/press-release/bamboo-health-and-prisma-health-collaborate-for-real-time-intelligence-to-enhance-care-and-drive-value"
        ],
        "verify": false
       },
       "timeToValue": {
        "score": 3,
        "rationale": "WakeMed built on an existing statewide hospital association ADT program, suggesting reuse of existing feeds, but client effort and timeline are not documented.",
        "evidence": [
         "https://hitconsultant.net/2024/06/19/wakemed-deploys-real-time-care-intelligence-to-enhance-care-coordination/"
        ],
        "verify": true
       },
       "viability": {
        "score": 3,
        "rationale": "Active customer base and stated coverage of 52 states and territories, but ownership and financial backing were not confirmed in sources opened.",
        "evidence": [
         "https://bamboohealth.com/"
        ],
        "verify": true
       },
       "measurement": {
        "score": 3,
        "rationale": "Spotlights provides KPI dashboards and transition tracking, but readmission baselining and export terms are not described.",
        "evidence": [
         "https://www.silicon.co.uk/press-release/bamboo-health-and-prisma-health-collaborate-for-real-time-intelligence-to-enhance-care-and-drive-value"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No public pricing model or risk-sharing option was found in sources opened.",
        "evidence": [
         "https://bamboohealth.com/"
        ],
        "verify": true
       }
      },
      "whyShortlisted": "It has two named multi-hospital IDN references (WakeMed and Prisma Health) using Pings for transitions, including a clinically integrated network. It adds out-of-network and post-acute events that ACME's state HIE feed covers thinly in rural areas.",
      "keyRisk": "No documented Epic integration means alerts may land in a separate portal rather than Epic worklists.",
      "verifyInDemo": [
       "An out-of-network ED visit and SNF admission for an ACME attributed patient arriving in an Epic worklist or In Basket, not a separate portal.",
       "Coverage report for ACME's rural service areas showing which hospitals and SNFs send ADTs and how that differs from the state HIE feed.",
       "Spotlights reporting of 7-day follow-up after discharge and 30-day readmission by contract cohort.",
       "Configuration of roster-based routing by attributed population and care team."
      ],
      "rfpQuestions": [
       "Which Epic customers receive Pings inside Epic today, and through which mechanism (HL7 inbound, In Basket, FHIR)?",
       "What percentage of hospitals, EDs and SNFs in our rural counties send ADTs to your network today, and how much of that is already available through our state HIE?",
       "Who owns Bamboo Health today, and what is the pricing basis (per attributed life, per facility, per event)?",
       "Can raw notification data be exported to Caboodle on a daily schedule without additional fees?"
      ]
     },
     {
      "vendor": "WellSky CarePort",
      "status": "shortlist",
      "rank": 2,
      "weightedScore": 63.0,
      "scores": {
       "functional": {
        "score": 3,
        "rationale": "CarePort Interop sends ADT-based event notifications to PCPs and post-acute providers to meet CMS Conditions of Participation, which is more outbound than an inbound out-of-network event feed.",
        "evidence": [
         "https://careporthealth.com/products/interop/"
        ],
        "verify": false
       },
       "integration": {
        "score": 4,
        "rationale": "Interop is accessible from the Epic patient record using FHIR APIs and is stated as available in the Epic marketplace.",
        "evidence": [
         "https://careporthealth.com/epic-clients/",
         "https://careporthealth.com/products/interop/"
        ],
        "verify": false
       },
       "proof": {
        "score": 3,
        "rationale": "Vendor cites 1,000 hospitals and 25M annual referrals, but no named IDN reference for Interop notifications was found.",
        "evidence": [
         "https://careporthealth.com/products/interop/"
        ],
        "verify": true
       },
       "timeToValue": {
        "score": 3,
        "rationale": "Could share an implementation with a Lot A CarePort purchase, but no timeline or client effort is documented.",
        "evidence": [
         "https://careporthealth.com/products/interop/"
        ],
        "verify": true
       },
       "viability": {
        "score": 4,
        "rationale": "Owned by WellSky, which reports 20,000+ client sites and continues to extend the CarePort line.",
        "evidence": [
         "https://www.businesswire.com/news/home/20250402229201/en/WellSky%C2%AE-Introduces-New-Care-Transition-Dashboards-to-Transform-Case-Management-and-Drive-Smarter-Decision-Making",
         "https://www.businesswire.com/news/home/20251210760242/en/WellSky-Joins-Epic-Toolbox-to-Improve-Patient-Care-Transitions-and-Coordination-Through-the-Community-Resource-Network"
        ],
        "verify": false
       },
       "measurement": {
        "score": 2,
        "rationale": "No notification outcome reporting or export terms found for Interop.",
        "evidence": [
         "https://careporthealth.com/products/interop/"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No public pricing model or risk-sharing option was found in sources opened.",
        "evidence": [
         "https://careporthealth.com/products/interop/"
        ],
        "verify": true
       }
      },
      "whyShortlisted": "It has the strongest documented Epic integration in this lot and could consolidate notifications with a Lot A referral network purchase. It addresses CMS patient event notification requirements to downstream providers.",
      "keyRisk": "It may not supply the inbound out-of-network and rural events that are ACME's actual gap, since sources describe outbound notifications.",
      "verifyInDemo": [
       "Inbound notification of an ACME patient's admission to a non-ACME hospital or SNF appearing in Epic.",
       "Outbound discharge notification to a PCP and SNF triggered from an Epic ADT, with delivery confirmation.",
       "Coverage view of hospitals and SNFs sending events in ACME's service areas.",
       "Shared configuration and single sign-on with the Community Referral Network inside Epic."
      ],
      "rfpQuestions": [
       "Does Interop deliver inbound events from non-ACME hospitals and SNFs, and from which sources (direct feeds, HIEs, CarePort referral activity)?",
       "Which Epic health systems use Interop in production and in which Epic workflow do events appear?",
       "What is the incremental price of Interop when bundled with the Community Referral Network?",
       "What notification volume, delivery and follow-up reporting is available and exportable?"
      ]
     },
     {
      "vendor": "PointClickCare (incl. Collective Medical and Audacious Inquiry)",
      "status": "shortlist",
      "rank": 3,
      "weightedScore": 60.0,
      "scores": {
       "functional": {
        "score": 4,
        "rationale": "Provides real-time event notifications across hospitals, SNFs, health plans and HIEs, with visibility into post-acute EHR data from its senior care customer base.",
        "evidence": [
         "https://pointclickcare.com/collective-medical/"
        ],
        "verify": false
       },
       "integration": {
        "score": 2,
        "rationale": "Vendor states hospital connectivity works regardless of EHR; no Epic-specific integration was found in sources opened.",
        "evidence": [
         "https://pointclickcare.com/collective-medical/",
         "https://pointclickcare.com/?p=63495"
        ],
        "verify": true
       },
       "proof": {
        "score": 3,
        "rationale": "Vendor cites 2,700+ hospitals and 27,000 senior care facilities, but no named health system reference for notifications was found.",
        "evidence": [
         "https://pointclickcare.com/collective-medical/"
        ],
        "verify": true
       },
       "timeToValue": {
        "score": 3,
        "rationale": "Existing hospital and SNF connections may reduce new interface work, but client effort and timeline are not documented.",
        "evidence": [
         "https://pointclickcare.com/collective-medical/"
        ],
        "verify": true
       },
       "viability": {
        "score": 4,
        "rationale": "Large company serving 30,000+ provider organizations with Best in KLAS recognition in skilled nursing for a seventh year.",
        "evidence": [
         "https://pointclickcare.com/?p=63495",
         "https://pointclickcare.com/collective-medical/"
        ],
        "verify": false
       },
       "measurement": {
        "score": 2,
        "rationale": "No notification outcome reporting or export terms described in sources opened.",
        "evidence": [
         "https://pointclickcare.com/collective-medical/",
         "https://pointclickcare.com/?p=63495"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No public pricing model or risk-sharing option was found in sources opened.",
        "evidence": [
         "https://pointclickcare.com/collective-medical/"
        ],
        "verify": true
       }
      },
      "whyShortlisted": "Its SNF EHR footprint is the most direct route to SNF admission and discharge events, which ACME needs for Phase 3 partner coordination. It operates a hospital and ED network alongside that post-acute data.",
      "keyRisk": "No named health system reference or Epic workflow integration was found, so fit with Epic worklists is unproven.",
      "verifyInDemo": [
       "Event for an ACME patient admitted to a PointClickCare SNF reaching an ACME care manager inside Epic.",
       "Share of SNFs in ACME's service area on PointClickCare and connected to the notification network.",
       "Hospital and ED event coverage in rural counties compared with the state HIE.",
       "Reporting on notification volume and follow-up actions by cohort."
      ],
      "rfpQuestions": [
       "Which Epic IDNs receive your notifications in Epic today, and through which integration method?",
       "How many hospitals and SNFs in our primary and secondary service areas send data to your network?",
       "Which current product (ADT Aware, Network Notify, Transitions IQ, or other) fits a health system receiving events, and how is each priced?",
       "Can event-level data be exported to our warehouse without additional fees?"
      ]
     },
     {
      "vendor": "Secure Exchange Solutions (SES Notify)",
      "status": "nearMiss",
      "rank": 4,
      "weightedScore": 56.0,
      "scores": {
       "functional": {
        "score": 3,
        "rationale": "Sends admit, discharge and critical-lab alerts to care managers, PCPs and post-acute facilities with configurable triggers, without described transition tracking.",
        "evidence": [
         "https://www.secureexsolutions.com/products/ses-notify/"
        ],
        "verify": false
       },
       "integration": {
        "score": 2,
        "rationale": "Works with any EHR through standard HL7 ADT and needs no on-premise install, but no Epic-specific integration was confirmed.",
        "evidence": [
         "https://www.secureexsolutions.com/products/ses-notify/"
        ],
        "verify": true
       },
       "proof": {
        "score": 3,
        "rationale": "Named 2026 Best in KLAS for ADT Notifications, but no health system customers are named.",
        "evidence": [
         "https://klasresearch.com/report/2026-best-in-klas-awards-software-and-services/3906",
         "https://hitconsultant.net/2026/02/04/2026-best-in-klas-winners-full-list-software-services/",
         "https://www.secureexsolutions.com/products/ses-notify/"
        ],
        "verify": true
       },
       "timeToValue": {
        "score": 4,
        "rationale": "Standard HL7 ADT input with no on-premise install implies a low client build load.",
        "evidence": [
         "https://www.secureexsolutions.com/products/ses-notify/"
        ],
        "verify": false
       },
       "viability": {
        "score": 3,
        "rationale": "Owned by Centauri Health Solutions since 2021 and connects to about 90,000 organizations and 50+ HIEs per the acquisition release.",
        "evidence": [
         "https://www.businesswire.com/news/home/20211208005227/en/Centauri-Health-Solutions-Adds-Clinical-Data-Exchange-Capabilities-with-Acquisition-of-Secure-Exchange-Solutions"
        ],
        "verify": false
       },
       "measurement": {
        "score": 2,
        "rationale": "No outcome reporting or export terms described.",
        "evidence": [
         "https://www.secureexsolutions.com/products/ses-notify/"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No public pricing model or risk-sharing option was found in sources opened.",
        "evidence": [
         "https://www.secureexsolutions.com/products/ses-notify/"
        ],
        "verify": true
       }
      },
      "whyNot": "Independent KLAS recognition and light implementation, but no named health system reference and no transition tracking; its reliance on HIE connections may not close ACME's rural coverage gap."
     }
    ]
   },
   {
    "id": "lot-c",
    "name": "Post-discharge outreach and follow-up automation",
    "gap": "Healthy Planet and Compass Rose provide worklists and campaigns, but contacting every targeted discharge within 48 hours with symptom and medication screening and escalation requires staff ACME does not have (2.1 FTE unallocated, 3 open requisitions).",
    "why": "Timely follow-up and medication reconciliation drive the 8-12% readmission target and the >85% transition completion score; automated or vendor-staffed outreach with Epic write-back is the fastest way to add that capacity.",
    "evaluated": [
     {
      "vendor": "Andor Health (ThinkAndor)",
      "status": "shortlist",
      "rank": 1,
      "weightedScore": 77.0,
      "scores": {
       "functional": {
        "score": 5,
        "rationale": "At UPMC, AI agents contact all enrolled discharged patients within 48 hours, run symptom screens, route higher-risk patients to coordinators and check in again on day 15, with a staffed clinical partner (Psynergy Health).",
        "evidence": [
         "https://hitconsultant.net/?p=97667",
         "https://andorhealth.com/"
        ],
        "verify": false
       },
       "integration": {
        "score": 4,
        "rationale": "ThinkAndor Remote Patient Monitoring holds an Epic Toolbox designation with data flowing to the Epic record, but the Epic integration of the transitional care agents was not described.",
        "evidence": [
         "https://medtechspectrum.com/news/15/25919/andor-health-integrates-ai-native-remote-patient-monitoring-with-epic-.html",
         "https://hitconsultant.net/?p=97667"
        ],
        "verify": true
       },
       "proof": {
        "score": 4,
        "rationale": "UPMC expanded the program to 18 hospitals and Tampa General, Ohio State, Ballad Health, MUSC and Sentara are named customers, but reported outcomes are not independently verified.",
        "evidence": [
         "https://hitconsultant.net/?p=97667",
         "https://andorhealth.com/"
        ],
        "verify": false
       },
       "timeToValue": {
        "score": 4,
        "rationale": "The operating-partner model supplies clinical staff and UPMC moved from one hospital to 10 live with 8 more in two waves, which fits ACME's thin capacity.",
        "evidence": [
         "https://hitconsultant.net/?p=97667"
        ],
        "verify": false
       },
       "viability": {
        "score": 2,
        "rationale": "Ownership, funding and financial position were not found in sources opened.",
        "evidence": [
         "https://andorhealth.com/"
        ],
        "verify": true
       },
       "measurement": {
        "score": 3,
        "rationale": "Reports follow-up completion and total cost of care changes, but methodology and baselines were not provided.",
        "evidence": [
         "https://hitconsultant.net/?p=97667"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No public pricing model or risk-sharing option was found in sources opened.",
        "evidence": [
         "https://andorhealth.com/"
        ],
        "verify": true
       }
      },
      "whyShortlisted": "It is the closest functional match to ACME's need for 48-hour post-discharge contact with escalation, and it comes with clinical staffing that offsets ACME's 2.1 unallocated FTE. UPMC's 18-hospital expansion is a named large-IDN reference for this exact workflow.",
      "keyRisk": "The UPMC outcome figures (for example a 45% total cost of care reduction) are self-reported without methodology, and the transitional care agents' Epic write-back is unconfirmed.",
      "verifyInDemo": [
       "End-to-end flow from an Epic discharge event to AI agent outreach, symptom screen, escalation to a coordinator and documentation back in Epic.",
       "Medication questions and reconciliation prompts during the outreach and how discrepancies are routed to a pharmacist or nurse.",
       "Coordinator worklist with filtered alerts and the rule logic for what counts as non-actionable.",
       "Reporting of 7-day follow-up completion and 30-day readmission against a matched comparison group."
      ],
      "rfpQuestions": [
       "Which Epic integration points do the transitional care agents use at UPMC and other Epic sites (ADT trigger, flowsheet, In Basket, notes)?",
       "What methodology produced the UPMC results on follow-up completion and total cost of care, and can UPMC speak to us directly?",
       "How is Psynergy Health staffing priced (per enrolled patient, per discharge, per FTE), and is outcome-based pricing available?",
       "Who owns and funds Andor Health, and what is your customer count for transitional care specifically?"
      ]
     },
     {
      "vendor": "CipherHealth",
      "status": "shortlist",
      "rank": 2,
      "weightedScore": 74.0,
      "scores": {
       "functional": {
        "score": 5,
        "rationale": "Automated voice and text check-ins within hours of discharge score responses, check prescription access and medication questions, route concerns to a nurse worklist and allow live transfer, with nurse-staffed programs also offered.",
        "evidence": [
         "https://www.cipherhealth.com/products/outreach/post-discharge-follow-up",
         "https://cipherhealth.com/resource/case-study/achieving-15m-in-savings-by-reducing-readmissions-with-post-discharge-follow-up/"
        ],
        "verify": false
       },
       "integration": {
        "score": 4,
        "rationale": "Bi-directional Epic integration via HL7 and FHIR with flowsheet and Issue Panel write-back of outreach results, though no Showroom or Toolbox listing was confirmed.",
        "evidence": [
         "https://cipherhealth.com/epic-integration/",
         "https://www.cipherhealth.com/products/outreach/post-discharge-follow-up"
        ],
        "verify": false
       },
       "proof": {
        "score": 3,
        "rationale": "Intermountain Healthcare reports about $15M savings from 6.4K patients called and Community Health Network is a named Epic integration customer, both in vendor materials.",
        "evidence": [
         "https://cipherhealth.com/resource/case-study/achieving-15m-in-savings-by-reducing-readmissions-with-post-discharge-follow-up/",
         "https://cipherhealth.com/epic-integration/"
        ],
        "verify": false
       },
       "timeToValue": {
        "score": 4,
        "rationale": "Automation sends only flagged patients to nurses, which limits added staff load, though no go-live timeline is published.",
        "evidence": [
         "https://www.cipherhealth.com/products/outreach/post-discharge-follow-up"
        ],
        "verify": true
       },
       "viability": {
        "score": 2,
        "rationale": "Ownership, funding and financial position were not found in sources opened.",
        "evidence": [
         "https://www.cipherhealth.com/products/outreach/post-discharge-follow-up"
        ],
        "verify": true
       },
       "measurement": {
        "score": 3,
        "rationale": "Outreach results write back to Epic flowsheets for measurement in ACME's own reporting, but vendor readmission claims (25% and 41%) conflict and lack methodology.",
        "evidence": [
         "https://www.cipherhealth.com/products/outreach/post-discharge-follow-up",
         "https://cipherhealth.com/epic-integration/"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No public pricing model or risk-sharing option was found in sources opened.",
        "evidence": [
         "https://www.cipherhealth.com/products/outreach/post-discharge-follow-up"
        ],
        "verify": true
       }
      },
      "whyShortlisted": "It covers the required post-discharge contact, medication questions and escalation with documented Epic flowsheet write-back, so results can be measured in Caboodle. Intermountain is a named large-IDN reference for the follow-up program.",
      "keyRisk": "Readmission outcome claims are inconsistent across its own materials, so ACME must validate impact with its own comparison design.",
      "verifyInDemo": [
       "Epic discharge triggering a call or text within hours, with answers written to an Epic flowsheet and a task created in Epic.",
       "Medication access and understanding questions, scoring thresholds and routing to a nurse worklist.",
       "Live transfer from automated call to an ACME nurse and how that contact is documented.",
       "Dashboard of reach rate, escalation rate and 30-day readmissions by unit and payer contract."
      ],
      "rfpQuestions": [
       "Your materials cite both 25% and 41% readmission reductions; which customers and comparison methods produced each figure?",
       "Is CipherHealth listed in Epic Showroom, and which Epic IDNs use flowsheet and Issue Panel write-back today?",
       "What is the price difference between automated-only and nurse-staffed follow-up, and on what unit (per discharge, per call)?",
       "Who owns CipherHealth, and what is your current health system customer count?"
      ]
     },
     {
      "vendor": "Artera (formerly WELL Health)",
      "status": "shortlist",
      "rank": 3,
      "weightedScore": 70.0,
      "scores": {
       "functional": {
        "score": 4,
        "rationale": "Automated post-discharge text and call sequences with a collaborative care team inbox and Care Navigator for clinical follow-up, without described symptom scoring or medication workflows.",
        "evidence": [
         "https://artera.io/care-navigator/",
         "https://artera.io/blog/vanderbilt-study-post-discharge-communication/"
        ],
        "verify": false
       },
       "integration": {
        "score": 3,
        "rationale": "Vendor describes an Epic integration for automated and manual messaging, but write-back, In Basket and Showroom details were not stated.",
        "evidence": [
         "https://artera.io/resource/epic-integrates-with-artera",
         "https://artera.io/care-navigator/"
        ],
        "verify": true
       },
       "proof": {
        "score": 5,
        "rationale": "Best in KLAS 2026 for Patient Communications, and Vanderbilt's Discharge Care Center (80,000+ discharges) reported 30-day readmissions falling from 10.6% to 9.9% in a NEJM Catalyst study.",
        "evidence": [
         "https://artera.io/blog/best-in-klas/",
         "https://hitconsultant.net/2026/02/04/2026-best-in-klas-winners-full-list-software-services/",
         "https://artera.io/blog/vanderbilt-study-post-discharge-communication/"
        ],
        "verify": false
       },
       "timeToValue": {
        "score": 3,
        "rationale": "Messaging can be automated, but replies require care team staffing in the shared inbox and no timeline is published.",
        "evidence": [
         "https://artera.io/care-navigator/"
        ],
        "verify": true
       },
       "viability": {
        "score": 3,
        "rationale": "Serves 1,000+ healthcare organizations per vendor, but ownership and funding were not confirmed.",
        "evidence": [
         "https://artera.io/blog/vanderbilt-study-post-discharge-communication/"
        ],
        "verify": true
       },
       "measurement": {
        "score": 3,
        "rationale": "Workflow analytics are provided and Vanderbilt measured readmissions over a large cohort, but data export terms are not described.",
        "evidence": [
         "https://artera.io/blog/vanderbilt-study-post-discharge-communication/",
         "https://artera.io/care-navigator/"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No public pricing model or risk-sharing option was found in sources opened.",
        "evidence": [
         "https://artera.io/care-navigator/"
        ],
        "verify": true
       }
      },
      "whyShortlisted": "It has the strongest independent proof in this lot, with Best in KLAS 2026 and a published Vanderbilt study across 80,000+ discharges. It may also be a lower-cost option if ACME already uses Artera for patient communications.",
      "keyRisk": "The Vanderbilt effect size is modest (10.6% to 9.9%) and responses depend on ACME staff working the inbox, which strains thin capacity.",
      "verifyInDemo": [
       "Post-discharge sequence triggered from an Epic discharge, with patient replies and escalations recorded in Epic.",
       "How the collaborative inbox prioritizes responses and how much staff time a 30-discharge day requires.",
       "Medication and follow-up appointment confirmation within the sequence and write-back to Cadence.",
       "Reporting of reach, response and escalation by unit and contract cohort."
      ],
      "rfpQuestions": [
       "What exactly writes back to Epic (flowsheets, notes, In Basket), and which Epic IDNs use Care Navigator for post-discharge today?",
       "What staffing model did Vanderbilt's Discharge Care Center use to respond to messages?",
       "Do you offer AI-handled responses or staffed services to reduce our team's inbox load, and how are they priced?",
       "Can message-level data be exported to our warehouse without additional fees?"
      ]
     },
     {
      "vendor": "Health Catalyst (incl. Twistle)",
      "status": "nearMiss",
      "rank": 4,
      "weightedScore": 66.0,
      "scores": {
       "functional": {
        "score": 4,
        "rationale": "Twistle provides automated post-discharge messaging with escalation to the EHR inbox, alongside an analytics platform that can track readmissions.",
        "evidence": [
         "https://www.healthcatalyst.com/products/twistle-patient-engagement",
         "https://healthcatalyst.com/learn/success-stories/post-discharge-monitoring"
        ],
        "verify": false
       },
       "integration": {
        "score": 3,
        "rationale": "Twistle escalates to the care team's EHR inbox, but Epic is not named in sources opened.",
        "evidence": [
         "https://www.healthcatalyst.com/products/twistle-patient-engagement"
        ],
        "verify": true
       },
       "proof": {
        "score": 3,
        "rationale": "A large health system reported an O/E readmission ratio change from 1.13 to 0.81 with Twistle (unnamed, vendor-reported), and population health applications scored 80.6 unranked in 2025 KLAS.",
        "evidence": [
         "https://healthcatalyst.com/learn/success-stories/post-discharge-monitoring",
         "https://klasresearch.com/best-in-klas-ranking/population-health-management/2025/256"
        ],
        "verify": false
       },
       "timeToValue": {
        "score": 3,
        "rationale": "Automated messaging limits staff effort, but no implementation timeline or client effort is described.",
        "evidence": [
         "https://www.healthcatalyst.com/products/twistle-patient-engagement"
        ],
        "verify": true
       },
       "viability": {
        "score": 3,
        "rationale": "1,100+ client organizations, but a CEO change in February 2026 and recent acquisitions add direction risk.",
        "evidence": [
         "https://healthcatalyst.com/news/health-catalyst-appoints-ben-albert-as-chief-executive-officer",
         "https://www.hcinnovationgroup.com/finance-revenue-cycle/article/55336749/new-leadership-partnerships-and-platforms-mark-health-catalysts-transformative-year"
        ],
        "verify": false
       },
       "measurement": {
        "score": 4,
        "rationale": "Reports readmission O/E ratios and has a data platform suited to baselines, though export terms for Twistle data were not reviewed.",
        "evidence": [
         "https://healthcatalyst.com/learn/success-stories/post-discharge-monitoring",
         "https://www.hcinnovationgroup.com/finance-revenue-cycle/article/55336749/new-leadership-partnerships-and-platforms-mark-health-catalysts-transformative-year"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No public pricing model or risk-sharing option was found in sources opened.",
        "evidence": [
         "https://www.healthcatalyst.com/products/twistle-patient-engagement"
        ],
        "verify": true
       }
      },
      "whyNot": "Sound messaging and measurement capability, but the readmission reference is unnamed and Epic integration is not documented; it also overlaps with analytics ACME can run in Caboodle."
     },
     {
      "vendor": "Wolters Kluwer UpToDate Patient Engagement (formerly Emmi)",
      "status": "nearMiss",
      "rank": 5,
      "weightedScore": 65.0,
      "scores": {
       "functional": {
        "score": 4,
        "rationale": "Automated patient education and outreach programs aimed at follow-up adherence and fewer readmissions, with outcome reporting.",
        "evidence": [
         "https://www.wolterskluwer.com/en/solutions/uptodate/enterprise/patient-engagement/driven-by-outcomes"
        ],
        "verify": false
       },
       "integration": {
        "score": 2,
        "rationale": "No Epic integration for the outreach programs was described on the page opened.",
        "evidence": [
         "https://www.wolterskluwer.com/en/solutions/uptodate/enterprise/patient-engagement/driven-by-outcomes"
        ],
        "verify": true
       },
       "proof": {
        "score": 4,
        "rationale": "Best in KLAS 2026 for Patient-Driven Care Management, with UAB Medicine and Memorial Hospital at Gulfport as named references.",
        "evidence": [
         "https://klasresearch.com/report/2026-best-in-klas-awards-software-and-services/3906",
         "https://hitconsultant.net/2026/02/04/2026-best-in-klas-winners-full-list-software-services/",
         "https://www.wolterskluwer.com/en/solutions/uptodate/enterprise/patient-engagement/driven-by-outcomes"
        ],
        "verify": false
       },
       "timeToValue": {
        "score": 3,
        "rationale": "UAB reported reduced nurse outreach effort, but no timeline or client build effort is published.",
        "evidence": [
         "https://www.wolterskluwer.com/en/solutions/uptodate/enterprise/patient-engagement/driven-by-outcomes"
        ],
        "verify": true
       },
       "viability": {
        "score": 4,
        "rationale": "Part of Wolters Kluwer, a large established publisher of clinical content.",
        "evidence": [
         "https://www.wolterskluwer.com/en/solutions/uptodate/enterprise/patient-engagement/driven-by-outcomes"
        ],
        "verify": false
       },
       "measurement": {
        "score": 3,
        "rationale": "Reports engagement, care gaps and outcome tracking, but baseline methodology and export terms are not described.",
        "evidence": [
         "https://www.wolterskluwer.com/en/solutions/uptodate/enterprise/patient-engagement/driven-by-outcomes"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No public pricing model or risk-sharing option was found in sources opened.",
        "evidence": [
         "https://www.wolterskluwer.com/en/solutions/uptodate/enterprise/patient-engagement/driven-by-outcomes"
        ],
        "verify": true
       }
      },
      "whyNot": "Independent KLAS recognition and named references, but no Epic integration was found for the outreach programs and escalation to care teams is not described."
     },
     {
      "vendor": "Get Well",
      "status": "nearMiss",
      "rank": 6,
      "weightedScore": 62.0,
      "scores": {
       "functional": {
        "score": 4,
        "rationale": "Digital Transitional Care Management delivers procedure-specific care plans, virtual check-ins, education and real-time risk flagging.",
        "evidence": [
         "https://www.getwellnetwork.com/resource/case-study-improve-value-based-care-performance/",
         "https://getwellnetwork.com/?p=25131"
        ],
        "verify": false
       },
       "integration": {
        "score": 3,
        "rationale": "Vendor states integration with Epic and Oracle, and St. Tammany is described as an Epic client, but integration points are not detailed.",
        "evidence": [
         "https://www.getwellnetwork.com/resource/case-study-improve-value-based-care-performance/",
         "https://getwellnetwork.com/?p=25131"
        ],
        "verify": true
       },
       "proof": {
        "score": 3,
        "rationale": "St. Tammany Health System reported a 13% COPD readmission reduction, and an unnamed case study reports a 45.6% reduction, both vendor-published.",
        "evidence": [
         "https://getwellnetwork.com/?p=25131",
         "https://www.getwellnetwork.com/resource/case-study-improve-value-based-care-performance/"
        ],
        "verify": false
       },
       "timeToValue": {
        "score": 3,
        "rationale": "Automated virtual check-ins limit staff effort, but no timeline is published.",
        "evidence": [
         "https://getwellnetwork.com/?p=25131"
        ],
        "verify": true
       },
       "viability": {
        "score": 2,
        "rationale": "Ownership and funding were not found in sources opened.",
        "evidence": [
         "https://www.getwellnetwork.com/resource/case-study-improve-value-based-care-performance/"
        ],
        "verify": true
       },
       "measurement": {
        "score": 3,
        "rationale": "Outcomes analytics are described, but methodology and export terms are not.",
        "evidence": [
         "https://www.getwellnetwork.com/resource/case-study-improve-value-based-care-performance/"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No public pricing model or risk-sharing option was found in sources opened.",
        "evidence": [
         "https://www.getwellnetwork.com/resource/case-study-improve-value-based-care-performance/"
        ],
        "verify": true
       }
      },
      "whyNot": "Relevant digital TCM features and an Epic customer reference, but the named reference is a community system and the larger outcome claim is from an unnamed customer."
     },
     {
      "vendor": "Luma Health",
      "status": "evaluated",
      "rank": 7,
      "weightedScore": 60.0,
      "scores": {
       "functional": {
        "score": 3,
        "rationale": "Patient access platform whose automations include post-discharge and readmission follow-up calls, but transitions are not its primary focus.",
        "evidence": [
         "https://go.lumahealth.io/epic/"
        ],
        "verify": false
       },
       "integration": {
        "score": 5,
        "rationale": "Documents Epic In Basket, Media Manager and order entry write-back with named Epic customers including UF Health, UW Medicine and Virtua Health.",
        "evidence": [
         "https://go.lumahealth.io/epic/"
        ],
        "verify": false
       },
       "proof": {
        "score": 2,
        "rationale": "Named Epic references relate to patient access, and no transitions or readmission reference was found.",
        "evidence": [
         "https://go.lumahealth.io/epic/"
        ],
        "verify": true
       },
       "timeToValue": {
        "score": 3,
        "rationale": "Epic write-back is established, but post-discharge program setup effort and timeline are not described.",
        "evidence": [
         "https://go.lumahealth.io/epic/"
        ],
        "verify": true
       },
       "viability": {
        "score": 2,
        "rationale": "Ownership and funding were not found in sources opened.",
        "evidence": [
         "https://go.lumahealth.io/epic/"
        ],
        "verify": true
       },
       "measurement": {
        "score": 2,
        "rationale": "No readmission or follow-up outcome reporting was found.",
        "evidence": [
         "https://go.lumahealth.io/epic/"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No public pricing model or risk-sharing option was found in sources opened.",
        "evidence": [
         "https://go.lumahealth.io/epic/"
        ],
        "verify": true
       }
      }
     },
     {
      "vendor": "Commure Engage (formerly Memora Health)",
      "status": "evaluated",
      "rank": 8,
      "weightedScore": 47.0,
      "scores": {
       "functional": {
        "score": 3,
        "rationale": "Automates two-way patient messaging, routing of patient-reported concerns and navigation pathways, without a described post-discharge program.",
        "evidence": [
         "https://www.commure.com/engage",
         "https://www.commure.com/press-releases/commure-acquires-memora-health-to-enhance-intelligent-care-navigation"
        ],
        "verify": false
       },
       "integration": {
        "score": 2,
        "rationale": "No Epic integration evidence in sources opened.",
        "evidence": [
         "https://www.commure.com/engage"
        ],
        "verify": true
       },
       "proof": {
        "score": 2,
        "rationale": "No customers named in sources opened.",
        "evidence": [
         "https://www.healthcarefinancenews.com/news/commure-acquires-digital-care-navigator-memora-health"
        ],
        "verify": true
       },
       "timeToValue": {
        "score": 2,
        "rationale": "No implementation timeline or client effort described.",
        "evidence": [
         "https://www.commure.com/engage"
        ],
        "verify": true
       },
       "viability": {
        "score": 3,
        "rationale": "Acquired by Commure in December 2024 and continued as Commure Engage, with integration risk.",
        "evidence": [
         "https://www.healthcarefinancenews.com/news/commure-acquires-digital-care-navigator-memora-health",
         "https://www.commure.com/press-releases/commure-acquires-memora-health-to-enhance-intelligent-care-navigation"
        ],
        "verify": false
       },
       "measurement": {
        "score": 2,
        "rationale": "No outcome reporting or export terms described.",
        "evidence": [
         "https://www.commure.com/engage"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No public pricing model or risk-sharing option was found in sources opened.",
        "evidence": [
         "https://www.commure.com/engage"
        ],
        "verify": true
       }
      }
     }
    ]
   }
  ],
  "screenedOut": [
   {
    "vendor": "Epic (Healthy Planet, Compass Rose, Care Management)",
    "reason": "Evaluated as the baseline in epicBaseline rather than as a lot candidate."
   },
   {
    "vendor": "Innovaccer",
    "reason": "Care management platform that duplicates Healthy Planet and Compass Rose for a single-EHR Epic client; no multi-EHR affiliate need identified. Flagged competitor."
   },
   {
    "vendor": "Lightbeam Health Solutions",
    "reason": "Care management platform that duplicates Healthy Planet and Compass Rose; Epic not named in sources; no multi-EHR affiliate need identified. Flagged competitor."
   },
   {
    "vendor": "Arcadia",
    "reason": "Data and care management platform that duplicates Healthy Planet and Compass Rose; Epic integration not described; ownership changed in 2025. Flagged competitor."
   },
   {
    "vendor": "Elligint Health (HealthEC + VirtualHealth HELIOS)",
    "reason": "Care management platform that duplicates Epic modules; no Epic evidence; recent merger. Flagged competitor."
   },
   {
    "vendor": "ZeOmega (Jiva)",
    "reason": "Knockout: sells mainly to health plans and no provider health system references were confirmed."
   },
   {
    "vendor": "Persivia (CareSpace)",
    "reason": "Population health platform that duplicates Epic modules; only a 2020 customer source located. Flagged competitor."
   },
   {
    "vendor": "Qventus",
    "reason": "Targets inpatient discharge timing and excess days (throughput) rather than the post-discharge transitions gap; Epic Case Management covers discharge milestones in the baseline."
   },
   {
    "vendor": "Optum Home & Community Care (formerly naviHealth)",
    "reason": "Knockout: current health-system-facing offering could not be verified after the naviHealth name was retired; owned by a payer."
   },
   {
    "vendor": "Oracle Health (Health Data Intelligence / population health)",
    "reason": "Native module of a different EHR; not applicable to an Epic client with no non-Epic affiliates identified."
   },
   {
    "vendor": "MEDITECH (Expanse Care Coordination / Population Health)",
    "reason": "Native module of a different EHR; not applicable to an Epic client."
   },
   {
    "vendor": "ClosedLoop",
    "reason": "Readmission risk modeling overlaps Epic's native readmission model that ACME owns; not tied to a purchase gap. Flagged competitor."
   },
   {
    "vendor": "Vizient (Clinical Data Base)",
    "reason": "Peer benchmarking and collaboratives rather than a transitions workflow purchase; ACME membership status unknown (see openQuestions). Flagged competitor."
   },
   {
    "vendor": "Premier (PINC AI)",
    "reason": "Current readmission-specific product not confirmed; benchmarking overlaps in-house measurement. Flagged competitor."
   },
   {
    "vendor": "Cedar Gate Technologies",
    "reason": "Value-based care platform oriented to payers and employers that duplicates Epic modules; pending ownership change to IQVIA. Flagged competitor."
   },
   {
    "vendor": "Lumeris",
    "reason": "Outsourced value-based care enablement rather than a transitions tool purchase; no health system customers named."
   },
   {
    "vendor": "Aledade",
    "reason": "Serves independent practices and may compete with an IDN's own clinically integrated network rather than sell to it."
   },
   {
    "vendor": "Evolent",
    "reason": "Knockout: sells mainly to health plans; no transitions-of-care offering for provider health systems described."
   },
   {
    "vendor": "Signify Health",
    "reason": "Knockout: health-plan-facing, and the health-system ACO offering (Caravan Health) could not be confirmed as current."
   },
   {
    "vendor": "Unite Us",
    "reason": "Social care referral is outside this use case's scope; revisit with Epic's Community Resource Network if social needs become a transitions driver."
   },
   {
    "vendor": "findhelp",
    "reason": "Social care referral is outside this use case's scope; revisit with Epic's Community Resource Network if social needs become a transitions driver."
   },
   {
    "vendor": "Salesforce Health Cloud",
    "reason": "Build-heavy CRM platform requiring a systems integrator, which conflicts with ACME's 2.1 unallocated FTE when the workflow lives in Epic."
   },
   {
    "vendor": "Microsoft for Healthcare (Dynamics 365, Azure Health Data Services)",
    "reason": "Build-heavy platform requiring custom development, which conflicts with ACME's thin capacity when the workflow lives in Epic."
   },
   {
    "vendor": "Health Recovery Solutions",
    "reason": "Remote monitoring is a separate program with device logistics and monitoring staff; not tied to a primary gap. Strong Epic integration makes it a candidate for a later high-risk cohort add-on."
   },
   {
    "vendor": "Vivify Health",
    "reason": "Remote monitoring is a separate program with device logistics; payer-owned (Optum) and readmission claims are vendor-reported only."
   }
  ],
  "assumptions": [
   "ACME licenses Epic Healthy Planet, Compass Rose, Case Management and the cognitive computing readmission model; client D3 lists Epic inpatient and ambulatory but not module-level licenses.",
   "ACME has no non-Epic affiliate hospitals or practices in scope; if it does, multi-EHR care management platforms screened out here should be reconsidered.",
   "The December 23, 2026 first-unit launch runs on Epic native workflow; any purchase from these lots lands in Phase 3 or the February 2027 scale-up.",
   "All vendors scored 2 on commercial transparency because no public pricing was found; pricing must come from RFP responses.",
   "Vendor-reported outcome figures were not treated as independent proof; only KLAS rankings and the NEJM Catalyst study cited by Artera were counted as independent.",
   "The 14 entries excluded in the landscape (for example Jvion, PatientPing, Collective Medical, naviHealth) were carried forward under their current owners and not re-evaluated.",
   "WellSky CarePort appears in Lot A (Community Referral Network) and Lot B (Interop) because they are separately licensable products."
  ],
  "openQuestions": [
   "Which Epic modules does ACME license today (Healthy Planet, Compass Rose, Community Resource Network), and does it already contract with CarePort, Aidin, Bamboo Health or Artera?",
   "Can the state HIE expand rural and SNF ADT coverage, and at what cost, before ACME buys a commercial notification network?",
   "Which SNFs and home health agencies are ACME's top post-acute partners by volume, and which EHRs do they run (PointClickCare or other)?",
   "Is ACME a Vizient or Premier member whose existing benchmarking could supply readmission baselines?",
   "Does ACME prefer vendor-staffed outreach (Andor Health with Psynergy, CipherHealth nurse programs) or automation worked by its own care managers?",
   "Epic integration for Bamboo Health, PointClickCare, Olio, Artera write-back and Andor's transitional care agents was not confirmed and must be verified with Epic references."
  ]
 },
 "opp-2": {
  "useCaseId": "opp-2",
  "asOf": "2026-10-21",
  "rubricVersion": "vendor-eval-v1",
  "recommendation": {
   "deliveryPath": "combine",
   "summary": "Keep Epic Healthy Planet as the registry and workflow layer and build the chronic-cohort risk model, PDC logic and alert thresholds on Caboodle, as UCLA Health did with an Epic-based risk model, because clinical validation is internal work under any path. Buy a cross-payer dispensed-medication feed first (demo Surescripts and DrFirst), since adherence measures cannot be computed from ACME's two-payer claims feed, and demo Cozeva, Innovaccer and Health Catalyst for multi-contract gap reconciliation scoped as an add-on to Healthy Planet rather than a second platform. Treat pharmacist intervention services (Linea, AssureCare, AnewHealth) as conditional on ACME's own pharmacist capacity, because every remaining candidate lacks a named health-system and Epic reference."
  },
  "epicBaseline": {
   "covers": [
    "Risk stratification of populations and prioritization of high-need and rising-risk patients using labs, risk scores, paid claims and external data (Epic population health page).",
    "Point-of-care care-gap prompts and year-round multichannel gap-closure campaigns that switch channel when patients do not respond (Epic population health page).",
    "Payer claims integration into the Epic record and QRDA-I aggregation with QRDA-III submission to CMS (Epic population health page).",
    "Measure tracking for chronic panels in Healthy Planet registries, for example A1C control, blood pressure and statin therapy for cardiovascular disease (Novant Health).",
    "Care-gap status exchange from health plans for attributed populations through Epic Payer Platform, for payers that use it (KLAS via TechTarget, Nov 2023).",
    "Ranked 2nd in Best in KLAS 2025 Population Health Management (89.2), so the base platform is independently rated.",
    "In-house risk modeling on Epic data is proven: UCLA Health's Epic-based population risk model with proactive care management cut potentially preventable admissions 27% in a stepped-wedge study (AJMC, Nov 2024). Cosmos AI risk prediction is previewed with no release date."
   ],
   "gaps": [
    "No cross-payer dispensed-medication data: Epic's population health page does not mention medication adherence, and ACME's claims feed covers two commercial payers monthly with a 60-90 day lag, so PDC-based Part D and statin measures cannot be computed for most patients (Lot B).",
    "No multi-contract gap and attribution reconciliation for payers outside Epic Payer Platform; KLAS notes payers need a dedicated team for the gap exchange, and the CFO flag requires reconciliation before scoring (Lot A).",
    "Population health pharmacy appears only as a roadmap item on Epic's page, and ACME has 2.1 unallocated FTE, so intervention capacity for medication optimization may be missing (Lot C, conditional).",
    "Clinical validation of a prescribing-adjacent model and alert-volume governance are internal work under any delivery path; no vendor removes them."
   ],
   "sources": [
    {
     "title": "Epic Population Health",
     "url": "https://www.epic.com/software/population-health/",
     "retrieved": "2026-10-21"
    },
    {
     "title": "Best in KLAS 2025: Population Health Management",
     "url": "https://klasresearch.com/best-in-klas-ranking/population-health-management/2025/256",
     "retrieved": "2026-10-21"
    },
    {
     "title": "Novant Health deploys Epic's Healthy Planet (Healthcare IT News)",
     "url": "https://healthcareitnews.com/news/novant-health-deploys-epics-healthy-planet-tech-track-patient-conditions",
     "retrieved": "2026-10-21"
    },
    {
     "title": "KLAS: Epic's Payer Platform Generates High User Satisfaction (TechTarget, Nov 2023)",
     "url": "https://www.techtarget.com/healthcarepayers/news/366603373/KLAS-Epics-Payer-Platform-Generates-High-User-Satisfaction",
     "retrieved": "2026-10-21"
    },
    {
     "title": "Epic unveils AI features, Cosmos AI risk prediction preview (Fierce Healthcare, Aug 2025)",
     "url": "https://www.fiercehealthcare.com/health-tech/epic-unveils-major-ai-features-ai-charting-microsoft-cosmos-ai-risk-prediction-and-rcm",
     "retrieved": "2026-10-21"
    },
    {
     "title": "Proactive care management of AI-identified at-risk patients decreases preventable admissions (AJMC, Nov 2024)",
     "url": "https://www.ajmc.com/view/proactive-care-management-of-ai-identified-at-risk-patients-decreases-preventable-admissions",
     "retrieved": "2026-10-21"
    }
   ]
  },
  "lots": [
   {
    "id": "lot-a",
    "name": "Multi-contract quality measure engine and payer gap reconciliation, delivered into Epic",
    "gap": "Healthy Planet computes measures on Epic data and receives payer gap status only from payers using Epic Payer Platform; ACME needs NCQA-certified measures reconciled against each payer's gap list and attribution reconciled across contracts before scoring.",
    "why": "The CFO flag and the cohort attribution drift risk require one reconciled view of measures and attribution per contract, and ACME's claims feed (two payers, monthly, 60-90 day lag, manual matching) cannot supply it; this is bought as an add-on to Healthy Planet, not as a replacement.",
    "evaluated": [
     {
      "vendor": "Cozeva",
      "status": "shortlist",
      "rank": 1,
      "weightedScore": 71.0,
      "scores": {
       "functional": {
        "score": 4,
        "rationale": "Provides an NCQA-certified HEDIS and AMP measure engine, payer care-gap reconciliation and pre-visit and point-of-care gap flags for provider groups, while risk stratification and medication-level prioritization are not described.",
        "evidence": [
         "https://www.healthcareitnews.com/news/providence-scores-big-vbc-wins-risk-and-quality-reporting-tool",
         "https://corp.cozeva.com/?p=7115"
        ],
        "verify": false
       },
       "integration": {
        "score": 4,
        "rationale": "Providence's medical group runs Cozeva inside its Epic EHR with an overlay for affiliated practices on other EHRs, but the integration method (Toolbox, FHIR, BPA or SMART app) is not documented.",
        "evidence": [
         "https://www.healthcareitnews.com/news/providence-scores-big-vbc-wins-risk-and-quality-reporting-tool"
        ],
        "verify": true
       },
       "proof": {
        "score": 4,
        "rationale": "Named Tier 1 reference at Providence (1 to 50+ quality contracts and about 100,000 to 650,000+ covered patients, 2016-2024) plus a KLAS 2025 PHM score of 87.4 (listed, not ranked); results are not independently studied.",
        "evidence": [
         "https://www.healthcareitnews.com/news/providence-scores-big-vbc-wins-risk-and-quality-reporting-tool",
         "https://klasresearch.com/best-in-klas-ranking/population-health-management/2025/256"
        ],
        "verify": false
       },
       "timeToValue": {
        "score": 3,
        "rationale": "Ingests health plan gap reports, claims, registry and EHR data directly, which lessens reliance on ACME's weak claims feed, but no implementation timeline or client FTE requirement is published.",
        "evidence": [
         "https://www.healthcareitnews.com/news/providence-scores-big-vbc-wins-risk-and-quality-reporting-tool"
        ],
        "verify": true
       },
       "viability": {
        "score": 3,
        "rationale": "Privately held by Applied Research Works with 84+ clients and 25M+ lives per vendor, and a merger with Vatica Health stated on its site without date or terms.",
        "evidence": [
         "https://corp.cozeva.com/?p=7115"
        ],
        "verify": true
       },
       "measurement": {
        "score": 3,
        "rationale": "Real-time measure engine and reconciliation against payer reports give a gap-closure baseline, but data export and ownership terms are not described.",
        "evidence": [
         "https://www.healthcareitnews.com/news/providence-scores-big-vbc-wins-risk-and-quality-reporting-tool"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No pricing model or commercial terms found in sources opened.",
        "evidence": [
         "https://corp.cozeva.com/?p=7115"
        ],
        "verify": true
       }
      },
      "whyShortlisted": "Cozeva is the only candidate in this lot with a named Epic health system reference for the exact gap: reconciling many payers' gap reports against EHR data across 50+ quality contracts. It also ingests payer gap files directly, which reduces dependence on ACME's weakest data asset.",
      "keyRisk": "Its core is quality and risk-adjustment reporting, so chronic-cohort risk stratification and medication-level prioritization may still have to come from Epic or an in-house model.",
      "verifyInDemo": [
       "Live reconciliation of one commercial payer's gap file against Epic data, showing how disagreements are flagged, worked and resolved",
       "How a gap appears to an ACME prescriber inside Epic (BPA, Storyboard, SMART app or separate window) and whether closure writes back to Epic",
       "Calculation of Part D adherence measures (PDC-DR, PDC-RASA, PDC-STA) and statin measures (SPC, SPD, SUPD), including which fill data source is used",
       "Patient-level export of numerators, denominators and exclusions so ACME can hold its own baseline"
      ],
      "rfpQuestions": [
       "Name Epic health systems besides Providence running Cozeva inside Epic and state the integration method (Epic Toolbox or Showroom listing, FHIR, HL7, SMART on FHIR).",
       "How many payers' gap reports do you ingest for a single health-system client today, and what is your process and turnaround when a payer disputes a closure?",
       "How does the Vatica Health merger affect ownership, roadmap and support for provider quality workflows outside risk adjustment?",
       "What is the pricing basis (per attributed life, per provider or per contract) and the ACME FTE requirement by implementation phase?"
      ]
     },
     {
      "vendor": "Innovaccer",
      "status": "shortlist",
      "rank": 2,
      "weightedScore": 66.0,
      "scores": {
       "functional": {
        "score": 4,
        "rationale": "Covers AI risk stratification on clinical, claims and SDoH data, measure status at numerator, denominator and exclusion level, care-gap tracking and point-of-care delivery, with Pharmacy Quality Solutions adding medication-quality measure content; much of this overlaps Healthy Planet.",
        "evidence": [
         "https://webflow.innovaccer.com/products/population-health-analytics",
         "https://www.businesswire.com/news/home/20240312730351/en/Innovaccer-Announces-Acquisition-of-Pharmacy-Quality-Solutions-to-Accelerate-Value-Based-Care-in-Pharmacy-Settings"
        ],
        "verify": false
       },
       "integration": {
        "score": 3,
        "rationale": "InNote and Provider Copilot are listed in Epic Connection Hub with automatic sign-in from Epic and quality-gap flagging, but no named Epic health system uses them for gap closure in sources opened.",
        "evidence": [
         "https://innovaccer.com/blogs/innovaccers-innote-application-now-available-in-the-epic-app-orchard/",
         "https://innovaccer.ai/blogs/innovaccer-provider-copilot-now-available-in-epic-connection-hub"
        ],
        "verify": true
       },
       "proof": {
        "score": 4,
        "rationale": "Ranked 3rd in Best in KLAS 2025 PHM (85.7) and Banner Health reports about 18% gap closure among providers using the in-office workflow, though Banner runs Oracle Cerner rather than Epic.",
        "evidence": [
         "https://klasresearch.com/best-in-klas-ranking/population-health-management/2025/256",
         "https://www.beckershospitalreview.com/innovation/banner-health-s-big-bet-on-population-health.html",
         "https://www.techtarget.com/revcyclemanagement/news/366600642/KLAS-Epic-Arcadia-Innovaccer-Earn-Top-Marks-for-Value-Based-Care"
        ],
        "verify": false
       },
       "timeToValue": {
        "score": 2,
        "rationale": "A full data-platform deployment aggregating EHR, claims and SDoH data implies data engineering load against ACME's 2.1 unallocated FTE, and no timeline is published.",
        "evidence": [
         "https://webflow.innovaccer.com/products/population-health-analytics"
        ],
        "verify": true
       },
       "viability": {
        "score": 4,
        "rationale": "Venture-backed with a Series F in January 2025 and four acquisitions from 2024 to 2025, which indicates funding depth along with some integration risk.",
        "evidence": [
         "https://en.wikipedia.org/wiki/Innovaccer"
        ],
        "verify": false
       },
       "measurement": {
        "score": 3,
        "rationale": "Measure status tracking supports gap-closure baselines, but data export and ownership terms are not described.",
        "evidence": [
         "https://webflow.innovaccer.com/products/population-health-analytics"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No pricing model or commercial terms found in sources opened.",
        "evidence": [
         "https://webflow.innovaccer.com/products/population-health-analytics"
        ],
        "verify": true
       }
      },
      "whyShortlisted": "Innovaccer has the strongest independent PHM recognition among Epic-capable candidates in this lot and covers risk stratification, measure tracking and point-of-care gap delivery in one product. It has Epic Connection Hub apps and, through Pharmacy Quality Solutions, medication-quality measure content tied to the Part D adherence measures.",
      "keyRisk": "It is a second population health platform beside Healthy Planet, so ACME could pay for duplicated registries and outreach while carrying a data integration load larger than its staffing supports.",
      "verifyInDemo": [
       "Gap and risk display inside an Epic encounter through InNote or Provider Copilot, including write-back of a closed gap",
       "Reconciliation of an Innovaccer measure result against a payer gap report and against Healthy Planet for the same patient",
       "How Pharmacy Quality Solutions (EQuIPP) content supplies PDC adherence status for health-system patients across payers",
       "A scoped configuration that uses only measure reconciliation and medication-quality modules without replacing Healthy Planet registries"
      ],
      "rfpQuestions": [
       "Name Epic health systems live with InNote or Provider Copilot for quality-gap closure and their go-live dates.",
       "Can ACME license reconciliation and medication-quality modules without the full Gravity data platform, and what internal FTEs does that scope require?",
       "Which HEDIS, Part D Stars and MSSP/APP measures are NCQA-certified in your engine for the current measurement year?",
       "Under what data-use terms would Pharmacy Quality Solutions pharmacy data be available to a provider customer?"
      ]
     },
     {
      "vendor": "Health Catalyst",
      "status": "shortlist",
      "rank": 3,
      "weightedScore": 64.0,
      "scores": {
       "functional": {
        "score": 4,
        "rationale": "Population Builder offers 21 chronic registries, 6,000+ value sets and ML risk models, with automated CMS and HEDIS measure tracking, outreach prioritization, ACO contract tracking and an Embedded Care Gaps application.",
        "evidence": [
         "https://www.healthcatalyst.com/healthcare-analytics/population-health-value-based-care/population-health-management",
         "https://www.healthcatalyst.com/insights/population-builder-stratification-module",
         "https://downloads.healthcatalyst.com/success-stories/CHNw_Closing-Care-Gaps-at-the-Point-of-Care-Yields-4X-Value-Realization_case-study.pdf"
        ],
        "verify": false
       },
       "integration": {
        "score": 3,
        "rationale": "Embedded Care Gaps runs inside the EHR at Community Health Network with order pre-population, but sources opened do not name Epic or describe the integration method.",
        "evidence": [
         "https://downloads.healthcatalyst.com/success-stories/CHNw_Closing-Care-Gaps-at-the-Point-of-Care-Yields-4X-Value-Realization_case-study.pdf",
         "https://www.healthcatalyst.com/healthcare-analytics/population-health-value-based-care/population-health-management"
        ],
        "verify": true
       },
       "proof": {
        "score": 4,
        "rationale": "Community Health Network reports 373,000+ gaps closed in six months with the embedded application, and PeaceHealth and Lehigh Valley Health Network are cited, all vendor-published; KLAS 2025 PHM score 80.6 (listed, not ranked).",
        "evidence": [
         "https://downloads.healthcatalyst.com/success-stories/CHNw_Closing-Care-Gaps-at-the-Point-of-Care-Yields-4X-Value-Realization_case-study.pdf",
         "https://www.healthcatalyst.com/healthcare-analytics/population-health-value-based-care/population-health-management",
         "https://klasresearch.com/best-in-klas-ranking/population-health-management/2025/256"
        ],
        "verify": false
       },
       "timeToValue": {
        "score": 2,
        "rationale": "Vendor describes a three-phase implementation on its Ignite data platform, a warehouse-scale effort for a client already consolidating Caboodle with thin spare capacity.",
        "evidence": [
         "https://www.healthcatalyst.com/healthcare-analytics/population-health-value-based-care/population-health-management"
        ],
        "verify": true
       },
       "viability": {
        "score": 3,
        "rationale": "Public company (Nasdaq: HCAT) with a CEO transition announced for June 2026 and customers migrating to Ignite, which adds platform-transition risk.",
        "evidence": [
         "https://www.hcinnovationgroup.com/finance-revenue-cycle/article/55336749/new-leadership-partnerships-and-platforms-mark-health-catalysts-transformative-year"
        ],
        "verify": false
       },
       "measurement": {
        "score": 3,
        "rationale": "Year-round measure tracking and analytics applications support baselines, but data export terms are not stated.",
        "evidence": [
         "https://www.healthcatalyst.com/healthcare-analytics/population-health-value-based-care/population-health-management",
         "https://downloads.healthcatalyst.com/success-stories/CHNw_Closing-Care-Gaps-at-the-Point-of-Care-Yields-4X-Value-Realization_case-study.pdf"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No pricing model or commercial terms found in sources opened.",
        "evidence": [
         "https://www.healthcatalyst.com/healthcare-analytics/population-health-value-based-care/population-health-management"
        ],
        "verify": true
       }
      },
      "whyShortlisted": "Health Catalyst has the most complete chronic registry and measure-tracking content in the lot and a health-system reference for gap closure delivered inside the EHR at volume. Its explainable risk models and outreach prioritization map to the cohort-definition and model-validation milestones.",
      "keyRisk": "The Ignite migration and a 2026 CEO change create delivery and roadmap risk, and its data-platform footprint overlaps ACME's Caboodle consolidation.",
      "verifyInDemo": [
       "Embedded Care Gaps running inside Epic Hyperspace, including order pre-population for a statin or ACE inhibitor/ARB gap",
       "Explainability output and validation report for a diabetes, heart failure or COPD risk model on a population similar to ACME's",
       "Whether the measure engine can run on Caboodle data without moving ACME onto Ignite",
       "Side-by-side of a payer gap list and the Health Catalyst gap list for one contract, with disagreement handling"
      ],
      "rfpQuestions": [
       "Name Epic health systems running Embedded Care Gaps and state the integration method.",
       "What is the minimum deployment (applications only, or Ignite required) and the ACME FTE ask per implementation phase?",
       "How do the 2026 leadership change and Ignite migration affect roadmap and support for existing population health applications?",
       "Which measures are NCQA-certified, and how are Part D adherence measures sourced when the client lacks pharmacy claims?"
      ]
     },
     {
      "vendor": "Arcadia",
      "status": "nearMiss",
      "rank": 4,
      "weightedScore": 60.0,
      "scores": {
       "functional": {
        "score": 4,
        "rationale": "Aggregates clinical, claims, social and engagement data for risk stratification, care management prioritization (Ascent Care), quality gap tracking and contract performance, with NCQA HEDIS and AMP certifications listed by the vendor.",
        "evidence": [
         "https://www.arcadia.io/population-health"
        ],
        "verify": false
       },
       "integration": {
        "score": 2,
        "rationale": "Arcadia states its workflows complement Epic, but sources opened show no Epic integration method or named Epic customer; its documented EHR embedding is in MEDITECH Expanse.",
        "evidence": [
         "https://arcadia.io/resources/arcadia-analytics-complements-epic",
         "https://arcadia.io/impact",
         "https://www.biospace.com/meditech-continues-to-optimize-arcadia-io-collaboration-with-expanse-population-health"
        ],
        "verify": true
       },
       "proof": {
        "score": 4,
        "rationale": "Ranked 5th in Best in KLAS 2025 PHM (79.7) and top-ranked in KLAS value-based care reimbursement 2022, with NorthShore - Edward-Elmhurst Health named but no reported outcomes.",
        "evidence": [
         "https://klasresearch.com/best-in-klas-ranking/population-health-management/2025/256",
         "https://www.techtarget.com/revcyclemanagement/news/366600642/KLAS-Epic-Arcadia-Innovaccer-Earn-Top-Marks-for-Value-Based-Care",
         "https://arcadia.io/impact"
        ],
        "verify": false
       },
       "timeToValue": {
        "score": 2,
        "rationale": "Multi-source aggregation platform with no published timeline or client effort estimate.",
        "evidence": [
         "https://www.arcadia.io/population-health"
        ],
        "verify": true
       },
       "viability": {
        "score": 3,
        "rationale": "Nordic Capital took a majority stake in July 2025; Nordic also led the Inovalon take-private, so portfolio consolidation is possible.",
        "evidence": [
         "https://www.mobihealthnews.com/news/nordic-capital-acquires-majority-stake-arcadia",
         "https://www.fiercehealthcare.com/tech/nordic-capital-insight-partners-take-inovalon-private-6-4b-acquisition"
        ],
        "verify": false
       },
       "measurement": {
        "score": 3,
        "rationale": "Contract performance and quality gap reporting support baselines, but data export terms are not stated.",
        "evidence": [
         "https://www.arcadia.io/population-health"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No pricing model or commercial terms found in sources opened.",
        "evidence": [
         "https://www.arcadia.io/population-health"
        ],
        "verify": true
       }
      },
      "whyNot": "Functional scope and KLAS standing are comparable to the shortlisted platforms, but no documented Epic integration path was found, which matters because the outcome requires gaps inside the prescribing workflow."
     },
     {
      "vendor": "Lightbeam Health Solutions",
      "status": "nearMiss",
      "rank": 5,
      "weightedScore": 58.0,
      "scores": {
       "functional": {
        "score": 4,
        "rationale": "AI risk stratification (including Jvion clinical AI and SDoH assets), quality and risk adjustment tools and condition programs for CHF, COPD and diabetes for ACOs and providers.",
        "evidence": [
         "https://www.healthcareitnews.com/news/lightbeam-acquires-jvions-ai-and-sdoh-assets",
         "https://lightbeamhealth.com/news/lightbeam-health-awarded-best-in-klas-for-remote-patient-monitoring/"
        ],
        "verify": false
       },
       "integration": {
        "score": 2,
        "rationale": "Vendor lists point-of-care integration, but no Epic-specific evidence was found.",
        "evidence": [
         "https://lightbeamhealth.com/news/lightbeam-health-awarded-best-in-klas-for-remote-patient-monitoring/",
         "https://amga.org/amga-membership/corporate-partner-directory/lightbeam-health-solutions"
        ],
        "verify": true
       },
       "proof": {
        "score": 4,
        "rationale": "Ranked 4th in Best in KLAS 2025 PHM (82.7), AMGA's analytics partner, with Carle Health and UnityPoint Health named in AMGA webinar listings.",
        "evidence": [
         "https://klasresearch.com/best-in-klas-ranking/population-health-management/2025/256",
         "https://amga.org/amga-membership/corporate-partner-directory/lightbeam-health-solutions"
        ],
        "verify": false
       },
       "timeToValue": {
        "score": 2,
        "rationale": "No implementation timeline or client effort estimate found.",
        "evidence": [
         "https://lightbeamhealth.com/news/lightbeam-health-awarded-best-in-klas-for-remote-patient-monitoring/"
        ],
        "verify": true
       },
       "viability": {
        "score": 3,
        "rationale": "Ownership not stated; acquisitive (Jvion assets 2022, Syntax Health December 2025).",
        "evidence": [
         "https://www.healthcareitnews.com/news/lightbeam-acquires-jvions-ai-and-sdoh-assets",
         "https://lightbeamhealth.com/news/lightbeam-health-solutions-acquires-syntax-health-expanding-value-based-contracting-and-incentive-design-capabilities/"
        ],
        "verify": true
       },
       "measurement": {
        "score": 2,
        "rationale": "No evidence found on outcome measurement methods or client data export.",
        "evidence": [
         "https://lightbeamhealth.com/news/lightbeam-health-awarded-best-in-klas-for-remote-patient-monitoring/"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No pricing model or commercial terms found in sources opened.",
        "evidence": [
         "https://lightbeamhealth.com/news/lightbeam-health-awarded-best-in-klas-for-remote-patient-monitoring/"
        ],
        "verify": true
       }
      },
      "whyNot": "Strong KLAS ranking and chronic-condition programs, but no Epic integration evidence and an emphasis on ACO care management and remote monitoring rather than multi-payer gap reconciliation."
     },
     {
      "vendor": "Reveleer",
      "status": "nearMiss",
      "rank": 6,
      "weightedScore": 51.0,
      "scores": {
       "functional": {
        "score": 3,
        "rationale": "AI care-gap identification and HEDIS and Stars support delivered in the clinician workflow, with a risk-adjustment emphasis and no risk stratification or medication adherence capability described.",
        "evidence": [
         "https://www.reveleer.com/who-we-serve/provider-solutions"
        ],
        "verify": false
       },
       "integration": {
        "score": 3,
        "rationale": "Vendor states native Epic integration that surfaces advisories in workflow without separate logins, but names no Epic customer.",
        "evidence": [
         "https://www.reveleer.com/who-we-serve/provider-solutions"
        ],
        "verify": true
       },
       "proof": {
        "score": 2,
        "rationale": "References are unnamed (a Florida health system, a national health network) and outcomes are vendor-reported risk-adjustment results.",
        "evidence": [
         "https://www.reveleer.com/who-we-serve/provider-solutions"
        ],
        "verify": true
       },
       "timeToValue": {
        "score": 2,
        "rationale": "No implementation timeline or client effort estimate found.",
        "evidence": [
         "https://www.reveleer.com/who-we-serve/provider-solutions"
        ],
        "verify": true
       },
       "viability": {
        "score": 3,
        "rationale": "Acquired Novillus in April 2025; combined company reports 72M covered lives across 86 organizations; ownership not stated.",
        "evidence": [
         "https://www.dotmed.com/legal/print/story.html?nid=64684"
        ],
        "verify": true
       },
       "measurement": {
        "score": 2,
        "rationale": "No evidence found on gap-closure measurement methods or data export.",
        "evidence": [
         "https://www.reveleer.com/who-we-serve/provider-solutions"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No pricing model or commercial terms found in sources opened.",
        "evidence": [
         "https://www.reveleer.com/who-we-serve/provider-solutions"
        ],
        "verify": true
       }
      },
      "whyNot": "States native Epic delivery, but its documented outcomes are risk-adjustment capture and no named health-system reference was found."
     },
     {
      "vendor": "Optum Performance Analytics",
      "status": "evaluated",
      "rank": 7,
      "weightedScore": 51.0,
      "scores": {
       "functional": {
        "score": 3,
        "rationale": "Identification and stratification, cost risk models, rising-risk detection, care-gap detection and HEDIS and Stars support, built primarily on claims data.",
        "evidence": [
         "https://cdn-aem.optum.com/content/dam/optum3/optum/en/resources/sell-sheet/optum-performance-analytics-sell-sheet.pdf"
        ],
        "verify": false
       },
       "integration": {
        "score": 2,
        "rationale": "No Epic or EHR workflow integration described in the sell sheet.",
        "evidence": [
         "https://cdn-aem.optum.com/content/dam/optum3/optum/en/resources/sell-sheet/optum-performance-analytics-sell-sheet.pdf"
        ],
        "verify": true
       },
       "proof": {
        "score": 2,
        "rationale": "Recognition cited is vendor-reported analyst awards (Frost & Sullivan 2021, IDC 2022) with no named provider health-system reference.",
        "evidence": [
         "https://cdn-aem.optum.com/content/dam/optum3/optum/en/resources/sell-sheet/optum-performance-analytics-sell-sheet.pdf",
         "https://www.frost.com/news/press-releases/optum-earns-acclaim-from-frost-sullivan-for-leading-the-population-health-management-market-with-its-integrated-technology-platforms-and-services/"
        ],
        "verify": true
       },
       "timeToValue": {
        "score": 2,
        "rationale": "Claims-centric analytics depend on ACME's weakest asset (two payers, monthly, 60-90 day lag, manual member matching).",
        "evidence": [
         "https://cdn-aem.optum.com/content/dam/optum3/optum/en/resources/sell-sheet/optum-performance-analytics-sell-sheet.pdf"
        ],
        "verify": true
       },
       "viability": {
        "score": 4,
        "rationale": "Backed by UnitedHealth Group; payer ownership may raise data-sharing concerns for a provider client.",
        "evidence": [
         "https://cdn-aem.optum.com/content/dam/optum3/optum/en/resources/sell-sheet/optum-performance-analytics-sell-sheet.pdf"
        ],
        "verify": false
       },
       "measurement": {
        "score": 3,
        "rationale": "Provider scorecards and validated cost models support outcome tracking; client data rights not stated.",
        "evidence": [
         "https://cdn-aem.optum.com/content/dam/optum3/optum/en/resources/sell-sheet/optum-performance-analytics-sell-sheet.pdf"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No pricing model or commercial terms found in sources opened.",
        "evidence": [
         "https://cdn-aem.optum.com/content/dam/optum3/optum/en/resources/sell-sheet/optum-performance-analytics-sell-sheet.pdf"
        ],
        "verify": true
       }
      }
     },
     {
      "vendor": "Milliman MedInsight",
      "status": "evaluated",
      "rank": 8,
      "weightedScore": 49.0,
      "scores": {
       "functional": {
        "score": 2,
        "rationale": "Claims-based analytics for at-risk group identification, quality dashboards and contract modeling, without point-of-care gap delivery.",
        "evidence": [
         "https://digital-release.wgnradio.com/business/press-releases/cision/20260303SF99818/milliman-medinsight-integrates-ai-and-next-generation-analytics-to-strengthen-value-based-care-decision-making"
        ],
        "verify": false
       },
       "integration": {
        "score": 2,
        "rationale": "No Epic integration described.",
        "evidence": [
         "https://digital-release.wgnradio.com/business/press-releases/cision/20260303SF99818/milliman-medinsight-integrates-ai-and-next-generation-analytics-to-strengthen-value-based-care-decision-making"
        ],
        "verify": true
       },
       "proof": {
        "score": 3,
        "rationale": "Used by 300+ payers, providers, ACOs and agencies and listed in KLAS 2025 PHM (74.5); no named health-system reference for gap closure.",
        "evidence": [
         "https://digital-release.wgnradio.com/business/press-releases/cision/20260303SF99818/milliman-medinsight-integrates-ai-and-next-generation-analytics-to-strengthen-value-based-care-decision-making",
         "https://klasresearch.com/best-in-klas-ranking/population-health-management/2025/256"
        ],
        "verify": false
       },
       "timeToValue": {
        "score": 2,
        "rationale": "Claims-based model depends on ACME's weakest data asset.",
        "evidence": [
         "https://digital-release.wgnradio.com/business/press-releases/cision/20260303SF99818/milliman-medinsight-integrates-ai-and-next-generation-analytics-to-strengthen-value-based-care-decision-making"
        ],
        "verify": true
       },
       "viability": {
        "score": 4,
        "rationale": "Part of Milliman, an established actuarial firm.",
        "evidence": [
         "https://digital-release.wgnradio.com/business/press-releases/cision/20260303SF99818/milliman-medinsight-integrates-ai-and-next-generation-analytics-to-strengthen-value-based-care-decision-making"
        ],
        "verify": false
       },
       "measurement": {
        "score": 3,
        "rationale": "Quality and outcomes dashboards and contract modeling support measurement; data rights not stated.",
        "evidence": [
         "https://digital-release.wgnradio.com/business/press-releases/cision/20260303SF99818/milliman-medinsight-integrates-ai-and-next-generation-analytics-to-strengthen-value-based-care-decision-making"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No pricing model or commercial terms found in sources opened.",
        "evidence": [
         "https://digital-release.wgnradio.com/business/press-releases/cision/20260303SF99818/milliman-medinsight-integrates-ai-and-next-generation-analytics-to-strengthen-value-based-care-decision-making"
        ],
        "verify": true
       }
      }
     },
     {
      "vendor": "Cedar Gate Technologies",
      "status": "evaluated",
      "rank": 9,
      "weightedScore": 45.0,
      "scores": {
       "functional": {
        "score": 3,
        "rationale": "Value-based care analytics, care management and clinical decision support on one platform for payers, providers and employers.",
        "evidence": [
         "https://www.businesswire.com/news/home/20250107462885/en/Cedar-Gate-Technologies-Named-Innovation-Leader-in-the-U.S.-Population-Health-Management-Market"
        ],
        "verify": false
       },
       "integration": {
        "score": 2,
        "rationale": "No Epic integration evidence found.",
        "evidence": [
         "https://www.businesswire.com/news/home/20250107462885/en/Cedar-Gate-Technologies-Named-Innovation-Leader-in-the-U.S.-Population-Health-Management-Market"
        ],
        "verify": true
       },
       "proof": {
        "score": 2,
        "rationale": "Frost Radar innovation recognition per vendor release; no named health-system reference.",
        "evidence": [
         "https://www.businesswire.com/news/home/20250107462885/en/Cedar-Gate-Technologies-Named-Innovation-Leader-in-the-U.S.-Population-Health-Management-Market"
        ],
        "verify": true
       },
       "timeToValue": {
        "score": 2,
        "rationale": "No implementation evidence found.",
        "evidence": [
         "https://www.businesswire.com/news/home/20250107462885/en/Cedar-Gate-Technologies-Named-Innovation-Leader-in-the-U.S.-Population-Health-Management-Market"
        ],
        "verify": true
       },
       "viability": {
        "score": 2,
        "rationale": "Ownership not stated in sources opened.",
        "evidence": [
         "https://www.businesswire.com/news/home/20250107462885/en/Cedar-Gate-Technologies-Named-Innovation-Leader-in-the-U.S.-Population-Health-Management-Market"
        ],
        "verify": true
       },
       "measurement": {
        "score": 2,
        "rationale": "No evidence on measurement methods or data export.",
        "evidence": [
         "https://www.businesswire.com/news/home/20250107462885/en/Cedar-Gate-Technologies-Named-Innovation-Leader-in-the-U.S.-Population-Health-Management-Market"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No pricing model or commercial terms found in sources opened.",
        "evidence": [
         "https://www.businesswire.com/news/home/20250107462885/en/Cedar-Gate-Technologies-Named-Innovation-Leader-in-the-U.S.-Population-Health-Management-Market"
        ],
        "verify": true
       }
      }
     },
     {
      "vendor": "Elligint Health (HealthEC + VirtualHealth)",
      "status": "evaluated",
      "rank": 10,
      "weightedScore": 45.0,
      "scores": {
       "functional": {
        "score": 3,
        "rationale": "FHIR-native data aggregation, risk stratification of high-risk populations, care management workflows and value-based performance tools.",
        "evidence": [
         "https://www.businesswire.com/news/home/20250109141307/en/HealthEC-and-VirtualHealth-Unite-to-Launch-Elligint-Health-Redefining-the-Future-of-Healthcare-with-Intelligent-Solutions"
        ],
        "verify": false
       },
       "integration": {
        "score": 2,
        "rationale": "FHIR-native aggregation is stated, but no Epic deployment evidence was found.",
        "evidence": [
         "https://www.businesswire.com/news/home/20250109141307/en/HealthEC-and-VirtualHealth-Unite-to-Launch-Elligint-Health-Redefining-the-Future-of-Healthcare-with-Intelligent-Solutions"
        ],
        "verify": true
       },
       "proof": {
        "score": 2,
        "rationale": "Listed in KLAS 2025 PHM (71.1) with no named health-system reference.",
        "evidence": [
         "https://klasresearch.com/best-in-klas-ranking/population-health-management/2025/256"
        ],
        "verify": true
       },
       "timeToValue": {
        "score": 2,
        "rationale": "No implementation evidence found.",
        "evidence": [
         "https://www.businesswire.com/news/home/20250109141307/en/HealthEC-and-VirtualHealth-Unite-to-Launch-Elligint-Health-Redefining-the-Future-of-Healthcare-with-Intelligent-Solutions"
        ],
        "verify": true
       },
       "viability": {
        "score": 2,
        "rationale": "Formed in 2024 from a merger with undisclosed backers.",
        "evidence": [
         "https://www.businesswire.com/news/home/20250109141307/en/HealthEC-and-VirtualHealth-Unite-to-Launch-Elligint-Health-Redefining-the-Future-of-Healthcare-with-Intelligent-Solutions"
        ],
        "verify": true
       },
       "measurement": {
        "score": 2,
        "rationale": "No evidence on measurement methods or data export.",
        "evidence": [
         "https://www.businesswire.com/news/home/20250109141307/en/HealthEC-and-VirtualHealth-Unite-to-Launch-Elligint-Health-Redefining-the-Future-of-Healthcare-with-Intelligent-Solutions"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No pricing model or commercial terms found in sources opened.",
        "evidence": [
         "https://www.businesswire.com/news/home/20250109141307/en/HealthEC-and-VirtualHealth-Unite-to-Launch-Elligint-Health-Redefining-the-Future-of-Healthcare-with-Intelligent-Solutions"
        ],
        "verify": true
       }
      }
     },
     {
      "vendor": "Persivia",
      "status": "evaluated",
      "rank": 11,
      "weightedScore": 45.0,
      "scores": {
       "functional": {
        "score": 3,
        "rationale": "CareSpace platform with multi-source aggregation, contract-aware workflows, patient prioritization and quality management.",
        "evidence": [
         "https://wp.persivia.com/blog/apg-2025/"
        ],
        "verify": false
       },
       "integration": {
        "score": 2,
        "rationale": "No Epic integration evidence found.",
        "evidence": [
         "https://wp.persivia.com/blog/apg-2025/"
        ],
        "verify": true
       },
       "proof": {
        "score": 2,
        "rationale": "No named customers on the page opened.",
        "evidence": [
         "https://wp.persivia.com/blog/apg-2025/"
        ],
        "verify": true
       },
       "timeToValue": {
        "score": 2,
        "rationale": "No implementation evidence found.",
        "evidence": [
         "https://wp.persivia.com/blog/apg-2025/"
        ],
        "verify": true
       },
       "viability": {
        "score": 2,
        "rationale": "Ownership not stated in sources opened.",
        "evidence": [
         "https://wp.persivia.com/blog/apg-2025/"
        ],
        "verify": true
       },
       "measurement": {
        "score": 2,
        "rationale": "No evidence on measurement methods or data export.",
        "evidence": [
         "https://wp.persivia.com/blog/apg-2025/"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No pricing model or commercial terms found in sources opened.",
        "evidence": [
         "https://wp.persivia.com/blog/apg-2025/"
        ],
        "verify": true
       }
      }
     },
     {
      "vendor": "Vim",
      "status": "evaluated",
      "rank": 12,
      "weightedScore": 40.0,
      "scores": {
       "functional": {
        "score": 2,
        "rationale": "Delivers payer quality-gap data into the EHR with read-write capability, but has no measure engine or risk stratification of its own.",
        "evidence": [
         "https://getvim.com/solution/quality-gaps/",
         "https://getvim.com/news-and-events/availity-and-vim-partner-to-streamline-care-gap-notification-and-closure-process/"
        ],
        "verify": false
       },
       "integration": {
        "score": 2,
        "rationale": "Supported EHRs are not named and Epic support was not verified.",
        "evidence": [
         "https://getvim.com/solution/quality-gaps/"
        ],
        "verify": true
       },
       "proof": {
        "score": 2,
        "rationale": "Evidence is a payer-side partnership with Availity; no provider health-system reference.",
        "evidence": [
         "https://getvim.com/news-and-events/availity-and-vim-partner-to-streamline-care-gap-notification-and-closure-process/"
        ],
        "verify": true
       },
       "timeToValue": {
        "score": 2,
        "rationale": "No implementation evidence found.",
        "evidence": [
         "https://getvim.com/solution/quality-gaps/"
        ],
        "verify": true
       },
       "viability": {
        "score": 2,
        "rationale": "Ownership not stated in sources opened.",
        "evidence": [
         "https://getvim.com/solution/quality-gaps/"
        ],
        "verify": true
       },
       "measurement": {
        "score": 2,
        "rationale": "No evidence on measurement or data export.",
        "evidence": [
         "https://getvim.com/solution/quality-gaps/"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No pricing model or commercial terms found in sources opened.",
        "evidence": [
         "https://getvim.com/solution/quality-gaps/"
        ],
        "verify": true
       }
      }
     }
    ]
   },
   {
    "id": "lot-b",
    "name": "Cross-payer dispensed-medication history feed for adherence measurement",
    "gap": "Part D Stars adherence measures (PDC-DR, PDC-RASA, PDC-STA) and statin-use measures need fill history across all payers, which neither Epic's population health module nor ACME's two-payer claims feed provides.",
    "why": "Prescribing-linked HEDIS and Stars movement within two quarters cannot be measured or targeted without timely fill data; a network feed removes dependence on the weakest data asset and the two engineers tied up on the payer feed rebuild through Q2.",
    "evaluated": [
     {
      "vendor": "Surescripts (Medication History for Populations)",
      "status": "shortlist",
      "rank": 1,
      "weightedScore": 80.0,
      "scores": {
       "functional": {
        "score": 4,
        "rationale": "Daily dispensed-medication history from pharmacy fills and PBM claims, including cash fills, with nonadherence, refill and abandoned-prescription notifications by cohort, which is the data PDC measures need; PDC calculation itself is not described.",
        "evidence": [
         "https://surescripts.com/what-we-do/medication-history-for-populations",
         "https://www.businesswire.com/news/home/20250219497477/en/Providing-Care-Managers-with-Patient-Medication-History-Data-Could-Help-Increase-Adherence-and-Save-U.S.-Healthcare-3.76-Billion"
        ],
        "verify": false
       },
       "integration": {
        "score": 5,
        "rationale": "Baptist Health Louisville uses Medication History for Populations integrated in Epic, with about 95% of patients on its population lists matched.",
        "evidence": [
         "https://surescripts.com/node/1320"
        ],
        "verify": false
       },
       "proof": {
        "score": 4,
        "rationale": "Named health-system users (Baptist Health, Mount Sinai Health System, Parkland) with vendor-published results; no independent study found.",
        "evidence": [
         "https://surescripts.com/node/1320",
         "https://surescripts.com/what-we-do/medication-history-for-populations",
         "https://www.businesswire.com/news/home/20250219497477/en/Providing-Care-Managers-with-Patient-Medication-History-Data-Could-Help-Increase-Adherence-and-Save-U.S.-Healthcare-3.76-Billion"
        ],
        "verify": false
       },
       "timeToValue": {
        "score": 4,
        "rationale": "Panel files return in 24-36 hours and refresh daily, bypassing ACME's two-payer, 60-90 day lag claims feed; ACME still has to build ingestion into Epic or Caboodle.",
        "evidence": [
         "https://surescripts.com/what-we-do/medication-history-for-populations",
         "https://surescripts.com/node/1320"
        ],
        "verify": false
       },
       "viability": {
        "score": 4,
        "rationale": "Operates a national network with data from virtually every US pharmacy and PBM per the vendor; ownership not described in sources opened.",
        "evidence": [
         "https://surescripts.com/what-we-do/medication-history-for-populations"
        ],
        "verify": true
       },
       "measurement": {
        "score": 3,
        "rationale": "Fill confirmation within days supports adherence baselines, and Baptist Health uses it to show payers its outreach; ACME's data-use and retention rights are not stated.",
        "evidence": [
         "https://surescripts.com/node/1320"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "Pricing not listed; vendor directs buyers to sales.",
        "evidence": [
         "https://surescripts.com/what-we-do/medication-history-for-populations"
        ],
        "verify": true
       }
      },
      "whyShortlisted": "Surescripts fills ACME's largest data gap, cross-payer dispensed-medication history for adherence measures, without depending on the weak payer claims feed. Baptist Health shows the feed running inside Epic for pharmacist-led adherence outreach.",
      "keyRisk": "It is a data feed only, so ACME must build PDC logic, cohort rules and Epic presentation itself or through a Lot A vendor, and data-use terms may limit secondary analytics.",
      "verifyInDemo": [
       "Match rate and turnaround on a test panel drawn from ACME's diabetes, heart failure and COPD cohorts",
       "How fills appear in Epic at an existing customer (Healthy Planet registry metric, worklist column or In Basket notification)",
       "Whether daily files carry the fields PDC needs (fill date, days supply, NDC) for statin, RAS antagonist and diabetes medication classes",
       "Volume of nonadherence and abandoned-prescription notifications per 1,000 patients, given the alert-fatigue risk"
      ],
      "rfpQuestions": [
       "What data-use rights does ACME have for population analytics, model training and sharing results with payers?",
       "What is the pricing basis (per patient per month, per panel record or per notification) and minimum term?",
       "Which Epic customers receive Medication History for Populations into Healthy Planet or Caboodle, and through which interface?",
       "Which pharmacy, PBM, mail-order or cash channels are not represented in the data?"
      ]
     },
     {
      "vendor": "DrFirst",
      "status": "shortlist",
      "rank": 2,
      "weightedScore": 66.0,
      "scores": {
       "functional": {
        "score": 3,
        "rationale": "MedHx supplies medication history at the point of care and at population level and was used at UMMS to compute first-fill rates, but PDC methodology and a scheduled population feed are not described.",
        "evidence": [
         "https://go.drfirst.com/hubfs/MedHx/S-MedHx-SmartSuite-Epic-Integration.pdf",
         "https://www.casestudies.com/company/drfirst/case-study/university-of-maryland-medical-system-boosts-prescription-first-fill-rates-20-to-25-with-drfirst"
        ],
        "verify": false
       },
       "integration": {
        "score": 4,
        "rationale": "Vendor states native Epic integration needing about ten hours of IT effort, with Temple Health and UMMS as Epic users; a Showroom listing was not confirmed.",
        "evidence": [
         "https://go.drfirst.com/hubfs/MedHx/S-MedHx-SmartSuite-Epic-Integration.pdf",
         "https://templehealth.org/about/news/in-the-media/beckers-health-it-highlights-temple-university-health-system-integrating-ai-powered-tools-from-drfirst-into-epic-electronic-health-record-system",
         "https://www.casestudies.com/company/drfirst/case-study/university-of-maryland-medical-system-boosts-prescription-first-fill-rates-20-to-25-with-drfirst"
        ],
        "verify": true
       },
       "proof": {
        "score": 3,
        "rationale": "UMMS reports first-fill rates up 20-25% and adherence up 8-14% in an undated vendor case study, and Temple adoption is a media mention.",
        "evidence": [
         "https://www.casestudies.com/company/drfirst/case-study/university-of-maryland-medical-system-boosts-prescription-first-fill-rates-20-to-25-with-drfirst",
         "https://templehealth.org/about/news/in-the-media/beckers-health-it-highlights-temple-university-health-system-integrating-ai-powered-tools-from-drfirst-into-epic-electronic-health-record-system"
        ],
        "verify": false
       },
       "timeToValue": {
        "score": 4,
        "rationale": "Vendor cites about ten hours of IT resources and no workflow change for Epic users.",
        "evidence": [
         "https://go.drfirst.com/hubfs/MedHx/S-MedHx-SmartSuite-Epic-Integration.pdf"
        ],
        "verify": true
       },
       "viability": {
        "score": 3,
        "rationale": "Privately held; raised about $135M in the year to May 2021 including Sixth Street Growth; current ownership not verified.",
        "evidence": [
         "https://htworld.co.uk/news/healthtech-pioneer-secures-further-investment/"
        ],
        "verify": true
       },
       "measurement": {
        "score": 3,
        "rationale": "UMMS merged DrFirst data with Epic records to compute first-fill rates, showing a baseline is feasible; data rights not stated.",
        "evidence": [
         "https://www.casestudies.com/company/drfirst/case-study/university-of-maryland-medical-system-boosts-prescription-first-fill-rates-20-to-25-with-drfirst"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No pricing model or commercial terms found in sources opened.",
        "evidence": [
         "https://go.drfirst.com/hubfs/MedHx/S-MedHx-SmartSuite-Epic-Integration.pdf"
        ],
        "verify": true
       }
      },
      "whyShortlisted": "DrFirst offers a second source of dispensed-medication history with Epic users (Temple Health, UMMS) and a low stated IT effort. UMMS shows its data merged with Epic records to measure first-fill and adherence change, which is the measurement ACME needs.",
      "keyRisk": "Its documented use is encounter-level medication history and first-fill measurement; a daily population feed for PDC across ACME's full panels is not shown in sources opened.",
      "verifyInDemo": [
       "Population-level adherence report for a test cohort, including how PDC is calculated",
       "Where MedHx data lands in Epic for prescribers and whether it can feed Healthy Planet registries",
       "The UMMS method for linking DrFirst data to Epic records and the resulting first-fill baseline",
       "The upstream source of dispensed-medication data and how its coverage compares with Surescripts"
      ],
      "rfpQuestions": [
       "Can you deliver a scheduled population file or API for every patient in a registry, and at what refresh frequency?",
       "What is the underlying source of dispensed-medication data and its national pharmacy and PBM coverage?",
       "Is population use priced separately from point-of-care medication history, and on what basis?",
       "Who currently owns DrFirst, and is any change of control planned?"
      ]
     }
    ]
   },
   {
    "id": "lot-c",
    "name": "Pharmacist-led medication optimization and adherence intervention service (conditional)",
    "gap": "Epic lists population health pharmacy only as a roadmap item, and ACME has 2.1 unallocated FTE, so staff capacity to act on adherence and therapy-optimization gaps may be missing.",
    "why": "Medication interventions (adherence outreach, statin initiation, heart failure therapy titration) need clinician time; buy only if ACME's own ambulatory pharmacists cannot absorb the phase-1 cohort volume.",
    "evaluated": [
     {
      "vendor": "Linea",
      "status": "shortlist",
      "rank": 1,
      "weightedScore": 52.0,
      "scores": {
       "functional": {
        "score": 3,
        "rationale": "Pharmacist titration of guideline-directed therapy and nurse follow-up after heart failure admissions addresses medication optimization and readmissions, but covers one of the three cohorts.",
        "evidence": [
         "https://www.fiercehealthcare.com/ai-and-machine-learning/startup-linea-backed-redesign-health-and-davita-takes-ai-driven-approach"
        ],
        "verify": false
       },
       "integration": {
        "score": 2,
        "rationale": "States it integrates into existing clinical workflows without naming an EHR.",
        "evidence": [
         "https://www.fiercehealthcare.com/ai-and-machine-learning/startup-linea-backed-redesign-health-and-davita-takes-ai-driven-approach"
        ],
        "verify": true
       },
       "proof": {
        "score": 2,
        "rationale": "No named ACO or health-system customer; readmission reductions are company-reported.",
        "evidence": [
         "https://www.fiercehealthcare.com/ai-and-machine-learning/startup-linea-backed-redesign-health-and-davita-takes-ai-driven-approach"
        ],
        "verify": true
       },
       "timeToValue": {
        "score": 3,
        "rationale": "Supplies its own pharmacists and nurses and detects admissions itself, which spares ACME staff, though data-access setup is not described.",
        "evidence": [
         "https://www.fiercehealthcare.com/ai-and-machine-learning/startup-linea-backed-redesign-health-and-davita-takes-ai-driven-approach"
        ],
        "verify": true
       },
       "viability": {
        "score": 2,
        "rationale": "Launched January 2024 with Redesign Health and DaVita backing and undisclosed funding.",
        "evidence": [
         "https://www.fiercehealthcare.com/ai-and-machine-learning/startup-linea-backed-redesign-health-and-davita-takes-ai-driven-approach"
        ],
        "verify": false
       },
       "measurement": {
        "score": 3,
        "rationale": "Reports 90-day readmission rates, matching a use-case outcome; patient-level data sharing is not described.",
        "evidence": [
         "https://www.fiercehealthcare.com/ai-and-machine-learning/startup-linea-backed-redesign-health-and-davita-takes-ai-driven-approach"
        ],
        "verify": true
       },
       "commercial": {
        "score": 4,
        "rationale": "ACOs pay a value-based fee only for actively enrolled patients; no amount is published.",
        "evidence": [
         "https://www.fiercehealthcare.com/ai-and-machine-learning/startup-linea-backed-redesign-health-and-davita-takes-ai-driven-approach"
        ],
        "verify": false
       }
      },
      "whyShortlisted": "Linea is the only candidate in this lot supplying pharmacist capacity aimed at a stated use-case outcome (heart failure readmissions), with a fee charged only for enrolled patients. It would add intervention capacity without drawing on ACME's 2.1 unallocated FTE.",
      "keyRisk": "It is an early-stage company with no named customers and heart-failure-only scope, so its readmission claims and Epic connectivity are unproven.",
      "verifyInDemo": [
       "Named ACO or health-system references with their measured 90-day readmission baselines",
       "How Linea detects ACME admissions (state HIE ADT or Epic) and documents titration changes back into Epic",
       "Pharmacist protocol and physician sign-off process that would satisfy ACME's clinical governance",
       "Format and frequency of the patient-level outcome file ACME would receive"
      ],
      "rfpQuestions": [
       "What is the fee per enrolled patient, and is there a shared-savings or outcome guarantee option?",
       "What collaborative practice or protocol arrangement is required for pharmacist titration in ACME's state or states?",
       "How many live ACO or health-system customers do you have, and what is your funding runway?",
       "What data must ACME provide, and what is your integration method with Epic?"
      ]
     },
     {
      "vendor": "AssureCare (AssureRx, incl. Cureatr and SinfoníaRx technology)",
      "status": "shortlist",
      "rank": 2,
      "weightedScore": 47.0,
      "scores": {
       "functional": {
        "score": 3,
        "rationale": "AssureRx combines Meds 360 medication management, pharmacist MTM services and RxCompanion claims-based medication-problem flagging covering adherence and reconciliation.",
        "evidence": [
         "https://www.santelog.com/actualites-sante-nasdaq/assurecare-acquires-cureatr-and-sinfoniarx-technology"
        ],
        "verify": false
       },
       "integration": {
        "score": 2,
        "rationale": "No Epic integration documented for the medication products; an EHR prior-authorization integration announced in 2024 concerns a separate product.",
        "evidence": [
         "https://www.santelog.com/actualites-sante-nasdaq/assurecare-acquires-cureatr-and-sinfoniarx-technology",
         "https://www.globenewswire.com/de/news-release/2024/09/04/2940612/0/en/AssureCare-Launches-Seamless-real-time-Integration-to-Submit-Prior-Authorization-Requests-from-EHRs.html?print=1"
        ],
        "verify": true
       },
       "proof": {
        "score": 2,
        "rationale": "No named health-system customer for the medication portfolio in sources opened.",
        "evidence": [
         "https://www.santelog.com/actualites-sante-nasdaq/assurecare-acquires-cureatr-and-sinfoniarx-technology"
        ],
        "verify": true
       },
       "timeToValue": {
        "score": 2,
        "rationale": "RxCompanion flags problems from claims, which depends on ACME's weakest data asset unless another fill source is supplied.",
        "evidence": [
         "https://www.santelog.com/actualites-sante-nasdaq/assurecare-acquires-cureatr-and-sinfoniarx-technology"
        ],
        "verify": true
       },
       "viability": {
        "score": 3,
        "rationale": "Operating company that assembled this portfolio through 2023-2024 acquisitions of Cureatr and SinfoníaRx technology, which carries integration risk.",
        "evidence": [
         "https://www.santelog.com/actualites-sante-nasdaq/assurecare-acquires-cureatr-and-sinfoniarx-technology",
         "https://stocktitan.net/news/TRHC/medication-management-services-leader-cureatr-acquires-sinfonia-mmctab44ob0n.html"
        ],
        "verify": false
       },
       "measurement": {
        "score": 2,
        "rationale": "No evidence on outcome measurement or client data export.",
        "evidence": [
         "https://www.santelog.com/actualites-sante-nasdaq/assurecare-acquires-cureatr-and-sinfoniarx-technology"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No pricing model or commercial terms found in sources opened.",
        "evidence": [
         "https://www.santelog.com/actualites-sante-nasdaq/assurecare-acquires-cureatr-and-sinfoniarx-technology"
        ],
        "verify": true
       }
      },
      "whyShortlisted": "AssureCare pairs medication management software with pharmacist MTM services, covering adherence and reconciliation work ACME lacks staff for. It tied for second in a thin field after payer-only knockouts, so a demo is needed to test provider fit.",
      "keyRisk": "No provider reference or Epic integration for the medication portfolio was found, and its claims-based flagging depends on ACME's weakest data asset.",
      "verifyInDemo": [
       "A named provider health-system customer using AssureRx and its measured adherence results",
       "How Meds 360 or RxCompanion would run on fill data (for example a Lot B feed) instead of payer claims",
       "Where pharmacist recommendations land in Epic for the prescriber and how sign-off is documented",
       "Who staffs the MTM service, at what caseload, and how outreach is prioritized"
      ],
      "rfpQuestions": [
       "Name provider health-system customers for Meds 360 or SinfoníaRx-derived services since the June 2024 acquisition.",
       "Which Epic integration methods are supported for the medication management products?",
       "What is the pricing basis for software and for pharmacist services (per patient, per intervention, outcome-based)?",
       "Which Part D Stars and HEDIS medication measures do you track, and how are results shared with the client at patient level?"
      ]
     },
     {
      "vendor": "AnewHealth (ExactCare + Tabula Rasa HealthCare)",
      "status": "shortlist",
      "rank": 3,
      "weightedScore": 47.0,
      "scores": {
       "functional": {
        "score": 3,
        "rationale": "Clinical pharmacy care, MedWise regimen technology and dispensing for complex chronic populations, serving primary and specialty care providers and risk-bearing organizations.",
        "evidence": [
         "https://www.fintechfutures.com/press-releases/anewhealth-announces-strategic-investment-from-the-vistria-group-and-acquisition-of-homefree",
         "https://anewhealthrx.com/about-us/"
        ],
        "verify": false
       },
       "integration": {
        "score": 2,
        "rationale": "No EHR or Epic integration evidence found.",
        "evidence": [
         "https://anewhealthrx.com/about-us/"
        ],
        "verify": true
       },
       "proof": {
        "score": 2,
        "rationale": "Serves nearly 110,000 patients but no named health-system customer; the base leans to PACE and home health.",
        "evidence": [
         "https://www.fintechfutures.com/press-releases/anewhealth-announces-strategic-investment-from-the-vistria-group-and-acquisition-of-homefree"
        ],
        "verify": true
       },
       "timeToValue": {
        "score": 2,
        "rationale": "No implementation evidence found.",
        "evidence": [
         "https://anewhealthrx.com/about-us/"
        ],
        "verify": true
       },
       "viability": {
        "score": 3,
        "rationale": "Controlled by Nautic Partners since 2016 with a Vistria Group minority investment in July 2024, after the ExactCare and Tabula Rasa combination.",
        "evidence": [
         "https://www.fintechfutures.com/press-releases/anewhealth-announces-strategic-investment-from-the-vistria-group-and-acquisition-of-homefree",
         "https://www.exactcarepharmacy.com/about-exactcare/news/exactcare-and-tabula-rasa-healthcare-rebrand-combined-organization-as-anewhealth/"
        ],
        "verify": false
       },
       "measurement": {
        "score": 2,
        "rationale": "No evidence on outcome measurement or client data export.",
        "evidence": [
         "https://anewhealthrx.com/about-us/"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No pricing model or commercial terms found in sources opened.",
        "evidence": [
         "https://anewhealthrx.com/about-us/"
        ],
        "verify": true
       }
      },
      "whyShortlisted": "AnewHealth supplies clinical pharmacists and MedWise regimen review for complex chronic patients and already serves primary care providers and risk-bearing organizations. It tied with AssureCare on score, proof and integration, so both are kept for demos in a thin field.",
      "keyRisk": "Its customer base centers on PACE and home health, so fit for a Tier 1 IDN's ambulatory chronic panels and Epic workflow is unproven.",
      "verifyInDemo": [
       "A reference among primary care or health-system customers with adherence or utilization results",
       "MedWise output for a sample heart failure or diabetes patient and how it reaches the prescriber in Epic",
       "Whether pharmacist services can run without AnewHealth dispensing or PBM services",
       "Pharmacist caseload, response times and documentation back into the chart"
      ],
      "rfpQuestions": [
       "Name health-system or large medical-group customers outside PACE and home health.",
       "Can clinical pharmacy services be contracted separately from dispensing and PBM services, and at what price basis?",
       "What EHR integration do you support for Epic customers?",
       "How do you measure and report adherence, ED visit and readmission changes at patient level?"
      ]
     }
    ]
   }
  ],
  "screenedOut": [
   {
    "vendor": "Epic Healthy Planet (with Cosmos-based AI)",
    "reason": "Client-owned baseline; evaluated in epicBaseline rather than as a purchase."
   },
   {
    "vendor": "Cotiviti (Quality Intelligence, Star Intelligence, DxCG)",
    "reason": "Knockout: sold primarily to health plans with providers positioned as network collaborators; no provider health-system reference in sources."
   },
   {
    "vendor": "Inovalon (Converged Quality)",
    "reason": "Knockout: payer-cloud HEDIS product used by 100+ health plans; no provider health-system reference in sources."
   },
   {
    "vendor": "ZeOmega (Jiva)",
    "reason": "Knockout: payer care management platform (Best in KLAS Payer Care Management); payvider expansion stated but no provider health-system reference."
   },
   {
    "vendor": "Arine",
    "reason": "Knockout: every named customer is a health plan, PBM or state Medicaid agency (SCAN, VNS Health, Priority Health, Magellan, Oklahoma Health Care Authority) and no provider health-system reference was found; reinstate into Lot C if Arine produces one."
   },
   {
    "vendor": "RxAnte (RxEffect, Mosaic Pharmacy Service)",
    "reason": "Knockout: RxEffect reaches providers through health plans that extend it to affiliated practices; no provider health-system reference found."
   },
   {
    "vendor": "Cardinal Health Outcomes",
    "reason": "Knockout: network connecting payers and manufacturer-sponsored programs to community pharmacists; no provider health-system reference."
   },
   {
    "vendor": "Pharmacy Quality Solutions (EQuIPP)",
    "reason": "Knockout: plan-to-pharmacy quality platform with no provider health-system customers; relevant capability is considered under Innovaccer, its owner, in Lot A."
   },
   {
    "vendor": "OptimizeRx",
    "reason": "Manufacturer-funded point-of-care messaging; does not supply adherence data, measure calculation or intervention capacity, and current Epic scope is unverified (2019 announcement)."
   },
   {
    "vendor": "Oracle Health (Health Data Intelligence, Quality Management, Care Coordination Intelligence)",
    "reason": "EHR-agnostic platform from a competing EHR vendor that duplicates Healthy Planet scope; no Epic deployment evidence found."
   },
   {
    "vendor": "Azara Healthcare (DRVS)",
    "reason": "Focused on community health centers, rural and community hospitals; not built for a Tier 1 IDN despite the 2025 KLAS PHM award."
   },
   {
    "vendor": "Navina",
    "reason": "Primary care documentation and risk-adjustment assistant; does not address multi-contract measure reconciliation, adherence data or medication intervention."
   },
   {
    "vendor": "ClosedLoop",
    "reason": "Model-building platform; the risk-model need is covered by Epic native models and an in-house build on Caboodle (UCLA Health precedent), and no Epic or health-system evidence was found."
   },
   {
    "vendor": "Medial EarlySign",
    "reason": "Point risk models (for example diabetic kidney disease); KLAS notes long integration timelines and clinician effort, and the risk-model need is covered by Epic and an in-house build."
   },
   {
    "vendor": "Aledade",
    "reason": "Operating partner with shared-savings economics for ACO portfolios; a different buying decision than software or data for an Epic IDN running its own contracts."
   },
   {
    "vendor": "Lumeris (Tom)",
    "reason": "Value-based care operating partnership; a different buying decision. Revisit if ACME seeks a risk partner (Hackensack Meridian Health is an Epic reference)."
   },
   {
    "vendor": "Notable",
    "reason": "Patient outreach automation; Healthy Planet campaigns and MyChart cover multichannel gap outreach natively and phase 1 targets the prescribing workflow. Revisit after phase 1 if outreach labor is the constraint."
   },
   {
    "vendor": "Luma Health",
    "reason": "Patient outreach and scheduling add-on to Epic; outside the phase-1 prescribing-workflow scope for the same reason as Notable."
   },
   {
    "vendor": "Artera (formerly WELL Health)",
    "reason": "Patient communications platform; outreach is covered by Epic campaigns for phase 1 and no Epic-specific evidence was found."
   },
   {
    "vendor": "ActiumHealth",
    "reason": "Outreach and scheduling AI agents; outside phase-1 scope (Epic campaigns cover outreach). Candidate for a later outreach lot."
   },
   {
    "vendor": "Get Well",
    "reason": "Inpatient and post-discharge engagement; its care-gap use case is payer-side."
   },
   {
    "vendor": "Lirio",
    "reason": "Behavioral messaging platform with no named health-system customers in sources; outreach is outside phase-1 scope."
   },
   {
    "vendor": "Upfront Healthcare Services",
    "reason": "Patient engagement outreach now owned by Health Catalyst; outside phase-1 scope and would be assessed with Health Catalyst if outreach is added."
   },
   {
    "vendor": "Cured",
    "reason": "Healthcare CRM now part of Innovaccer; outreach is outside phase-1 scope."
   },
   {
    "vendor": "Salesforce Health Cloud",
    "reason": "CRM that tracks care gaps created by external systems; does not calculate measures or supply data, and duplicates Epic care coordination."
   },
   {
    "vendor": "Clarify Health",
    "reason": "Market and provider-performance benchmarking; not patient-level gap closure."
   },
   {
    "vendor": "Kythera Labs (Wayfinder)",
    "reason": "Data platform for strategy and data science teams; not patient-level gap closure or measure reconciliation."
   }
  ],
  "assumptions": [
   "ACME licenses Epic Healthy Planet with claims integration and Payer Platform available; the client context lists Epic inpatient and ambulatory but does not itemize population health modules.",
   "Lots are separate purchases; a Lot A vendor that resells dispensed-medication data could satisfy Lot B in the same contract.",
   "Prescribers act on recommendations inside Epic for the phase-1 cohorts; ACME's ambulatory pharmacist headcount is unknown, which is why Lot C is conditional.",
   "Patient outreach automation is excluded from phase 1 because Healthy Planet campaigns and MyChart cover multichannel outreach; outreach vendors were screened out, not scored.",
   "Scores rely on sources opened on 2026-10-21; vendor-reported outcomes are treated as vendor claims and scored below independent evidence.",
   "In Lot C, AssureCare and AnewHealth tie on weighted score, proof and integration; both are shortlisted and their rank order is arbitrary.",
   "Competitor and potential-acquirer flags in the landscape (for example Innovaccer, Health Catalyst, Arcadia, Lightbeam) did not affect scoring.",
   "Evaluation is evidence only and does not change any VRI score."
  ],
  "openQuestions": [
   "Does ACME already receive Surescripts or DrFirst medication history through Epic, and is a population-level license in place?",
   "How many value-based contracts and payers are in phase 1, and which of those payers send gap status through Epic Payer Platform?",
   "Which measure sets drive the ROI target (HEDIS, Medicare Stars Part D, MSSP/APP), and which payer definitions govern attribution?",
   "How many ambulatory clinical pharmacists does ACME employ, and under what collaborative practice protocols?",
   "Can Lot A vendors run on Caboodle data, or do they require a separate data platform?",
   "Do Arine or RxAnte have provider health-system references that would reverse their knockouts?",
   "What data-use rights would ACME hold over dispensed-medication data for model training and payer reporting?",
   "When will Epic's Cosmos-based risk prediction be generally available, and would it replace an in-house model?"
  ]
 },
 "opp-3": {
  "useCaseId": "opp-3",
  "asOf": "2026-10-21",
  "rubricVersion": "vendor-eval-v1",
  "recommendation": {
   "deliveryPath": "combine",
   "summary": "Configure what ACME already owns in Epic (referral work queues and routing, referral auto-linking, in-network order sets, EpicCare Link status for community referrers, CERM and 360X where partners support it) as the system of record, and buy only where Epic data cannot reach. Buy an external all-payer leakage dataset (Lot A) for the Nov 2026 baseline because ACME's two-payer claims feed is too weak and its engineers are committed to the rebuild through Q2, and buy Epic-integrated referral intake and conversion (Lot B) for the first three specialties from Dec 2026. Defer the provider data and scheduling purchase (Lot C) until the directory remediation shows whether Epic plus a data clean-up is enough for the Mar to May 2027 scale-out, and build the join of Epic referral orders with the purchased claims destinations in Caboodle so loop status and leakage are reported together."
  },
  "epicBaseline": {
   "covers": [
    "Ambulatory referral orders create referral records that route by order name and requested location to specialty referral scheduling work queues used by Cadence schedulers (MGH Epic guide).",
    "Referral auto-linking of scheduled appointments to referrals, standardized referral work queues, referral triage and Fast Pass are configurable Epic access features (BDO Epic patient access case).",
    "EpicCare Link lets community referrers place referral orders, search referral and scheduling status, and attach a Consultant Report note to close the loop (MercyOne EpicCare Link guide).",
    "Care Everywhere Referrals Management (CERM) tracks Epic-to-Epic referrals when CERM is enabled at the sending organization (UC Davis Health).",
    "360X closed-loop referral exchange with non-Epic EHRs that meet the specification, and Direct (XDR) messages that flag specialist reports for referred patients (open.epic).",
    "Community Connect extends ACME's Epic instance to affiliates so their referrals, scheduling and in-basket messages stay in one environment, and order sets can surface in-network specialists (third-party consultancy description, not Epic)."
   ],
   "gaps": [
    "Out-of-network destinations: Epic records what ACME orders and sees, not where patients went outside ACME; leakage baselining needs all-payer claims, and ACME's own feed covers two commercial payers monthly with a 60-90 day lag, manual member matching and quality 2/5 (client D3).",
    "Inbound referrals from non-Epic practices that do not use EpicCare Link still arrive by fax and need manual entry into Epic orders and work queues (landscape build-vs-buy note).",
    "Loop closure with non-Epic receivers depends on 360X or Direct participation by the receiver; open.epic lists only eClinicalWorks and Netsmart as current 360X integrations.",
    "Provider directory accuracy and matching: Epic routing is rule-based on order name and location, while sub-specialty, location and network participation data are inconsistent across ACME systems (use-case risk), and matching by plan, language and next availability needs curated provider data.",
    "Coordinator capacity: unscheduled referral follow-up and status chasing are manual today, and ACME has 2.1 unallocated FTE and 3 open requisitions to absorb new work (client D1)."
   ],
   "sources": [
    {
     "title": "open.epic: Exchanging clinical findings (EHR-to-EHR, 360X)",
     "url": "https://open.epic.com/clinical/ehrtoehr",
     "retrieved": "2026-10-21"
    },
    {
     "title": "UC Davis Health: How to use CERM",
     "url": "https://health.ucdavis.edu/referrals/ways-to-refer/how-to-use-CERM",
     "retrieved": "2026-10-21"
    },
    {
     "title": "MercyOne TogetherCare: EpicCare Link - Working with Referrals",
     "url": "https://www.mercyone.org/sites/default/files/2025-05/MercyOne%20TogetherCare_EpicCare%20Link%20-%20Working%20with%20Referrals.pdf",
     "retrieved": "2026-10-21"
    },
    {
     "title": "MGH: Getting Started with Referral Scheduling WQs",
     "url": "https://eristest2-6.partners.org/wp-content/uploads/1.-Getting-Started-with-Referral-Scheduling-WQs.pdf",
     "retrieved": "2026-10-21"
    },
    {
     "title": "BDO: Streamlining patient access with Epic",
     "url": "https://www.bdo.com/insights/blogs/bdo-knows-healthcare/streamlining-patient-access-with-epic-a-success-story",
     "retrieved": "2026-10-21"
    },
    {
     "title": "CSI Companies: Epic Community Connect and referral leakage",
     "url": "https://www.csicompanies.com/insights/epic-community-connectnbsp-how-health-systems-use-it-to-eliminate-referral-leakage",
     "retrieved": "2026-10-21"
    }
   ]
  },
  "lots": [
   {
    "id": "lot-a",
    "name": "Referral leakage baseline and network integrity analytics",
    "gap": "Epic cannot show where referred patients receive care outside ACME, and ACME's payer claims feed is too thin and late to baseline leakage by Nov 2026.",
    "why": "Delivers the Leakage Baseline milestone and the measurement for the 'retained specialty volume within 2 quarters' outcome using vendor-supplied all-payer claims, without drawing on the engineers committed to the payer feed rebuild.",
    "evaluated": [
     {
      "vendor": "Clarify Health (including Loyal)",
      "status": "shortlist",
      "rank": 1,
      "weightedScore": 67.0,
      "scores": {
       "functional": {
        "score": 5,
        "rationale": "Quantifies referral leakage and high-value referral corridors from payer-complete patient journeys, and the Loyal acquisition adds provider data, care search and scheduling to act on what the analysis finds.",
        "evidence": [
         "https://www.healthcareittoday.com/2026/06/19/clarify-health-acquires-loyal-health-to-create-healthcares-first-closed-loop-network-intelligence-and-patient-activation-platform/",
         "https://www.aha.org/system/files/media/file/2024/11/Clarify-atlas-case-study-health-system-enhances-care-continuity.pdf"
        ],
        "verify": false
       },
       "integration": {
        "score": 2,
        "rationale": "Loyal is described as having native EHR integrations, but Epic and Caboodle connectivity are not named in any source opened.",
        "evidence": [
         "https://www.healthcareittoday.com/2026/06/19/clarify-health-acquires-loyal-health-to-create-healthcares-first-closed-loop-network-intelligence-and-patient-activation-platform/"
        ],
        "verify": true
       },
       "proof": {
        "score": 3,
        "rationale": "One anonymous East Coast not-for-profit system reported 37,000 more referrals kept in-network year over year (vendor-reported, hosted under an investor portfolio page); the cited Best in KLAS 2024 award is for data analytics platform, not referral leakage.",
        "evidence": [
         "https://www.aha.org/system/files/media/file/2024/11/Clarify-atlas-case-study-health-system-enhances-care-continuity.pdf",
         "https://www.aha.org/concord/case-studies/clarify-health"
        ],
        "verify": false
       },
       "timeToValue": {
        "score": 3,
        "rationale": "Brings its own 300M-life dataset so the leakage baseline does not wait on ACME's weak payer claims feed, but no implementation timeline is published.",
        "evidence": [
         "https://www.aha.org/concord/case-studies/clarify-health"
        ],
        "verify": true
       },
       "viability": {
        "score": 3,
        "rationale": "Customer base is broad (Loyal reported close to 500 hospitals), but the June 2026 Loyal acquisition adds product integration risk during ACME's rollout window.",
        "evidence": [
         "https://www.healthcareittoday.com/2026/06/19/clarify-health-acquires-loyal-health-to-create-healthcares-first-closed-loop-network-intelligence-and-patient-activation-platform/"
        ],
        "verify": false
       },
       "measurement": {
        "score": 4,
        "rationale": "Case study reports year-over-year in-network referral counts, which shows baseline-and-change measurement; client data export rights are not documented.",
        "evidence": [
         "https://www.aha.org/system/files/media/file/2024/11/Clarify-atlas-case-study-health-system-enhances-care-continuity.pdf"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No pricing model found in sources opened.",
        "evidence": [
         "https://www.healthcareittoday.com/2026/06/19/clarify-health-acquires-loyal-health-to-create-healthcares-first-closed-loop-network-intelligence-and-patient-activation-platform/"
        ],
        "verify": true
       }
      },
      "whyShortlisted": "Highest functional fit in the lot: it covers the leakage baseline and, through Loyal, the provider data and scheduling layer that acts on it. It brings external all-payer data, which avoids dependence on ACME's two-payer claims feed.",
      "keyRisk": "The Loyal integration closed in June 2026, so the combined analytics-plus-activation product and its Epic connectivity may still be in development during ACME's Nov 2026 to May 2027 milestones.",
      "verifyInDemo": [
       "Live leakage view for one ACME service line showing out-of-network destinations by ordering provider, with the data source (claims vs EHR) and lag shown for each figure.",
       "How Epic referral orders from Caboodle are joined to the claims-based journey so order-level loop status and claims-level destinations appear in one view.",
       "Which Loyal modules (provider data, care search, scheduling) are generally available under Clarify today, with an Epic customer live on each.",
       "Export of patient-level and provider-level leakage results to ACME's warehouse, and what ACME retains at contract end."
      ],
      "rfpQuestions": [
       "Name two US Epic health systems of comparable size using Clarify referral analytics in production, and describe how the 37,000 in-network referral figure in the AHA-hosted case study was measured.",
       "What is the product roadmap and support model for Loyal customers following the June 2026 acquisition, and which Loyal components connect to Epic today and by what mechanism?",
       "What share of ACME's commercial, Medicare Advantage and Medicaid patients does your dataset cover in ACME's markets, and what is the claims lag?",
       "Can analytics and Loyal modules be licensed separately, and is any fee tied to measured in-network retention?"
      ]
     },
     {
      "vendor": "Sg2 Provider Connections",
      "status": "shortlist",
      "rank": 2,
      "weightedScore": 64.0,
      "scores": {
       "functional": {
        "score": 4,
        "rationale": "Uses all-payer claims for each market to find physician relationship opportunities, run referral-pattern initiatives and quantify the financial impact of leakage.",
        "evidence": [
         "https://www.sg2.com/press-releases/sg2-launches-tool-physician-engagement-network-optimization",
         "https://www.sg2.com/what-we-do"
        ],
        "verify": false
       },
       "integration": {
        "score": 2,
        "rationale": "No Epic or Caboodle integration evidence found.",
        "evidence": [
         "https://www.sg2.com/press-releases/sg2-launches-tool-physician-engagement-network-optimization"
        ],
        "verify": true
       },
       "proof": {
        "score": 2,
        "rationale": "No named health system references for Provider Connections in sources opened.",
        "evidence": [
         "https://www.sg2.com/press-releases/sg2-launches-tool-physician-engagement-network-optimization"
        ],
        "verify": true
       },
       "timeToValue": {
        "score": 4,
        "rationale": "Market-level all-payer claims are supplied by the vendor, so the baseline does not depend on ACME's payer feed.",
        "evidence": [
         "https://www.sg2.com/press-releases/sg2-launches-tool-physician-engagement-network-optimization"
        ],
        "verify": false
       },
       "viability": {
        "score": 4,
        "rationale": "Part of Vizient, and Sg2 continues to list Network Integrity Management and Provider Connections on its current site.",
        "evidence": [
         "https://www.sg2.com/what-we-do"
        ],
        "verify": false
       },
       "measurement": {
        "score": 4,
        "rationale": "Quantifies the financial impact of network leakage, which supports a dollar baseline; export rights not described.",
        "evidence": [
         "https://www.sg2.com/press-releases/sg2-launches-tool-physician-engagement-network-optimization"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No pricing model found in sources opened.",
        "evidence": [
         "https://www.sg2.com/what-we-do"
        ],
        "verify": true
       }
      },
      "whyShortlisted": "Market-level all-payer claims with leakage valued in dollars matches the Nov 2026 baseline milestone and the ROI tracking in this use case. Vizient ownership gives the strongest viability score in the lot.",
      "keyRisk": "No named health system using Provider Connections for leakage was found, and Epic connectivity is undocumented, so references and data joins must be proven before purchase.",
      "verifyInDemo": [
       "Leakage valued in dollars for one ACME service line, with the claims period, lag and method used to price leaked volume.",
       "Physician-level referral pattern views that ACME's network relations team would use, and how they map to ACME's provider IDs in Epic.",
       "Data export of provider-level leakage results to Caboodle and how often it refreshes."
      ],
      "rfpQuestions": [
       "Name US Epic health systems using Provider Connections or Network Integrity Management for referral leakage, and offer a reference call.",
       "If ACME is an existing Vizient or Sg2 member, which of these capabilities are already licensed and what is incremental?",
       "What is the source, coverage and lag of the all-payer claims data in ACME's markets?",
       "How is the financial impact of leakage calculated, and can ACME adjust the method to its own cost data?"
      ]
     },
     {
      "vendor": "Definitive Healthcare (Network Intelligence)",
      "status": "shortlist",
      "rank": 3,
      "weightedScore": 61.0,
      "scores": {
       "functional": {
        "score": 4,
        "rationale": "Claims- and affiliation-based views of referral patterns, shared patient pathways and patient out-migration, positioned for physician relationship work.",
        "evidence": [
         "https://platform.softwareone.com/product/network-intelligence/PCP-5155-7538",
         "https://www.definitivehc.com/resources/case-study/influence-patient-referrals-leakage-market-expansion"
        ],
        "verify": false
       },
       "integration": {
        "score": 2,
        "rationale": "No Epic or Caboodle integration evidence found.",
        "evidence": [
         "https://platform.softwareone.com/product/network-intelligence/PCP-5155-7538"
        ],
        "verify": true
       },
       "proof": {
        "score": 3,
        "rationale": "Vendor case study with an unnamed pediatric health system that used Atlas All-Payor Claims to find 650 hospitals patients visited within 60 days.",
        "evidence": [
         "https://www.definitivehc.com/resources/case-study/influence-patient-referrals-leakage-market-expansion"
        ],
        "verify": false
       },
       "timeToValue": {
        "score": 4,
        "rationale": "Subscription data product that does not depend on ACME's internal claims feed.",
        "evidence": [
         "https://platform.softwareone.com/product/network-intelligence/PCP-5155-7538"
        ],
        "verify": false
       },
       "viability": {
        "score": 2,
        "rationale": "Ownership and financial condition not reviewed in sources opened.",
        "evidence": [
         "https://platform.softwareone.com/product/network-intelligence/PCP-5155-7538"
        ],
        "verify": true
       },
       "measurement": {
        "score": 3,
        "rationale": "Shows post-care destinations for a cohort; baseline tracking and client data rights not described.",
        "evidence": [
         "https://www.definitivehc.com/resources/case-study/influence-patient-referrals-leakage-market-expansion"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No pricing model found in sources opened.",
        "evidence": [
         "https://platform.softwareone.com/product/network-intelligence/PCP-5155-7538"
        ],
        "verify": true
       }
      },
      "whyShortlisted": "All-payer claims data with referral pathways and out-migration views supports the leakage baseline without ACME's payer feed. It ties Trilliant at 61.0 and ranks ahead on proof because it has a documented health system leakage case.",
      "keyRisk": "The only leakage case is an unnamed pediatric system and Epic connectivity is undocumented, so fit for an adult Tier 1 IDN and the join to Epic referral orders are unproven.",
      "verifyInDemo": [
       "Out-of-network destinations for ACME's 3 first-wave specialties by referring provider, with the claims source and lag shown.",
       "How affiliation data distinguishes employed, affiliated and independent specialists in ACME's markets.",
       "Export of provider-level referral and leakage data to Caboodle and refresh frequency."
      ],
      "rfpQuestions": [
       "Name adult US Epic health systems using Network Intelligence or Atlas All-Payor Claims for referral leakage, and offer a reference call.",
       "What is the claims coverage by payer type in ACME's markets and the typical lag?",
       "Which modules are needed for referral leakage work, how are they priced, and what data may ACME keep after termination?"
      ]
     },
     {
      "vendor": "Trilliant Health",
      "status": "shortlist",
      "rank": 4,
      "weightedScore": 61.0,
      "scores": {
       "functional": {
        "score": 4,
        "rationale": "All-payer claims dataset tracks referral destinations down to the individual provider, in-system and at competing facilities, but it is an analysis tool with no workflow to act on referrals.",
        "evidence": [
         "https://www.trillianthealth.com/analytics/network-integrity",
         "https://hitconsultant.net/2026/09/24/trilliant-health-launches-oria-ai-strategist-healthcare-claims-analytics/"
        ],
        "verify": false
       },
       "integration": {
        "score": 2,
        "rationale": "No Epic or Caboodle integration evidence found; joining claims destinations to Epic referral orders would be ACME work.",
        "evidence": [
         "https://www.trillianthealth.com/analytics/network-integrity"
        ],
        "verify": true
       },
       "proof": {
        "score": 2,
        "rationale": "No named health system customers on the pages opened; premium Oria with a leakage use case launched in Sept 2026.",
        "evidence": [
         "https://hitconsultant.net/2026/09/24/trilliant-health-launches-oria-ai-strategist-healthcare-claims-analytics/"
        ],
        "verify": true
       },
       "timeToValue": {
        "score": 4,
        "rationale": "Claims-based directory with monthly refreshes and a free Oria Lite tier lets ACME see its network before contracting, with no internal data feed needed.",
        "evidence": [
         "https://www.trillianthealth.com/analytics/network-integrity"
        ],
        "verify": false
       },
       "viability": {
        "score": 2,
        "rationale": "Ownership and financial backing not verified in sources opened.",
        "evidence": [
         "https://hitconsultant.net/2026/09/24/trilliant-health-launches-oria-ai-strategist-healthcare-claims-analytics/"
        ],
        "verify": true
       },
       "measurement": {
        "score": 4,
        "rationale": "Benchmarks how much downstream care employed providers keep in-system and what the gap costs; data downloads are listed for the premium tier.",
        "evidence": [
         "https://www.trillianthealth.com/analytics/network-integrity"
        ],
        "verify": false
       },
       "commercial": {
        "score": 3,
        "rationale": "Free tier is public; premium pricing is on request.",
        "evidence": [
         "https://www.trillianthealth.com/analytics/network-integrity"
        ],
        "verify": false
       }
      },
      "whyShortlisted": "Provider-level, all-payer referral destination data with monthly refresh fits the Nov 2026 leakage baseline milestone without waiting for ACME's payer feed rebuild. A free tier allows ACME to check market coverage before a demo.",
      "keyRisk": "No named health system references were found for the leakage use case, and the product does not connect to Epic workflows, so ACME would carry the join to referral orders.",
      "verifyInDemo": [
       "Referral destinations for 3 ACME high-volume specialties at individual-provider level, showing in-system vs competitor share and the claims period covered.",
       "How a destination is attributed to the referring ACME provider when the claim does not carry a referring NPI.",
       "Export format and frequency for provider-level leakage data into Caboodle."
      ],
      "rfpQuestions": [
       "Name US health systems using Trilliant network integrity or premium Oria for referral leakage, and offer a reference call.",
       "What is the claims lag behind the monthly refresh, and how complete is commercial coverage in ACME's markets?",
       "What are premium Oria list prices or price bands, and what data can ACME retain after termination?"
      ]
     },
     {
      "vendor": "Strata Decision Technology (Axiom Referral Patterns, ex-Syntellis)",
      "status": "nearMiss",
      "rank": 5,
      "weightedScore": 59.0,
      "scores": {
       "functional": {
        "score": 4,
        "rationale": "Axiom Referral Patterns maps physician referral patterns and leakage using an all-payer claims dataset.",
        "evidence": [
         "https://www.businesswire.com/news/home/20230628536825/en/Syntellis-Performance-Solutions-Launches-Axiom-Referral-Patterns-A-New-Data-Insights-Solution-to-Drive-Strategic-Growth-for-Healthcare-Organizations"
        ],
        "verify": false
       },
       "integration": {
        "score": 2,
        "rationale": "No Epic integration evidence for the referral module.",
        "evidence": [
         "https://www.businesswire.com/news/home/20230628536825/en/Syntellis-Performance-Solutions-Launches-Axiom-Referral-Patterns-A-New-Data-Insights-Solution-to-Drive-Strategic-Growth-for-Healthcare-Organizations"
        ],
        "verify": true
       },
       "proof": {
        "score": 2,
        "rationale": "No referral-specific health system references; the 2,800+ organizations figure covers the broader Axiom/Stratasan software.",
        "evidence": [
         "https://www.builtinchicago.org/articles/strata-combines-with-syntellis"
        ],
        "verify": true
       },
       "timeToValue": {
        "score": 3,
        "rationale": "Brings its own claims data, but current availability of the 2023 module under Strata was not confirmed.",
        "evidence": [
         "https://www.businesswire.com/news/home/20230628536825/en/Syntellis-Performance-Solutions-Launches-Axiom-Referral-Patterns-A-New-Data-Insights-Solution-to-Drive-Strategic-Growth-for-Healthcare-Organizations",
         "https://www.builtinchicago.org/articles/strata-combines-with-syntellis"
        ],
        "verify": true
       },
       "viability": {
        "score": 4,
        "rationale": "Owned by Roper Technologies and combined with Strata in 2023.",
        "evidence": [
         "https://www.builtinchicago.org/articles/strata-combines-with-syntellis"
        ],
        "verify": false
       },
       "measurement": {
        "score": 3,
        "rationale": "Leakage mapping is described; baseline and export details are not.",
        "evidence": [
         "https://www.businesswire.com/news/home/20230628536825/en/Syntellis-Performance-Solutions-Launches-Axiom-Referral-Patterns-A-New-Data-Insights-Solution-to-Drive-Strategic-Growth-for-Healthcare-Organizations"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No pricing model found in sources opened.",
        "evidence": [
         "https://www.businesswire.com/news/home/20230628536825/en/Syntellis-Performance-Solutions-Launches-Axiom-Referral-Patterns-A-New-Data-Insights-Solution-to-Drive-Strategic-Growth-for-Healthcare-Organizations"
        ],
        "verify": true
       }
      },
      "whyNot": "Strong owner, but no referral-specific references and the 2023 module's current status under Strata was not confirmed."
     },
     {
      "vendor": "Health Catalyst (Network Management)",
      "status": "nearMiss",
      "rank": 6,
      "weightedScore": 55.0,
      "scores": {
       "functional": {
        "score": 4,
        "rationale": "Combines claims, clinical and CRM data to track patient pathways, out-of-network drivers and referral behavior by provider and specialty.",
        "evidence": [
         "https://healthcatalyst.com/healthcare-analytics/population-health-value-based-care/network-management"
        ],
        "verify": false
       },
       "integration": {
        "score": 2,
        "rationale": "Page does not describe EHR or Caboodle connectivity.",
        "evidence": [
         "https://healthcatalyst.com/healthcare-analytics/population-health-value-based-care/network-management"
        ],
        "verify": true
       },
       "proof": {
        "score": 3,
        "rationale": "Links to Lehigh Valley Health Network and PeaceHealth success stories, details not opened.",
        "evidence": [
         "https://healthcatalyst.com/healthcare-analytics/population-health-value-based-care/network-management"
        ],
        "verify": true
       },
       "timeToValue": {
        "score": 2,
        "rationale": "Relies on ACME supplying claims data, which is its weakest asset (two payers, 60-90 day lag, manual matching) with engineers committed to the rebuild through Q2.",
        "evidence": [
         "https://healthcatalyst.com/healthcare-analytics/population-health-value-based-care/network-management"
        ],
        "verify": false
       },
       "viability": {
        "score": 2,
        "rationale": "Ownership and financial condition not reviewed in sources opened.",
        "evidence": [
         "https://healthcatalyst.com/healthcare-analytics/population-health-value-based-care/network-management"
        ],
        "verify": true
       },
       "measurement": {
        "score": 3,
        "rationale": "Tracks out-of-network drivers by provider; data rights not described.",
        "evidence": [
         "https://healthcatalyst.com/healthcare-analytics/population-health-value-based-care/network-management"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No pricing model found in sources opened.",
        "evidence": [
         "https://healthcatalyst.com/healthcare-analytics/population-health-value-based-care/network-management"
        ],
        "verify": true
       }
      },
      "whyNot": "Functionally relevant, but it depends on ACME supplying claims data, which is ACME's weakest asset and is under rebuild through Q2."
     },
     {
      "vendor": "Trella Health",
      "status": "evaluated",
      "rank": 7,
      "weightedScore": 50.0,
      "scores": {
       "functional": {
        "score": 3,
        "rationale": "Tracks referral destinations and out-of-network use by specialty, but from a Medicare Advantage dataset only, updated quarterly.",
        "evidence": [
         "https://www.trellahealth.com/?p=22522"
        ],
        "verify": false
       },
       "integration": {
        "score": 2,
        "rationale": "No EHR integration evidence found.",
        "evidence": [
         "https://www.trellahealth.com/?p=22522"
        ],
        "verify": true
       },
       "proof": {
        "score": 2,
        "rationale": "No named health system references found.",
        "evidence": [
         "https://www.trellahealth.com/?p=22522"
        ],
        "verify": true
       },
       "timeToValue": {
        "score": 3,
        "rationale": "External dataset needs no ACME feed, but quarterly updates slow the feedback loop.",
        "evidence": [
         "https://www.trellahealth.com/?p=22522"
        ],
        "verify": false
       },
       "viability": {
        "score": 2,
        "rationale": "Ownership not verified.",
        "evidence": [
         "https://www.trellahealth.com/?p=22522"
        ],
        "verify": true
       },
       "measurement": {
        "score": 3,
        "rationale": "Out-of-network use by specialty is reported; baseline and export not described.",
        "evidence": [
         "https://www.trellahealth.com/?p=22522"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No pricing model found in sources opened.",
        "evidence": [
         "https://www.trellahealth.com/?p=22522"
        ],
        "verify": true
       }
      }
     },
     {
      "vendor": "Arcadia (Network Modeler)",
      "status": "evaluated",
      "rank": 8,
      "weightedScore": 47.0,
      "scores": {
       "functional": {
        "score": 3,
        "rationale": "Visualizes referral flows and leakage and supports in-network referral pathways, but is aimed at payers and risk-bearing organizations using Medicare market data.",
        "evidence": [
         "https://arcadia.io/assets/Network-Modeler-Tear-Sheet.pdf"
        ],
        "verify": false
       },
       "integration": {
        "score": 2,
        "rationale": "No Epic evidence in tear sheet.",
        "evidence": [
         "https://arcadia.io/assets/Network-Modeler-Tear-Sheet.pdf"
        ],
        "verify": true
       },
       "proof": {
        "score": 2,
        "rationale": "No named health system references for referral leakage.",
        "evidence": [
         "https://arcadia.io/assets/Network-Modeler-Tear-Sheet.pdf"
        ],
        "verify": true
       },
       "timeToValue": {
        "score": 2,
        "rationale": "Data inputs from the client and deployment effort are not described.",
        "evidence": [
         "https://arcadia.io/assets/Network-Modeler-Tear-Sheet.pdf"
        ],
        "verify": true
       },
       "viability": {
        "score": 2,
        "rationale": "Ownership not reviewed in sources opened.",
        "evidence": [
         "https://arcadia.io/assets/Network-Modeler-Tear-Sheet.pdf"
        ],
        "verify": true
       },
       "measurement": {
        "score": 3,
        "rationale": "Provides referral flow and leakage views; baselines and export not described.",
        "evidence": [
         "https://arcadia.io/assets/Network-Modeler-Tear-Sheet.pdf"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No pricing model found in sources opened.",
        "evidence": [
         "https://arcadia.io/assets/Network-Modeler-Tear-Sheet.pdf"
        ],
        "verify": true
       }
      }
     }
    ]
   },
   {
    "id": "lot-b",
    "name": "Referral intake, conversion and closed-loop tracking",
    "gap": "Faxed referrals from non-Epic practices need manual entry, unscheduled referrals need manual follow-up, and loop closure with non-Epic receivers depends on thin 360X/Direct participation.",
    "why": "Targets the outcomes 'coordinators reclaim time lost to manual routing and status chasing' and 'closed-loop completion status recorded for every outbound referral' in the three first-wave specialties (Dec 2026 to Feb 2027), working inside Epic work queues.",
    "evaluated": [
     {
      "vendor": "Notable",
      "status": "shortlist",
      "rank": 1,
      "weightedScore": 69.0,
      "scores": {
       "functional": {
        "score": 4,
        "rationale": "Automates the path from incoming fax to Epic order, patient outreach and appointment scheduling, plus self-scheduling and MyChart inquiry handling; outbound loop closure is not described.",
        "evidence": [
         "https://www.notablehealth.com/lp/access-landing/access-landing-epic",
         "https://www.notablehealth.com/lp/extending-the-power-of-epic"
        ],
        "verify": false
       },
       "integration": {
        "score": 4,
        "rationale": "Vendor states it writes data back into Epic, complements MyChart and offers integration through the Epic Showroom.",
        "evidence": [
         "https://www.notablehealth.com/lp/access-landing/access-landing-epic",
         "https://www.notablehealth.com/lp/extending-the-power-of-epic"
        ],
        "verify": false
       },
       "proof": {
        "score": 3,
        "rationale": "Named Epic customers include CommonSpirit, MUSC Health and Catholic Health, but referral outcomes are attributed to unnamed systems and some customer cards on the vendor page are mislabeled.",
        "evidence": [
         "https://www.notablehealth.com/lp/access-landing/access-landing-epic",
         "https://www.notablehealth.com/lp/extending-the-power-of-epic"
        ],
        "verify": false
       },
       "timeToValue": {
        "score": 4,
        "rationale": "Vendor states individual workflows deploy in 4-6 weeks and pre-built workflows in 6-12 weeks, which fits the Dec 2026 start of first-wave specialties.",
        "evidence": [
         "https://www.notablehealth.com/lp/access-landing/access-landing-epic",
         "https://www.notablehealth.com/lp/extending-the-power-of-epic"
        ],
        "verify": false
       },
       "viability": {
        "score": 2,
        "rationale": "Ownership and funding not verified.",
        "evidence": [
         "https://www.notablehealth.com/lp/access-landing/access-landing-epic"
        ],
        "verify": true
       },
       "measurement": {
        "score": 3,
        "rationale": "Reports fax-to-order turnaround reductions; referral completion and leakage metrics and data export not described.",
        "evidence": [
         "https://www.notablehealth.com/lp/extending-the-power-of-epic"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No pricing model found in sources opened.",
        "evidence": [
         "https://www.notablehealth.com/lp/extending-the-power-of-epic"
        ],
        "verify": true
       }
      },
      "whyShortlisted": "Highest weighted score in the lot, driven by documented Epic write-back, named Epic customers and the shortest vendor-stated deployment time. It addresses coordinator time lost to manual intake and status chasing, which is one of the three stated outcomes.",
      "keyRisk": "Referral results are vendor-reported from unnamed systems and some customer references on its page are inconsistent, so proof rests on reference calls.",
      "verifyInDemo": [
       "End-to-end run of a faxed referral from a non-Epic practice into a signed Epic referral order, the correct work queue and a scheduled Cadence appointment.",
       "Referral status write-back fields in Epic at each step, and how 'completed' and 'results returned' are recorded for outbound referrals.",
       "Which of the 4-6 week workflows are pre-built for specialty referrals versus configured for ACME.",
       "Reporting on referral-to-appointment conversion by specialty and export to Caboodle."
      ],
      "rfpQuestions": [
       "Which Epic health systems use Notable for specialty referral intake in production, and which of them will take a reference call?",
       "Is Notable listed in Epic Showroom for this workflow, and which Epic APIs and interfaces does it use for order creation and status updates?",
       "Does Notable support outbound referral loop closure to non-Epic specialists (360X or Direct), or only inbound intake?",
       "What is the pricing basis (per referral, per workflow or per site), and are outcome-based terms available?"
      ]
     },
     {
      "vendor": "Luma Health",
      "status": "shortlist",
      "rank": 2,
      "weightedScore": 66.0,
      "scores": {
       "functional": {
        "score": 4,
        "rationale": "Fax automation identifies inbound and outbound referrals, creates referrals in the EHR, routes them to teams and supports scheduling and loop closure.",
        "evidence": [
         "https://www.lumahealth.io/product/referral-management/"
        ],
        "verify": false
       },
       "integration": {
        "score": 4,
        "rationale": "Vendor documents integration through Epic Interconnect web services, including creating EHR referrals; Showroom status not stated.",
        "evidence": [
         "https://lumahealth.io/wp-content/uploads/2023/11/Luma-Integrated-with-Epic.pdf",
         "https://www.lumahealth.io/product/referral-management/"
        ],
        "verify": false
       },
       "proof": {
        "score": 3,
        "rationale": "Named customers are Kelsey-Seybold Clinic, Mile Bluff Medical Center and Northfield Hospital + Clinics with vendor-reported results; no Tier 1 IDN named for referrals.",
        "evidence": [
         "https://www.lumahealth.io/product/referral-management/",
         "https://lumahealth.io/wp-content/uploads/2023/11/Luma-Integrated-with-Epic.pdf"
        ],
        "verify": false
       },
       "timeToValue": {
        "score": 3,
        "rationale": "No published timeline; Epic integration is through existing web services rather than a new data platform.",
        "evidence": [
         "https://lumahealth.io/wp-content/uploads/2023/11/Luma-Integrated-with-Epic.pdf"
        ],
        "verify": true
       },
       "viability": {
        "score": 2,
        "rationale": "Ownership and funding not verified.",
        "evidence": [
         "https://www.lumahealth.io/product/referral-management/"
        ],
        "verify": true
       },
       "measurement": {
        "score": 3,
        "rationale": "Reports referral-to-appointment conversion rates; data export not described.",
        "evidence": [
         "https://lumahealth.io/wp-content/uploads/2023/11/Luma-Integrated-with-Epic.pdf"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No pricing model found in sources opened.",
        "evidence": [
         "https://www.lumahealth.io/product/referral-management/"
        ],
        "verify": true
       }
      },
      "whyShortlisted": "It is the only Epic-connected vendor in the lot that states support for both inbound and outbound referrals and loop closure. Named Epic-based customers report referral conversion results, although they are smaller than ACME.",
      "keyRisk": "References are mid-size organizations, so performance at Tier 1 IDN referral volumes is unproven.",
      "verifyInDemo": [
       "Outbound referral loop closure: how Luma detects that a referred patient was seen and results returned, for an Epic and a non-Epic specialist.",
       "Referral creation in Epic from a faxed document, and the Interconnect web services used.",
       "Referral-to-appointment conversion reporting by specialty with a pre-go-live baseline."
      ],
      "rfpQuestions": [
       "Name an Epic health system with more than 1,000 providers using Luma referral management and offer a reference call.",
       "What Epic build and ACME FTE are needed, and what is the typical timeline to go live on fax referral automation?",
       "How does Luma record closed-loop completion status for outbound referrals in Epic?",
       "How is referral management priced relative to the rest of the Luma platform?"
      ]
     },
     {
      "vendor": "Artera (formerly WELL Health)",
      "status": "shortlist",
      "rank": 3,
      "weightedScore": 65.0,
      "scores": {
       "functional": {
        "score": 3,
        "rationale": "Runs patient outreach on referral creation and status changes and writes referral status back through conversation flows, but does not handle intake and does not support outbound referrals to other organizations.",
        "evidence": [
         "https://knowledge.artera.io/referrals/1257807-referral-management-overview"
        ],
        "verify": false
       },
       "integration": {
        "score": 4,
        "rationale": "Vendor states bidirectional Epic integration, and referral status write-back is supported through conversation flows.",
        "evidence": [
         "https://artera.io/ai-health-systems/",
         "https://knowledge.artera.io/referrals/1257807-referral-management-overview"
        ],
        "verify": false
       },
       "proof": {
        "score": 4,
        "rationale": "2026 Best in KLAS for Patient Communications; Prisma Health reported revenue recovered from outreach and referral conversion (vendor-reported).",
        "evidence": [
         "https://klasresearch.com/report/2026-best-in-klas-awards-software-and-services/3906",
         "https://artera.io/ai-health-systems/"
        ],
        "verify": false
       },
       "timeToValue": {
        "score": 3,
        "rationale": "Referral triggers are an add-on, but setup requires specialty mapping to EMR specialty IDs; effort depends on whether ACME already uses Artera.",
        "evidence": [
         "https://knowledge.artera.io/referrals/1257807-referral-management-overview"
        ],
        "verify": true
       },
       "viability": {
        "score": 3,
        "rationale": "Independent recognition indicates an established customer base; ownership not reviewed.",
        "evidence": [
         "https://klasresearch.com/report/2026-best-in-klas-awards-software-and-services/3906"
        ],
        "verify": true
       },
       "measurement": {
        "score": 2,
        "rationale": "Referral conversion reporting and data export not described.",
        "evidence": [
         "https://knowledge.artera.io/referrals/1257807-referral-management-overview"
        ],
        "verify": true
       },
       "commercial": {
        "score": 3,
        "rationale": "Referral triggers are described as a referrals add-on, which shows the packaging if not the price.",
        "evidence": [
         "https://knowledge.artera.io/referrals/1257807-referral-management-overview"
        ],
        "verify": false
       }
      },
      "whyShortlisted": "Strongest independent recognition in the lot and documented referral status write-back, which addresses the status chasing that consumes coordinator time. It scores on proof and integration rather than breadth.",
      "keyRisk": "It explicitly does not support referrals from ACME's EMR to other organizations, so it cannot close the loop on outbound external referrals and covers only part of the lot gap.",
      "verifyInDemo": [
       "A conversation flow that moves an unscheduled Epic referral to scheduled and writes the status back to Epic.",
       "Handling of inbound external referrals after they land in an Epic work queue.",
       "Throttled referral queue behavior at ACME volumes and the reporting on conversion by specialty."
      ],
      "rfpQuestions": [
       "Is support for outbound external referrals on the roadmap, and with what date?",
       "Name Epic health systems using the referrals add-on in production and offer a reference call.",
       "What is the price of the referrals add-on, and does it require the broader Artera platform?"
      ]
     },
     {
      "vendor": "Innovaccer (Referral Management / Comet)",
      "status": "shortlist",
      "rank": 4,
      "weightedScore": 63.0,
      "scores": {
       "functional": {
        "score": 4,
        "rationale": "Point-of-care referral creation, specialist filters for cost, quality and network status, fax OCR, a network leakage dashboard and AI scheduling agents in one product.",
        "evidence": [
         "https://marketplace.aviahealth.com/product/64709",
         "https://www.businesswire.com/news/home/20251008423565/en/Olympia-Orthopaedic-Associates-Taps-Innovaccer-to-Automate-Referrals-and-Scheduling-with-AI-Agents",
         "https://innovaccer.ai/blogs/reducing-network-leakage-and-improve-physician-and-patient-engagement-through-a-comprehensive-referral-management-solution"
        ],
        "verify": false
       },
       "integration": {
        "score": 3,
        "rationale": "AVIA lists Epic with a bi-directional HL7/FHIR interface, with integration type marked use-case dependent and no Showroom listing found.",
        "evidence": [
         "https://marketplace.aviahealth.com/product/64709"
        ],
        "verify": true
       },
       "proof": {
        "score": 3,
        "rationale": "Olympia Orthopaedic Associates is the named referral customer; Orlando Health, Adventist HealthCare and Banner Health are named clients but not tied to referrals.",
        "evidence": [
         "https://www.businesswire.com/news/home/20251008423565/en/Olympia-Orthopaedic-Associates-Taps-Innovaccer-to-Automate-Referrals-and-Scheduling-with-AI-Agents",
         "https://www.businesswire.com/news/home/20260604845432/en/"
        ],
        "verify": false
       },
       "timeToValue": {
        "score": 2,
        "rationale": "Runs on Innovaccer's data platform, implying data onboarding that ACME's 2.1 unallocated FTE would have to support; no timeline published.",
        "evidence": [
         "https://marketplace.aviahealth.com/product/64709"
        ],
        "verify": true
       },
       "viability": {
        "score": 3,
        "rationale": "Names several large health system clients; financials not reviewed.",
        "evidence": [
         "https://www.businesswire.com/news/home/20260604845432/en/"
        ],
        "verify": true
       },
       "measurement": {
        "score": 4,
        "rationale": "Includes a network leakage dashboard alongside referral tracking.",
        "evidence": [
         "https://marketplace.aviahealth.com/product/64709",
         "https://innovaccer.ai/blogs/reducing-network-leakage-and-improve-physician-and-patient-engagement-through-a-comprehensive-referral-management-solution"
        ],
        "verify": false
       },
       "commercial": {
        "score": 2,
        "rationale": "No pricing model found in sources opened.",
        "evidence": [
         "https://marketplace.aviahealth.com/product/64709"
        ],
        "verify": true
       }
      },
      "whyShortlisted": "The only Lot B vendor that combines intake, in-network specialist filtering and a leakage dashboard, which maps to all three outcomes in this use case. It ties with Janus on weighted score and ranks ahead on named references.",
      "keyRisk": "It may require ACME to stand up Innovaccer's data platform, which competes for the same thin engineering capacity already committed to the payer feed rebuild.",
      "verifyInDemo": [
       "An Epic-originated referral flowing through specialist filtering by network status and back into an Epic work queue, showing the HL7/FHIR messages used.",
       "Fax OCR producing a structured Epic referral order from a sample ACME inbound referral.",
       "Network leakage dashboard: which data feeds it (Epic orders only or claims), and how loop status is defined.",
       "Minimum data onboarding required if ACME does not license the broader Innovaccer platform."
      ],
      "rfpQuestions": [
       "Can Referral Management and Comet be licensed without the full Healthcare Intelligence Cloud, and what Epic build does ACME own?",
       "Name an Epic health system using Innovaccer referral management in production and offer a reference call.",
       "How are specialist cost, quality and network-status filters sourced and audited for referral steering defensibility?",
       "What implementation timeline and ACME FTE commitment should be planned for the Dec 2026 to Feb 2027 first-wave specialties?"
      ]
     },
     {
      "vendor": "Janus Health",
      "status": "nearMiss",
      "rank": 5,
      "weightedScore": 63.0,
      "scores": {
       "functional": {
        "score": 4,
        "rationale": "Extracts and validates faxed and handwritten referrals, detects duplicates, creates referral orders and tracks them through scheduling, but outbound loop closure is not described.",
        "evidence": [
         "https://www.janus-ai.com/blog/referral-management-process-checklist/",
         "https://www.healthcareitnews.com/news/how-health-systems-can-stop-losing-money-referrals-while-boosting-patient-care"
        ],
        "verify": false
       },
       "integration": {
        "score": 4,
        "rationale": "Vendor describes automatic Epic referral order creation, FHIR patient matching, HL7 into work queues and Media Manager fax import; Epic Showroom listing not confirmed.",
        "evidence": [
         "https://www.janus-ai.com/blog/referral-management-process-checklist/",
         "https://platform.softwareone.com/product/referral-management/PCP-1955-1347"
        ],
        "verify": true
       },
       "proof": {
        "score": 2,
        "rationale": "Results come from unnamed systems, including a vendor-reported 40% referral volume increase.",
        "evidence": [
         "https://www.healthcareitnews.com/news/how-health-systems-can-stop-losing-money-referrals-while-boosting-patient-care"
        ],
        "verify": true
       },
       "timeToValue": {
        "score": 3,
        "rationale": "Works inside existing Epic work queues, which limits retraining, but no deployment timeline is published.",
        "evidence": [
         "https://platform.softwareone.com/product/referral-management/PCP-1955-1347"
        ],
        "verify": true
       },
       "viability": {
        "score": 2,
        "rationale": "Ownership and funding not verified.",
        "evidence": [
         "https://platform.softwareone.com/product/referral-management/PCP-1955-1347"
        ],
        "verify": true
       },
       "measurement": {
        "score": 3,
        "rationale": "Tracks referrals from intake to scheduling; export and leakage measures not described.",
        "evidence": [
         "https://www.janus-ai.com/blog/referral-management-process-checklist/"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No pricing model found in sources opened.",
        "evidence": [
         "https://platform.softwareone.com/product/referral-management/PCP-1955-1347"
        ],
        "verify": true
       }
      },
      "whyNot": "Has the most specific Epic write-back mechanics in the lot (orders, HL7 work queues, Media Manager), but no named customer and no published timeline kept it below Innovaccer on the proof tie-break; worth a reference check before final selection."
     },
     {
      "vendor": "ReferralMD",
      "status": "nearMiss",
      "rank": 6,
      "weightedScore": 62.0,
      "scores": {
       "functional": {
        "score": 4,
        "rationale": "Covers referral management, eConsults, fax management, intake, appointment management and analytics for health systems.",
        "evidence": [
         "https://marketplace.aviahealth.com/product/25090/referralmd-platform"
        ],
        "verify": false
       },
       "integration": {
        "score": 3,
        "rationale": "AVIA lists Epic with API, FHIR, HL7 and Direct connectivity, but EHR integration is optional, which risks a parallel queue.",
        "evidence": [
         "https://marketplace.aviahealth.com/product/25090/referralmd-platform"
        ],
        "verify": false
       },
       "proof": {
        "score": 3,
        "rationale": "AVIA listing includes a Baptist Memorial Healthcare reviewer; no other named IDN references found.",
        "evidence": [
         "https://marketplace.aviahealth.com/product/25090/referralmd-platform"
        ],
        "verify": true
       },
       "timeToValue": {
        "score": 3,
        "rationale": "Cloud platform that can run without EHR integration, which shortens start-up but defers Epic write-back work.",
        "evidence": [
         "https://marketplace.aviahealth.com/product/25090/referralmd-platform"
        ],
        "verify": true
       },
       "viability": {
        "score": 2,
        "rationale": "Ownership not verified.",
        "evidence": [
         "https://marketplace.aviahealth.com/product/25090/referralmd-platform"
        ],
        "verify": true
       },
       "measurement": {
        "score": 3,
        "rationale": "Includes analytics, but loop-closure metrics and data export were not confirmed.",
        "evidence": [
         "https://marketplace.aviahealth.com/product/25090/referralmd-platform"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No pricing model found in sources opened.",
        "evidence": [
         "https://marketplace.aviahealth.com/product/25090/referralmd-platform"
        ],
        "verify": true
       }
      },
      "whyNot": "Broad referral feature set, but Epic integration is optional, which risks a parallel queue, and only one IDN reviewer was found."
     },
     {
      "vendor": "LeadingReach",
      "status": "nearMiss",
      "rank": 7,
      "weightedScore": 58.0,
      "scores": {
       "functional": {
        "score": 4,
        "rationale": "Consolidates fax, email, Direct and web-form referrals into one queue, gives referring providers free status accounts and reports referral-to-appointment and aging metrics.",
        "evidence": [
         "https://leadingreach.com/news/enhanced-healthcare-partners-announced-its-growth-investment-in-leadingreach/",
         "https://leadingreach.com/outreach-crm"
        ],
        "verify": false
       },
       "integration": {
        "score": 2,
        "rationale": "Works across EHRs and is an athenahealth partner; no Epic-specific evidence found.",
        "evidence": [
         "https://leadingreach.com/news/enhanced-healthcare-partners-announced-its-growth-investment-in-leadingreach/"
        ],
        "verify": true
       },
       "proof": {
        "score": 2,
        "rationale": "Customers listed are mainly specialty and outpatient practices plus ChenMed; no large IDN named.",
        "evidence": [
         "https://leadingreach.com/news/enhanced-healthcare-partners-announced-its-growth-investment-in-leadingreach/"
        ],
        "verify": false
       },
       "timeToValue": {
        "score": 3,
        "rationale": "Existing network with free referring-provider accounts lowers onboarding, but Epic connection effort is unknown.",
        "evidence": [
         "https://leadingreach.com/news/enhanced-healthcare-partners-announced-its-growth-investment-in-leadingreach/"
        ],
        "verify": true
       },
       "viability": {
        "score": 3,
        "rationale": "Growth investment from Enhanced Healthcare Partners in July 2026.",
        "evidence": [
         "https://leadingreach.com/news/enhanced-healthcare-partners-announced-its-growth-investment-in-leadingreach/"
        ],
        "verify": false
       },
       "measurement": {
        "score": 3,
        "rationale": "Reports referral-to-appointment and aging metrics; data export not described.",
        "evidence": [
         "https://leadingreach.com/outreach-crm"
        ],
        "verify": true
       },
       "commercial": {
        "score": 3,
        "rationale": "Tiered plans from free to enterprise are described.",
        "evidence": [
         "https://leadingreach.com/news/enhanced-healthcare-partners-announced-its-growth-investment-in-leadingreach/"
        ],
        "verify": false
       }
      },
      "whyNot": "Its free referrer accounts and status visibility fit outbound loop closure, but no Epic integration evidence and no large health system references leave it behind Epic-connected vendors."
     },
     {
      "vendor": "ActiumHealth",
      "status": "evaluated",
      "rank": 8,
      "weightedScore": 57.0,
      "scores": {
       "functional": {
        "score": 3,
        "rationale": "AI agents detect signed referral orders, run outreach and schedule, and update status from New to Scheduled or Closed; no fax intake.",
        "evidence": [
         "https://www.actiumhealth.com/use-cases/referral-management/"
        ],
        "verify": false
       },
       "integration": {
        "score": 3,
        "rationale": "Describes detecting referral orders in Epic and updating status, but integration mechanics are not described.",
        "evidence": [
         "https://www.actiumhealth.com/use-cases/referral-management/"
        ],
        "verify": true
       },
       "proof": {
        "score": 3,
        "rationale": "Logos include MemorialCare, Nebraska Medicine, MultiCare, Legacy Health and Virtua, none tied to the referral use case.",
        "evidence": [
         "https://www.actiumhealth.com/"
        ],
        "verify": false
       },
       "timeToValue": {
        "score": 3,
        "rationale": "Agent-based outreach reduces staff load, but deployment effort is not described.",
        "evidence": [
         "https://www.actiumhealth.com/use-cases/referral-management/"
        ],
        "verify": true
       },
       "viability": {
        "score": 3,
        "rationale": "Owned by Syllable Corporation since 2024.",
        "evidence": [
         "https://www.healthcareittoday.com/2024/04/23/syllable-corporation-announces-purchase-of-actium-health/"
        ],
        "verify": false
       },
       "measurement": {
        "score": 2,
        "rationale": "Conversion reporting and data rights not described.",
        "evidence": [
         "https://www.actiumhealth.com/use-cases/referral-management/"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No pricing model found in sources opened.",
        "evidence": [
         "https://www.actiumhealth.com/use-cases/referral-management/"
        ],
        "verify": true
       }
      }
     },
     {
      "vendor": "Tennr",
      "status": "evaluated",
      "rank": 9,
      "weightedScore": 49.0,
      "scores": {
       "functional": {
        "score": 3,
        "rationale": "Automates intake of referral documents with payer criteria checks and status visibility through Tennr Network; named customer is a medical supply company rather than a health system.",
        "evidence": [
         "https://pulse2.com/tennr-101-million-raised-for-automating-referral-based-care"
        ],
        "verify": false
       },
       "integration": {
        "score": 2,
        "rationale": "Works without EMR replacement; no Epic-specific evidence found.",
        "evidence": [
         "https://pulse2.com/tennr-101-million-raised-for-automating-referral-based-care"
        ],
        "verify": true
       },
       "proof": {
        "score": 2,
        "rationale": "No named provider health system reference.",
        "evidence": [
         "https://pulse2.com/tennr-101-million-raised-for-automating-referral-based-care"
        ],
        "verify": true
       },
       "timeToValue": {
        "score": 2,
        "rationale": "Implementation effort and timeline not described.",
        "evidence": [
         "https://pulse2.com/tennr-101-million-raised-for-automating-referral-based-care"
        ],
        "verify": true
       },
       "viability": {
        "score": 4,
        "rationale": "Raised a $101M Series C led by IVP in June 2025.",
        "evidence": [
         "https://pulse2.com/tennr-101-million-raised-for-automating-referral-based-care"
        ],
        "verify": false
       },
       "measurement": {
        "score": 2,
        "rationale": "Measurement and data rights not described.",
        "evidence": [
         "https://pulse2.com/tennr-101-million-raised-for-automating-referral-based-care"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No pricing model found in sources opened.",
        "evidence": [
         "https://pulse2.com/tennr-101-million-raised-for-automating-referral-based-care"
        ],
        "verify": true
       }
      }
     },
     {
      "vendor": "ReferWell",
      "status": "evaluated",
      "rank": 10,
      "weightedScore": 48.0,
      "scores": {
       "functional": {
        "score": 3,
        "rationale": "Matches patients to local providers and books appointments with a concierge scheduling service, aimed at health plans and provider organizations.",
        "evidence": [
         "https://www.businesswire.com/news/home/20240312997249/en/Essen-Health-Care-Selects-ReferWell-as-Their-Strategic-Partner-to-Improve-Access-to-Care"
        ],
        "verify": false
       },
       "integration": {
        "score": 2,
        "rationale": "No EHR integration details in sources opened.",
        "evidence": [
         "https://www.businesswire.com/news/home/20240312997249/en/Essen-Health-Care-Selects-ReferWell-as-Their-Strategic-Partner-to-Improve-Access-to-Care"
        ],
        "verify": true
       },
       "proof": {
        "score": 2,
        "rationale": "Essen Health Care named; no large Epic IDN reference.",
        "evidence": [
         "https://www.businesswire.com/news/home/20240312997249/en/Essen-Health-Care-Selects-ReferWell-as-Their-Strategic-Partner-to-Improve-Access-to-Care"
        ],
        "verify": true
       },
       "timeToValue": {
        "score": 3,
        "rationale": "Tech-enabled service model shifts scheduling work off ACME staff.",
        "evidence": [
         "https://www.businesswire.com/news/home/20240312997249/en/Essen-Health-Care-Selects-ReferWell-as-Their-Strategic-Partner-to-Improve-Access-to-Care"
        ],
        "verify": false
       },
       "viability": {
        "score": 2,
        "rationale": "Ownership not verified.",
        "evidence": [
         "https://www.businesswire.com/news/home/20240312997249/en/Essen-Health-Care-Selects-ReferWell-as-Their-Strategic-Partner-to-Improve-Access-to-Care"
        ],
        "verify": true
       },
       "measurement": {
        "score": 2,
        "rationale": "Measurement and data rights not described.",
        "evidence": [
         "https://www.businesswire.com/news/home/20240312997249/en/Essen-Health-Care-Selects-ReferWell-as-Their-Strategic-Partner-to-Improve-Access-to-Care"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No pricing model found in sources opened.",
        "evidence": [
         "https://www.businesswire.com/news/home/20240312997249/en/Essen-Health-Care-Selects-ReferWell-as-Their-Strategic-Partner-to-Improve-Access-to-Care"
        ],
        "verify": true
       }
      }
     },
     {
      "vendor": "Lightbeam Health (Referral Management)",
      "status": "evaluated",
      "rank": 11,
      "weightedScore": 47.0,
      "scores": {
       "functional": {
        "score": 3,
        "rationale": "Ranks preferred-network specialists by performance, insurance, distance and language and tracks each referral step, but as a module of a population health platform.",
        "evidence": [
         "https://lightbeamhealth.com/solutions/referral-management/"
        ],
        "verify": false
       },
       "integration": {
        "score": 2,
        "rationale": "No EHR integration detail on referral page or case study.",
        "evidence": [
         "https://lightbeamhealth.com/solutions/referral-management/"
        ],
        "verify": true
       },
       "proof": {
        "score": 2,
        "rationale": "Case study is a physician group (Vanguard Medical Group) with vendor-reported results.",
        "evidence": [
         "https://lightbeamhealth.com/wp-content/uploads/2026/01/Case-Study-Referral-Management.pdf"
        ],
        "verify": false
       },
       "timeToValue": {
        "score": 2,
        "rationale": "Requires the broader population health platform.",
        "evidence": [
         "https://lightbeamhealth.com/solutions/referral-management/"
        ],
        "verify": false
       },
       "viability": {
        "score": 2,
        "rationale": "Ownership not reviewed.",
        "evidence": [
         "https://lightbeamhealth.com/solutions/referral-management/"
        ],
        "verify": true
       },
       "measurement": {
        "score": 3,
        "rationale": "Case study reports leakage reduction against a prior period (vendor-reported).",
        "evidence": [
         "https://lightbeamhealth.com/wp-content/uploads/2026/01/Case-Study-Referral-Management.pdf"
        ],
        "verify": false
       },
       "commercial": {
        "score": 2,
        "rationale": "No pricing model found in sources opened.",
        "evidence": [
         "https://lightbeamhealth.com/solutions/referral-management/"
        ],
        "verify": true
       }
      }
     },
     {
      "vendor": "AristaMD",
      "status": "evaluated",
      "rank": 12,
      "weightedScore": 45.0,
      "scores": {
       "functional": {
        "score": 3,
        "rationale": "Combines eConsults, specialist matching by insurance, location and sub-specialty, and referral sending and tracking from primary care.",
        "evidence": [
         "https://www.fiercehealthcare.com/providers/aristamd-launches-solution-expand-access-specialty-care-pcps",
         "https://www.healthcareittoday.com/2022/02/11/aristamd-acquires-referral-management-solution-preferral-to-increase-patient-access-to-specialty-care/"
        ],
        "verify": false
       },
       "integration": {
        "score": 2,
        "rationale": "Described as interoperable; no Epic-specific evidence.",
        "evidence": [
         "https://www.fiercehealthcare.com/providers/aristamd-launches-solution-expand-access-specialty-care-pcps"
        ],
        "verify": true
       },
       "proof": {
        "score": 2,
        "rationale": "Health systems use it but none are named.",
        "evidence": [
         "https://www.fiercehealthcare.com/providers/aristamd-launches-solution-expand-access-specialty-care-pcps"
        ],
        "verify": true
       },
       "timeToValue": {
        "score": 2,
        "rationale": "Implementation effort not described; optional coordination service exists.",
        "evidence": [
         "https://www.fiercehealthcare.com/providers/aristamd-launches-solution-expand-access-specialty-care-pcps"
        ],
        "verify": true
       },
       "viability": {
        "score": 2,
        "rationale": "Independent; funding not verified.",
        "evidence": [
         "https://www.healthcareittoday.com/2022/02/11/aristamd-acquires-referral-management-solution-preferral-to-increase-patient-access-to-specialty-care/"
        ],
        "verify": true
       },
       "measurement": {
        "score": 2,
        "rationale": "Measurement capability not described.",
        "evidence": [
         "https://www.fiercehealthcare.com/providers/aristamd-launches-solution-expand-access-specialty-care-pcps"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No pricing model found in sources opened.",
        "evidence": [
         "https://www.fiercehealthcare.com/providers/aristamd-launches-solution-expand-access-specialty-care-pcps"
        ],
        "verify": true
       }
      }
     },
     {
      "vendor": "Phreesia (Referral Management)",
      "status": "evaluated",
      "rank": 13,
      "weightedScore": 45.0,
      "scores": {
       "functional": {
        "score": 3,
        "rationale": "Specialist-side work queues that standardize faxed and electronic referrals, prioritize, check eligibility and send patient outreach; inbound only.",
        "evidence": [
         "https://phreesia.com/?p=42418"
        ],
        "verify": false
       },
       "integration": {
        "score": 2,
        "rationale": "Referral page does not mention Epic.",
        "evidence": [
         "https://phreesia.com/?p=42418"
        ],
        "verify": true
       },
       "proof": {
        "score": 2,
        "rationale": "No named customers on the referral page.",
        "evidence": [
         "https://phreesia.com/?p=42418"
        ],
        "verify": true
       },
       "timeToValue": {
        "score": 2,
        "rationale": "Implementation effort not described.",
        "evidence": [
         "https://phreesia.com/?p=42418"
        ],
        "verify": true
       },
       "viability": {
        "score": 2,
        "rationale": "Financial condition not reviewed in sources opened.",
        "evidence": [
         "https://phreesia.com/?p=42418"
        ],
        "verify": true
       },
       "measurement": {
        "score": 2,
        "rationale": "Only a vendor-reported scheduling likelihood claim; no baseline method.",
        "evidence": [
         "https://phreesia.com/?p=42418"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No pricing model found in sources opened.",
        "evidence": [
         "https://phreesia.com/?p=42418"
        ],
        "verify": true
       }
      }
     }
    ]
   },
   {
    "id": "lot-c",
    "name": "Provider data, in-network matching and scheduling access",
    "gap": "Sub-specialty, location and network participation data are inconsistent, and Epic's order-and-location routing does not rank in-network specialists by plan, language and next Cadence availability.",
    "why": "Supports the directory remediation in the Baseline milestone and the Network-Wide Rollout with scheduling write-back (Mar to May 2027), and addresses the referral steering and directory accuracy risks.",
    "evaluated": [
     {
      "vendor": "Kyruus Health",
      "status": "shortlist",
      "rank": 1,
      "weightedScore": 69.0,
      "scores": {
       "functional": {
        "score": 4,
        "rationale": "Provider data management, provider search and matching, self-scheduling and listings for health systems, which addresses directory accuracy and matching; referral-specific matching for clinicians was not confirmed.",
        "evidence": [
         "https://kyruushealth.com/",
         "https://www.fiercehealthcare.com/health-tech/revspring-set-buy-kyruus-health-combine-provider-search-scheduling-and-payment"
        ],
        "verify": false
       },
       "integration": {
        "score": 4,
        "rationale": "Joined Epic App Orchard in 2018 to book into Epic schedules from ProviderMatch, with Piedmont Healthcare as an early joint customer; Cadence specifics not documented.",
        "evidence": [
         "https://kyruushealth.com/kyruus-joins-the-epic-app-orchard-with-direct-scheduling-solution/",
         "https://kyruushealth.com/"
        ],
        "verify": false
       },
       "proof": {
        "score": 4,
        "rationale": "Reported 1,400 hospitals and 550 medical groups as customers, with Banner Health, Geisinger, Mass General Brigham, MedStar and UAB shown as customers.",
        "evidence": [
         "https://www.fiercehealthcare.com/health-tech/revspring-set-buy-kyruus-health-combine-provider-search-scheduling-and-payment",
         "https://kyruushealth.com/"
        ],
        "verify": false
       },
       "timeToValue": {
        "score": 3,
        "rationale": "Provider data work is required either way, but no timeline is published.",
        "evidence": [
         "https://kyruushealth.com/"
        ],
        "verify": true
       },
       "viability": {
        "score": 3,
        "rationale": "RevSpring agreed to acquire Kyruus in Sept 2025 with closing expected in Q4 2025; closing was not confirmed in sources opened and the product is described as a complement to RevSpring's offerings.",
        "evidence": [
         "https://www.techtarget.com/patientengagement/news/366631620/RevSpring-acquires-Kyruus-to-fuse-provider-search-functions",
         "https://www.healthcareittoday.com/2025/10/28/revspring-to-acquire-kyruus-health-creating-a-connected-care-journey-from-search-to-final-payment/"
        ],
        "verify": true
       },
       "measurement": {
        "score": 2,
        "rationale": "Measurement of referral retention and data export not described.",
        "evidence": [
         "https://kyruushealth.com/"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No pricing model found in sources opened.",
        "evidence": [
         "https://kyruushealth.com/"
        ],
        "verify": true
       }
      },
      "whyShortlisted": "Largest health system customer base in the lot and an Epic scheduling integration, applied to the provider directory accuracy risk named in this use case. It is the closest match to the Baseline milestone's directory remediation and the Scale milestone's scheduling write-back.",
      "keyRisk": "Ownership is changing under RevSpring, and the product direction for provider search and scheduling under a payments company is not yet documented.",
      "verifyInDemo": [
       "Matching rules that rank employed and affiliated specialists by sub-specialty, insurance, location, language and next Cadence availability, with an audit trail for each match.",
       "Booking into Epic Cadence from a referral context, including how template changes that are not versioned in ACME's Cadence are handled.",
       "Provider data correction workflow: who edits sub-specialty and network participation, and how changes reach Epic and the public directory.",
       "Reporting that ties matched and booked referrals back to in-network retention."
      ],
      "rfpQuestions": [
       "Has the RevSpring acquisition closed, and what is the committed roadmap and support model for provider search, provider data and scheduling?",
       "Is a clinician-facing referral matching workflow available inside Epic, or only consumer and access-center search?",
       "Name an Epic health system using Kyruus for referral routing to employed specialists and offer a reference call.",
       "What is the pricing basis (per provider, per site, per booking), and what are renewal protections through an ownership change?"
      ]
     },
     {
      "vendor": "DexCare",
      "status": "shortlist",
      "rank": 2,
      "weightedScore": 66.0,
      "scores": {
       "functional": {
        "score": 3,
        "rationale": "Matches patient demand to available capacity across ambulatory services and routes patients to the right service, but referral routing is not described.",
        "evidence": [
         "https://news.massgeneralbrigham.org/en/mass-general-brigham-implements-dexcare-to-support-care-integration-and-advance-digital-health",
         "https://dexcare.com/wp-content/uploads/2025/06/DexCare-2025-Emerging-Company-Spotlight.pdf"
        ],
        "verify": false
       },
       "integration": {
        "score": 4,
        "rationale": "KLAS lists Epic as customer-validated integration, with respondents describing real-time scheduling data pulled from the EMR (n=5).",
        "evidence": [
         "https://dexcare.com/wp-content/uploads/2025/06/DexCare-2025-Emerging-Company-Spotlight.pdf"
        ],
        "verify": false
       },
       "proof": {
        "score": 4,
        "rationale": "KLAS Emerging Company Spotlight (June 2025) with 100% would-buy-again (n=5), and Mass General Brigham implemented it for its digital front door.",
        "evidence": [
         "https://dexcare.com/wp-content/uploads/2025/06/DexCare-2025-Emerging-Company-Spotlight.pdf",
         "https://news.massgeneralbrigham.org/en/mass-general-brigham-implements-dexcare-to-support-care-integration-and-advance-digital-health"
        ],
        "verify": false
       },
       "timeToValue": {
        "score": 3,
        "rationale": "Platform-as-a-service built on existing EMR investments; no timeline published.",
        "evidence": [
         "https://news.massgeneralbrigham.org/en/mass-general-brigham-implements-dexcare-to-support-care-integration-and-advance-digital-health"
        ],
        "verify": true
       },
       "viability": {
        "score": 4,
        "rationale": "Investors include Providence, Kaiser Permanente, Mass General Brigham, Tampa General and Texas Health Resources.",
        "evidence": [
         "https://chiefhealthcareexecutive.com/view/dexcare-ceo-pushes-against-capacity-constraints-in-hospitals"
        ],
        "verify": false
       },
       "measurement": {
        "score": 2,
        "rationale": "KLAS notes outcomes could not be clearly mapped; data export not described.",
        "evidence": [
         "https://dexcare.com/wp-content/uploads/2025/06/DexCare-2025-Emerging-Company-Spotlight.pdf"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No pricing model found in sources opened.",
        "evidence": [
         "https://chiefhealthcareexecutive.com/view/dexcare-ceo-pushes-against-capacity-constraints-in-hospitals"
        ],
        "verify": true
       }
      },
      "whyShortlisted": "Independent KLAS validation of its Epic integration and real-time capacity data is the strongest integration proof in the lot. Health system investors and a Mass General Brigham deployment support viability at Tier 1 scale.",
      "keyRisk": "Its documented use is digital front door and capacity matching, not specialist referral routing, so fit for clinician-initiated referrals is unproven.",
      "verifyInDemo": [
       "Routing a clinician-initiated specialty referral to the next available in-network slot using live Cadence capacity.",
       "Matching rule configuration and audit for patient choice and steering defensibility.",
       "Reporting on booking success and in-network retention by specialty."
      ],
      "rfpQuestions": [
       "Does DexCare support clinician-initiated specialty referral routing, and which health system uses it for that?",
       "Which Epic interfaces does DexCare use to read Cadence availability and book appointments?",
       "What implementation timeline and ACME FTE are required for 3 specialties?"
      ]
     },
     {
      "vendor": "Clarify Health (including Loyal)",
      "status": "shortlist",
      "rank": 3,
      "weightedScore": 57.0,
      "scores": {
       "functional": {
        "score": 4,
        "rationale": "Loyal adds provider data management, care search, real-time scheduling and CRM, connected to Clarify's leakage analytics.",
        "evidence": [
         "https://www.healthcareittoday.com/2026/06/19/clarify-health-acquires-loyal-health-to-create-healthcares-first-closed-loop-network-intelligence-and-patient-activation-platform/"
        ],
        "verify": false
       },
       "integration": {
        "score": 2,
        "rationale": "Loyal is described as having native EHR integrations, but Epic and Cadence are not named in sources opened.",
        "evidence": [
         "https://www.healthcareittoday.com/2026/06/19/clarify-health-acquires-loyal-health-to-create-healthcares-first-closed-loop-network-intelligence-and-patient-activation-platform/"
        ],
        "verify": true
       },
       "proof": {
        "score": 3,
        "rationale": "Loyal's former CEO reported use by close to 500 hospitals; no named Epic IDN reference for scheduling.",
        "evidence": [
         "https://www.healthcareittoday.com/2026/06/19/clarify-health-acquires-loyal-health-to-create-healthcares-first-closed-loop-network-intelligence-and-patient-activation-platform/"
        ],
        "verify": false
       },
       "timeToValue": {
        "score": 2,
        "rationale": "Products are being combined after the June 2026 acquisition and no timeline is published.",
        "evidence": [
         "https://www.healthcareittoday.com/2026/06/19/clarify-health-acquires-loyal-health-to-create-healthcares-first-closed-loop-network-intelligence-and-patient-activation-platform/"
        ],
        "verify": true
       },
       "viability": {
        "score": 3,
        "rationale": "Broad customer base, but integration risk from the recent acquisition.",
        "evidence": [
         "https://www.healthcareittoday.com/2026/06/19/clarify-health-acquires-loyal-health-to-create-healthcares-first-closed-loop-network-intelligence-and-patient-activation-platform/"
        ],
        "verify": false
       },
       "measurement": {
        "score": 3,
        "rationale": "Positioned to connect leakage analysis to patient activation, which could measure retention end to end; export rights not documented.",
        "evidence": [
         "https://www.healthcareittoday.com/2026/06/19/clarify-health-acquires-loyal-health-to-create-healthcares-first-closed-loop-network-intelligence-and-patient-activation-platform/"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No pricing model found in sources opened.",
        "evidence": [
         "https://www.healthcareittoday.com/2026/06/19/clarify-health-acquires-loyal-health-to-create-healthcares-first-closed-loop-network-intelligence-and-patient-activation-platform/"
        ],
        "verify": true
       }
      },
      "whyShortlisted": "It is the only Lot C candidate that links provider data and scheduling to its own leakage analytics, so one vendor could cover the Baseline and Scale milestones. It ranks third on score, one point ahead of Relatient and symplr.",
      "keyRisk": "Epic and Cadence integration for the Loyal components is undocumented and the products were only combined in June 2026.",
      "verifyInDemo": [
       "Live booking into Epic Cadence from Loyal care search for an ACME specialty, with the integration method shown.",
       "How a leakage corridor found in Clarify analytics becomes a matching rule or outreach list in Loyal.",
       "Provider data correction workflow for sub-specialty and network participation."
      ],
      "rfpQuestions": [
       "Which Epic health systems run Loyal scheduling into Cadence today, and can ACME speak with one?",
       "If ACME licenses Clarify analytics in Lot A, what does adding Loyal modules cost, and can they be bought separately?",
       "What Loyal capabilities are on a sunset or migration path after the acquisition?"
      ]
     },
     {
      "vendor": "Relatient (Dash)",
      "status": "nearMiss",
      "rank": 4,
      "weightedScore": 56.0,
      "scores": {
       "functional": {
        "score": 3,
        "rationale": "Rules-based self-scheduling, messaging, reminders and a voice AI agent; not referral-specific.",
        "evidence": [
         "https://www.relatient.com/dash-epic-ehr-software-integration/"
        ],
        "verify": false
       },
       "integration": {
        "score": 4,
        "rationale": "Vendor states Epic App Orchard membership, bi-directional real-time FHIR integration and write-back to Epic.",
        "evidence": [
         "https://www.relatient.com/dash-epic-ehr-software-integration/"
        ],
        "verify": false
       },
       "proof": {
        "score": 2,
        "rationale": "No named health system reference; athenahealth partnership is not an Epic reference.",
        "evidence": [
         "https://businesswire.com/news/home/20260407667960/en/athenahealth-Names-Relatient-as-Preferred-Solution-Partner-for-Intelligent-Scheduling"
        ],
        "verify": true
       },
       "timeToValue": {
        "score": 3,
        "rationale": "No timeline published; scheduling APIs added in 2025.",
        "evidence": [
         "https://www.relatient.com/dash-epic-ehr-software-integration/"
        ],
        "verify": true
       },
       "viability": {
        "score": 2,
        "rationale": "Ownership not verified.",
        "evidence": [
         "https://www.relatient.com/dash-epic-ehr-software-integration/"
        ],
        "verify": true
       },
       "measurement": {
        "score": 2,
        "rationale": "Measurement capability not described.",
        "evidence": [
         "https://www.relatient.com/dash-epic-ehr-software-integration/"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No pricing model found in sources opened.",
        "evidence": [
         "https://www.relatient.com/dash-epic-ehr-software-integration/"
        ],
        "verify": true
       }
      },
      "whyNot": "Documented Epic write-back, but it is a general scheduling tool with no referral matching and no named Epic health system reference."
     },
     {
      "vendor": "symplr (symplr Directory / Provider Data Management)",
      "status": "nearMiss",
      "rank": 5,
      "weightedScore": 56.0,
      "scores": {
       "functional": {
        "score": 3,
        "rationale": "Provider data management built from credentialing data with location, plan participation, taxonomy and availability feeding directories and scheduling; matching workflow is not described.",
        "evidence": [
         "https://www.symplr.com/press-releases/symplr-launches-symplr-directory-as-part-of-end-to-end-provider-data-management-portfolio"
        ],
        "verify": false
       },
       "integration": {
        "score": 4,
        "rationale": "Provider Data Connect and Schedule Advisor were listed in Epic App Orchard in 2021, and AVIA lists Epic as the only supported EMR.",
        "evidence": [
         "https://www.symplr.com/press-releases/symplr-launches-symplr-directory-as-part-of-end-to-end-provider-data-management-portfolio",
         "https://marketplace.aviahealth.com/product/68038"
        ],
        "verify": false
       },
       "proof": {
        "score": 2,
        "rationale": "AVIA shows 7 verified clients behind a sign-up and no reviews; no named references found.",
        "evidence": [
         "https://marketplace.aviahealth.com/product/68038"
        ],
        "verify": true
       },
       "timeToValue": {
        "score": 3,
        "rationale": "Faster if ACME already uses symplr credentialing, which is unknown.",
        "evidence": [
         "https://www.symplr.com/press-releases/symplr-launches-symplr-directory-as-part-of-end-to-end-provider-data-management-portfolio"
        ],
        "verify": true
       },
       "viability": {
        "score": 2,
        "rationale": "Ownership and financials not reviewed.",
        "evidence": [
         "https://www.symplr.com/press-releases/symplr-launches-symplr-directory-as-part-of-end-to-end-provider-data-management-portfolio"
        ],
        "verify": true
       },
       "measurement": {
        "score": 2,
        "rationale": "Measurement capability not described.",
        "evidence": [
         "https://marketplace.aviahealth.com/product/68038"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No pricing model found in sources opened.",
        "evidence": [
         "https://marketplace.aviahealth.com/product/68038"
        ],
        "verify": true
       }
      },
      "whyNot": "Strong fit for the provider directory remediation step, but it does not show matching or referral workflow and has no named references; consider it if ACME already runs symplr credentialing."
     },
     {
      "vendor": "Hyro",
      "status": "evaluated",
      "rank": 6,
      "weightedScore": 54.0,
      "scores": {
       "functional": {
        "score": 2,
        "rationale": "Conversational AI for booking, rescheduling and cancelling appointments; it does not address referral matching, intake or loop closure.",
        "evidence": [
         "https://www.hyro.ai/?p=2687"
        ],
        "verify": false
       },
       "integration": {
        "score": 4,
        "rationale": "Vendor states integration with Epic scheduling and deployment via Epic Vendor Services.",
        "evidence": [
         "https://www.hyro.ai/?p=2687"
        ],
        "verify": false
       },
       "proof": {
        "score": 3,
        "rationale": "Weill Cornell Medicine named with a CIO quote on the Epic integration.",
        "evidence": [
         "https://www.hyro.ai/?p=2687"
        ],
        "verify": false
       },
       "timeToValue": {
        "score": 3,
        "rationale": "Deployment via Epic Vendor Services suggests a defined path; timeline not published.",
        "evidence": [
         "https://www.hyro.ai/?p=2687"
        ],
        "verify": true
       },
       "viability": {
        "score": 2,
        "rationale": "Ownership not verified.",
        "evidence": [
         "https://www.hyro.ai/?p=2687"
        ],
        "verify": true
       },
       "measurement": {
        "score": 2,
        "rationale": "Measurement capability not described.",
        "evidence": [
         "https://www.hyro.ai/?p=2687"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No pricing model found in sources opened.",
        "evidence": [
         "https://www.hyro.ai/?p=2687"
        ],
        "verify": true
       }
      }
     },
     {
      "vendor": "Experian Health (Patient Schedule)",
      "status": "evaluated",
      "rank": 7,
      "weightedScore": 50.0,
      "scores": {
       "functional": {
        "score": 3,
        "rationale": "Rules-driven online and guided call-center scheduling that the vendor says can keep referrals within a targeted set of specialists.",
        "evidence": [
         "https://www.experian.com/healthcare/products/payment-tools/online-patient-appointment-scheduling"
        ],
        "verify": false
       },
       "integration": {
        "score": 2,
        "rationale": "Product page does not mention Epic.",
        "evidence": [
         "https://www.experian.com/healthcare/products/payment-tools/online-patient-appointment-scheduling"
        ],
        "verify": true
       },
       "proof": {
        "score": 3,
        "rationale": "IU Health named in a guided-scheduling case study.",
        "evidence": [
         "https://www.experian.com/healthcare/products/payment-tools/online-patient-appointment-scheduling"
        ],
        "verify": false
       },
       "timeToValue": {
        "score": 2,
        "rationale": "Implementation effort not described.",
        "evidence": [
         "https://www.experian.com/healthcare/products/payment-tools/online-patient-appointment-scheduling"
        ],
        "verify": true
       },
       "viability": {
        "score": 3,
        "rationale": "Owned by Experian; financial review not done.",
        "evidence": [
         "https://www.experian.com/healthcare/products/payment-tools/online-patient-appointment-scheduling"
        ],
        "verify": true
       },
       "measurement": {
        "score": 2,
        "rationale": "Portfolio averages are vendor-reported; referral retention measurement not described.",
        "evidence": [
         "https://www.experian.com/healthcare/products/payment-tools/online-patient-appointment-scheduling"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No pricing model found in sources opened.",
        "evidence": [
         "https://www.experian.com/healthcare/products/payment-tools/online-patient-appointment-scheduling"
        ],
        "verify": true
       }
      }
     },
     {
      "vendor": "Fabric",
      "status": "evaluated",
      "rank": 8,
      "weightedScore": 50.0,
      "scores": {
       "functional": {
        "score": 3,
        "rationale": "Symptom intake, navigation to the right setting and self-scheduling through an organization-specific provider directory.",
        "evidence": [
         "https://pulse2.com/why-fabric-is-buying-gyant/"
        ],
        "verify": false
       },
       "integration": {
        "score": 2,
        "rationale": "No Epic evidence in the source opened.",
        "evidence": [
         "https://pulse2.com/why-fabric-is-buying-gyant/"
        ],
        "verify": true
       },
       "proof": {
        "score": 3,
        "rationale": "Reported 70 enterprise health customers with OSF HealthCare named.",
        "evidence": [
         "https://pulse2.com/why-fabric-is-buying-gyant/"
        ],
        "verify": false
       },
       "timeToValue": {
        "score": 2,
        "rationale": "Implementation effort not described.",
        "evidence": [
         "https://pulse2.com/why-fabric-is-buying-gyant/"
        ],
        "verify": true
       },
       "viability": {
        "score": 3,
        "rationale": "Consolidated GYANT and Zipnosis, which adds scale and integration risk.",
        "evidence": [
         "https://pulse2.com/why-fabric-is-buying-gyant/"
        ],
        "verify": false
       },
       "measurement": {
        "score": 2,
        "rationale": "Measurement capability not described.",
        "evidence": [
         "https://pulse2.com/why-fabric-is-buying-gyant/"
        ],
        "verify": true
       },
       "commercial": {
        "score": 2,
        "rationale": "No pricing model found in sources opened.",
        "evidence": [
         "https://pulse2.com/why-fabric-is-buying-gyant/"
        ],
        "verify": true
       }
      }
     }
    ]
   }
  ],
  "screenedOut": [
   {
    "vendor": "Epic (Referrals, Care Everywhere, EpicCare Link, Community Connect, Cadence)",
    "reason": "Already owned by ACME; evaluated as the baseline in epicBaseline, not as a sourcing candidate."
   },
   {
    "vendor": "Oracle Health (Referral Management)",
    "reason": "Knockout 1 risk: a second EHR vendor's referral module would duplicate Epic Referrals as system of record, and no Epic integration evidence was found for the EHR-agnostic version."
   },
   {
    "vendor": "NextGen Healthcare",
    "reason": "Ambulatory EHR, not something ACME would buy; relevant only as an affiliate-side 360X counterpart (record in the Epic baseline work, not as a lot)."
   },
   {
    "vendor": "H1 (formerly Ribbon Health)",
    "reason": "Knockout 2: now offered as H1 for Health Plans and Digital Health, with no provider health system references or Epic evidence found."
   },
   {
    "vendor": "Rhapsody (formerly Lyniate)",
    "reason": "Integration engine infrastructure rather than a referral product; not tied to a lot gap. Evaluate only if a shortlisted vendor needs an interface engine ACME does not already run."
   },
   {
    "vendor": "Salesforce Agentforce Health (formerly Health Cloud)",
    "reason": "Physician relationship CRM platform needing systems-integrator configuration and its own data feeds; not tied to the three lot gaps. Revisit if specialist adoption (a stated risk) needs a liaison CRM."
   },
   {
    "vendor": "Microsoft Dynamics 365 / Microsoft Cloud for Healthcare",
    "reason": "General CRM requiring partner-built referral management (8-week proof of concept offering); not tied to the three lot gaps."
   },
   {
    "vendor": "Healthgrades (health system CRM, includes former Evariant)",
    "reason": "Current physician relationship management functions were not confirmed in sources opened and the product is not tied to the lot gaps."
   },
   {
    "vendor": "Marketware",
    "reason": "Physician relationship management with claims-based shared-patient views; outreach CRM is outside the three lot gaps and no EHR integration is named. Revisit with the CRM vendors if a liaison program is funded."
   },
   {
    "vendor": "WellSky (CarePort)",
    "reason": "Post-acute and community referral network, not specialist referral; adjacent category."
   },
   {
    "vendor": "Unite Us",
    "reason": "Social-care closed-loop referral network, not specialist referral; adjacent category."
   },
   {
    "vendor": "findhelp",
    "reason": "Social-care search and referral, not specialist referral; adjacent category."
   },
   {
    "vendor": "RubiconMD",
    "reason": "CVS Health-owned eConsult network that avoids in-person referrals rather than routing, retaining or closing them; outside the lot gaps. Revisit if ACME adds an eConsult program."
   },
   {
    "vendor": "Zocdoc",
    "reason": "Consumer marketplace whose enterprise offering targets payers and platforms; it books across all providers and does not support in-network referral matching for an IDN."
   },
   {
    "vendor": "Optum Patient Access and Engagement (formerly DocASAP)",
    "reason": "Knockout 3: current ownership and product status rest only on a Dealroom profile and could not be verified; no Epic evidence found."
   }
  ],
  "assumptions": [
   "ACME's Epic licensing includes Referrals, Cadence, Care Everywhere and EpicCare Link; whether CERM, Community Connect, Fast Pass, ticket scheduling and MyChart self-scheduling are live at ACME is not recorded in the client context.",
   "Leakage baselining for the Nov 2026 milestone cannot rely on ACME's internal payer claims feed (quality 2/5, two payers, 60-90 day lag) and therefore needs a vendor dataset.",
   "Clarify Health (including Loyal) is evaluated in both Lot A and Lot C because it sells analytics and access modules that could be bought together or separately; scores are lot-specific.",
   "Where no evidence was found either way, the criterion is scored 2 with verify=true per rubric; this lowers scores for vendors with thin public material, not only for weak products.",
   "Lot A vendors (Clarify, Trilliant, Definitive, Sg2, Strata, Health Catalyst, Arcadia, Trella) carry the landscape competitor flag and were scored with the same rubric as all others.",
   "Vendor-reported outcomes (for example Clarify's 37,000 referrals, Janus's 40% volume increase, Luma's conversion rates) are cited as vendor claims and were not independently verified.",
   "Ties were broken by proof, then integration; Innovaccer and Janus Health tied at 63.0 in Lot B and Innovaccer ranks ahead on proof; symplr and Relatient tied at 56.0 in Lot C and remain tied after both tie-breaks, so they are listed alphabetically. Definitive Healthcare and Trilliant Health tied at 61.0 at the Lot A cutoff; Definitive ranks ahead on proof and both are shortlisted."
  ],
  "openQuestions": [
   "Is ACME a Vizient/Sg2 member or an existing Strata, Health Catalyst, Innovaccer, Artera or symplr customer? Existing contracts would change time to value and commercial terms.",
   "Which Epic referral features are live at ACME today (CERM, 360X, EpicCare Link adoption by community referrers, referral auto-linking, MyChart and ticket scheduling)? This sets how much of Lot B is configuration rather than purchase.",
   "Could Epic Cosmos or Care Everywhere data show leakage to other Epic organizations in ACME's markets? This was not evaluated and could partly cover Lot A.",
   "What share of ACME's inbound specialty referrals arrive by fax versus Epic or EpicCare Link? This sizes the Lot B intake business case.",
   "Who owns referral steering rules (clinical, contracting, legal) so matching logic is defensible and preserves patient choice before any Lot C purchase?",
   "Did RevSpring's acquisition of Kyruus Health close, and what is the product roadmap?",
   "Which Loyal capabilities remain generally available under Clarify after the June 2026 acquisition?"
  ]
 }
};
